/*
 * The one dictionary of the cabinet's texts (Live · Studio and Status first; older screens move
 * here with the localization stage). Keys are flat and stable; EN is the source. ES and RU are
 * filled by the localization task: until then a missing key falls back to EN. {name} in a text
 * is a parameter. Product names (Strevix, platform names) are never translated.
 */

const en = {
  'common.soon': 'Soon',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.hide': 'Hide',
  'common.show': 'Show',
  'common.dismiss': 'Dismiss',

  'auth.footer.status': 'Status',
  'auth.register.elsewhere': "Accounts are created in the cabinet, not at this address. Open your invite link again or ask the admin for a new one.",

  'connect.ok': '{platform} connected',
  'connect.denied': "You didn't allow access at {platform}.",
  'connect.expired': 'The connection took too long or was opened in another browser. Try again.',
  'connect.noChannel': 'This Google account has no YouTube channel.',
  'connect.failed': "Couldn't connect {platform}. Try again.",
  'connect.later': 'Live stats for {platform} are coming later.',
  'connect.cantStart': "Couldn't start connecting {platform}. Try again.",

  'studio.loading': 'Loading live data…',
  'studio.reconnecting': 'Reconnecting to live data…',
  'studio.limited': 'Live data paused: too many open tabs or devices. Close some to resume.',
  'studio.loadingSettings': 'Loading settings…',
  'studio.nodeDown': 'Media server{node} unreachable, state unknown. Your settings are saved; statuses and numbers come back when it answers.',

  'state.live': "You're live",
  'state.ready': 'Ready when you are',
  'state.dropped': 'OBS stopped sending',

  'bar.views': 'Live views',
  'bar.studio': 'Studio',
  'bar.status': 'Status',
  'bar.needYou': '{n} need you',
  'bar.live': 'LIVE',
  'bar.obsStopped': 'OBS STOPPED',
  'bar.offAir': 'OFF AIR',
  'bar.out': '{on}/{n} out',
  'bar.chat': 'Chat',
  'bar.chatHint': 'Show chats in the right column (C)',
  'bar.controls': 'Controls',
  'bar.allOn': 'All on',
  'bar.allOff': 'All off',
  'bar.endAll': 'End on all platforms',
  'bar.end': 'End',
  'bar.waiting': 'Waiting for the encoder · going live starts there',
  'bar.keep': 'Keep streaming',
  'bar.confirmOff.title': 'Turn all outputs off?',
  'bar.confirmOff.body': 'Goes off air now: {names}. OBS keeps sending; turn them on again any time.',
  'bar.confirmOff.yes': 'Turn off {n}',

  'stage.label': 'Stream preview',
  'stage.noPicture': 'The picture comes with the next keyframe.',
  'stage.listening': 'LISTENING',
  'stage.quiet': 'The picture appears here as soon as it arrives.',
  'stage.start': 'Start streaming in OBS',
  'stage.obsHow': 'In OBS open Settings → Stream, set Service to Custom and paste these. Outputs that are on go live as soon as the picture arrives.',
  'stage.more': 'Region, hold when OBS drops, new key →',
  'stage.back': '← Back to the address and key',
  'stage.unknown.tag': 'NO DATA',
  'stage.unknown.body': 'The media server does not answer. Your stream may still be on air; the picture and numbers come back when it answers.',
  'stage.dropped.tag': 'NO PICTURE',
  'stage.dropped.body': 'Your encoder stopped sending. Outputs on hold stay connected for a short while: start streaming in OBS again to continue.',
  'stage.dropped.check': 'Check the connection →',

  'strip.input': 'Input',
  'strip.edit': 'Edit OBS settings',
  'strip.notConnected': 'not connected',
  'strip.bitrate': 'Bitrate',
  'strip.video': 'Video',
  'strip.keyframes': 'Keyframes',
  'strip.rtt': 'RTT',
  'strip.jitter': 'Jitter',
  'strip.latency': 'Latency',
  'strip.lostResent': 'Lost / resent',
  'strip.unreachable': 'unreachable',

  'tips.title': 'Tips',
  'tips.now': 'Now',
  'tips.before': 'Before you go live',
  'tips.better': 'To make it better',
  'tips.empty': 'Nothing needs you. Tips show up here when something does.',
  'tips.hide': 'Hide tip: {title}',
  'tips.open': 'Open tips',
  'tips.fold': 'Fold tips',

  'tip.dropped.title': 'OBS stopped sending',
  'tip.dropped.body': 'Start streaming in OBS again. Outputs on hold continue without a new live on the platform.',
  'tip.obs.title': 'Start streaming in OBS',
  'tip.obs.body': 'Outputs that are on go live as soon as the picture arrives.',
  'tip.obs.noOutputs': 'Add an output first: without one the stream goes nowhere.',
  'tip.goLive.title': '{platform}: press Go live there',
  'tip.rtmp.title': 'Streaming without encryption',
  'tip.rtmp.body': 'Your encoder sends plain RTMP: the stream key travels unencrypted. If it can, switch to RTMPS (Input).',
  'tip.gop.title': 'Keyframes every {s} s',
  'tip.gop.body': 'Set the keyframe interval to 2 s in OBS: platforms buffer or reject longer ones.',
  'tip.srt.title': 'SRT recovered {n} lost packets',
  'tip.srt.body': 'Nothing reached viewers late in the last 5 s.',
  'tip.reconnects.title': 'OBS reconnected {n} times',
  'tip.reconnects.body': 'A wired connection or a lower bitrate keeps the upload steady.',
  'tip.app.required.title': 'Update {app} to {v}',
  'tip.app.required.body': 'You have {have}. After {date} this version can no longer go live.',
  'tip.app.enforced.title': '{app} {v} or newer is required',
  'tip.app.enforced.body': 'Version {have} can no longer go live. Install the update and start the stream again.',
  'tip.app.newer.title': '{app} {v} is available',
  'tip.app.newer.body': 'You have {have}.',
  'tip.app.download': 'Get {app}',
  'tip.input.key_revoked': 'Stream key revoked. Issue a new one in Input and paste it into OBS.',
  'tip.input.stream_suspended': 'Suspended by an administrator.',
  'tip.input.account_blocked': 'Account blocked.',
  'tip.input.codec_unsupported': 'Unsupported codec. Set OBS to H.264 and AAC.',
  'tip.input.bitrate_over_limit': 'Bitrate over your limit. Lower it in OBS.',
  'tip.input.unstable': 'Unstable signal from OBS. Check the upload of your network.',
  'tip.input.ingest_mismatch': 'Another of your streams is on a different ingest server. Send all your streams (main and vertical) to the same server address.',

  'fix.key.form': 'Stream key for {name}',
  'fix.key.label': 'New stream key for {name}',
  'fix.key.placeholder': 'Paste your {platform} key',
  'fix.key.save': 'Save',
  'fix.key.saving': 'Saving…',
  'fix.key.spaces': 'The key has no spaces. Copy it again.',
  'fix.key.open': 'Open {site}',
  'fix.retryIn': 'Retrying in {s} s.',
  'fix.nextTry': 'Next try in {s} s.',
  'fix.retry.body': 'We retry by ourselves; the other outputs keep streaming.',
  'fix.settings': 'Check the server in its settings →',

  'row.live': 'Live',
  'row.ready': 'Waits for OBS',
  'row.needsKey': 'Needs a stream key',
  'row.off': 'Off',
  'row.connecting': 'Connecting…',
  'row.stopping': 'Stopping…',
  'row.noData': 'No data · server unreachable',
  'row.dropped': 'Dropped',
  'row.failed': 'Not on air',
  'row.retrying': 'retrying now',
  'row.retryIn': 'retry in {s} s',
  'row.nextTry': 'next try in {s} s',
  'row.keyRejected': 'Key rejected',
  'row.serverRejected': 'Server address rejected',
  'row.unreachable': "Can't reach the server",
  'row.tls': 'Secure connection failed',
  'row.disconnected': 'Stopped answering',
  'row.stalled': 'Stopped reading',
  'row.slowLink': 'Slow link · frames dropped',
  'row.lagging': 'Falling behind',
  'row.gop': 'Keyframes too far apart',
  'row.gopNote': 'Keyframes over 5 s apart: YouTube may drop parts. Set Keyframe Interval to 2 s in OBS.',
  'row.internal': 'Error on our side',
  'row.viewers': 'Watching now, as the platform reports',
  'row.pasteKey': 'Paste key',
  'row.checkServer': 'Check server',
  'row.savedReconnect': '✓ Saved. Reconnecting {name} only.',
  'row.savedNoReconnect': '✓ Saved. Applies right away, no reconnect.',

  'tc.running': 'Optimized for {platform} · {res} · {mbps} Mbps',
  'tc.starting': 'Starting the optimizer for {platform}…',
  'tc.passthrough': 'Original · fits {platform}',
  'tc.original': 'Original stream to {platform}',
  'tc.bitrateOver': 'Your OBS sends {in} Mbps; {platform} gets {out} Mbps',
  'tc.resolutionOver': 'Your OBS sends {in}; {platform} gets {out}',
  'tc.gop': 'Keyframe every 2 s for {platform}',
  'tc.codec': 'Converted to H.264 for {platform}',
  'tc.audioOver': 'Audio is above what {platform} accepts: set OBS audio to 160 kbps',
  'tc.fallback': 'Optimizer unavailable — {platform} gets your original stream',
  'tc.fallbackOver': 'Optimizer unavailable — {platform} gets your original stream ({in} Mbps, above {platform}’s {max} Mbps)',
  'tc.planNone': 'Optimizing for {platform} is not in your plan: it gets your original stream',
  // WM3 (claude/wm-ui-stage0.md, owner 02.11): the free plan's Strevix mark — the cabinet's word is "Strevix mark" (WMU-01)
  'wm.row': 'Strevix mark · {res} · Free plan',
  'wm.rowNoRes': 'Strevix mark · Free plan',
  'wm.rowTitle': 'Viewers on this output see the Strevix mark in the top-right corner. Paid plans go out without it.',
  'wm.toastOn.title': 'Strevix mark on your outputs',
  'wm.toastOn.body': 'Your plan is now Free: viewers see the Strevix mark on every output.',
  'wm.toastOff.title': 'Strevix mark removed',
  'wm.toastOff.body': 'Your plan is {plan}; outputs go out without it.',
  'cd.http': 'Captions · {platform}',
  'cd.httpLang': 'Captions · {platform} · {lang}',
  'cd.cc608': 'CC · {lang} (CEA-608)',
  'cd.burn': 'Captions burned in · {lang} — uses transcoding hours',
  'cd.burnOriginal': 'Captions burned in — uses transcoding hours',
  'cd.burnFixed': 'Burned-in captions are part of the video: viewers can’t turn them off.',
  'cd.burn608': 'CEA-608 doesn’t support Cyrillic — on {platform} captions will be burned into the video (transcoding hours).',
  'cd.off': 'Captions off on this output',
  'cd.waiting': 'Captions · waiting for the language',
  'cd.nodeNo608': 'This media server can’t add CC to the video yet — {platform} gets the stream without captions.',
  'cd.planNone': 'Captions aren’t in your plan.',
  'cd.planTranslate': 'Caption translation isn’t in your plan: {platform} gets no captions.',
  'cd.burnNotReady': 'Burned-in captions aren’t available yet — {platform} gets the video without captions.',
  'cd.noTranscoder': 'No transcoder is free — {platform} gets the video without captions.',
  'cd.hoursOut': 'This period’s transcoding hours are used up — {platform} gets the video without captions.',
  'cd.cc608NotReady': 'CC in the video isn’t available yet — {platform} gets the stream without captions.',
  'cd.httpOff': 'Turn on Send captions in this output’s settings to show captions on {platform}.',
  'cd.notTranslated': 'Your stream doesn’t translate into {lang}: turn it on in the captions panel.',
  'cd.unavailable': 'Captions are unavailable on {platform} right now.',
  'cd.setting': 'Captions on this output',
  'cd.settingHint.http': '{platform} gets your captions as a captions track viewers can turn on.',
  'cd.settingHint.cea608': '{platform} gets English or Spanish captions as CC in the video; viewers can turn them on.',
  'cd.settingHint.burn': 'Captions are drawn into {platform}’s video: viewers can’t turn them off, and the time counts in your transcoding hours.',
  'cd.lang.en': 'English',
  'cd.lang.es': 'Spanish',
  'cd.lang.ru': 'Russian',
  'tc.planOutputs': 'Your plan’s optimized destinations are all in use: {platform} gets your original stream',
  'tc.planHours': 'This period’s optimization hours are used up: {platform} gets your original stream',
  'tc.switch': 'Optimize for {platform}',
  'tc.switchSub': 'When your OBS sends more than {platform} accepts (bitrate, resolution, keyframes), we make a copy that fits. Streams that already fit go as they are.',
  'tc.saveNote': 'Applies at the next keyframe, without reconnecting.',

  'right.label': 'Outputs and chats',
  'right.outputs': 'Outputs',
  'right.chats': 'Chats',
  'right.live': '{live}/{n} live',
  'right.chatsSoon': '{n} can read · soon',
  'right.chatsN': '{n} reading',

  'outs.search': 'Find an output',
  'outs.show': 'Show',
  'outs.all': 'All',
  'outs.need': 'Needs you',
  'outs.live': 'Live',
  'outs.off': 'Off',
  'outs.empty': 'No outputs yet. Add YouTube, Twitch, Kick, Facebook or any RTMP server: they go live together with your stream.',
  'outs.none': 'No outputs match.',
  'outs.add': 'Add output',
  'outs.limit': '{n} of {max} on your plan',
  'outs.full': '{max} of {max} on your plan. Remove one to add another.',

  'chats.outputs': 'OUTPUTS',
  'chats.offAir': '{name} is off air',
  'chats.needsYou': '{name} needs you',
  'chats.fix': 'Fix',
  'chats.title': 'Chats from every platform',
  'chats.body': 'All your chats in one column: reading first, then replies. Below is who will be able to read chat.',
  'chats.access': 'Chat access by output',
  'chats.noOutputs': 'Turn on an output to see its chat here.',
  'chats.reads': 'READS CHAT',
  'chats.connect': 'Connect account',
  'chats.opening': 'Opening {platform}…',
  'chats.instagram': 'No chat for apps',
  'chats.noAccess': 'No chat access yet',

  'chat.feed': 'Chat messages',
  'chat.filterLabel': 'Chat of',
  'chat.viewLabel': 'Show',
  'chat.all': 'All',
  'chat.view.all': 'All',
  'chat.view.qa': 'Q&A',
  'chat.view.highlights': 'Highlights',
  'chat.prelive': 'Chats open when you go live.',
  'chat.preliveNote': 'Chat needs the platform account connected, not just the stream key.',
  'chat.empty': 'No messages yet. They show up here as viewers write.',
  'chat.emptyView': 'Nothing here yet.',
  'chat.paused': 'Paused · {n} new ↓',
  'chat.pausedNone': 'Paused · back to live ↓',
  'chat.first': 'FIRST MESSAGE',
  'chat.role.owner': 'OWNER',
  'chat.role.mod': 'MOD',
  'chat.role.vip': 'VIP',
  'chat.role.staff': 'STAFF',
  'chat.kind.paid': 'PAID',
  'chat.kind.membership': 'MEMBERSHIP',
  'chat.kind.raid': 'RAID',
  'chat.kind.announcement': 'ANNOUNCEMENT',
  'chat.tier': 'TIER {n}',
  'chat.months': '{n} MONTHS',
  'chat.gifts': '{n} GIFTS',
  'chat.viewers': '{n} VIEWERS',
  'chat.replyTo': 'replying to {name}',
  'chat.reply': 'Reply from the Studio',
  'chat.reconnecting': 'Reconnecting to chats…',
  'chat.limited': 'Chats paused: too many open tabs or devices.',
  'chat.unread': '{n} unread',
  'chat.openOf': 'Chat of {name}',
  'chat.notReading': 'Outputs whose chat is not read',
  'chat.reconnect': 'Reconnect',
  'chat.access.connect': 'Connect your {platform} account to read its chat',
  'chat.access.otherChannel': 'The stream key is for another channel than the connected {platform} account',
  'chat.access.reconnect': 'Connect {platform} again to allow chat',
  'chat.access.comingLater': '{platform} chat is coming later',
  'chat.access.notConfigured': '{platform} chat isn’t available on this server yet',
  'chat.access.unsupported': '{platform} doesn’t share chat with other apps',
  'chat.access.custom': 'Custom RTMP has no chat',
  'chat.access.dropped': 'Chat connection dropped · reconnecting',

  'add.title': 'ADD OUTPUT',
  'add.titleDetails': 'ADD OUTPUT · DETAILS',
  'add.search': 'Find a platform',
  'add.loading': 'Loading platforms…',
  'add.none': 'No platform matches. Custom RTMP takes any server.',
  'add.customName': 'Custom RTMP',
  'add.customSub': 'Any RTMP or RTMPS server',
  'add.keySub': 'Key from {site}',
  'add.keyEveryLive': 'New key for every live',
  'add.goLiveThere': 'Press Go live there after OBS starts',
  'add.formLabel': 'Add {platform}',
  'add.whereKey': 'Where to get the stream key',
  'add.name': 'Name',
  'add.server': 'Server',
  'add.serverFilled': 'Filled in for this platform. Change it only if the platform shows a different one.',
  'add.serverAny': 'rtmp:// or rtmps:// address of the server',
  'add.urlError': 'Start with rtmp:// or rtmps://',
  'add.key': 'Stream key',
  'add.keyLater': 'Optional now: add the output and paste the key later.',
  'add.keyLaterSession': 'This platform gives a new key for every live: paste it before you go live.',
  'add.keyOptional': 'Only if your server asks for one.',
  'add.advanced': 'Advanced',
  'add.liveNote': "You're live, so {name} starts as soon as you add it. Others keep streaming.",
  'add.offNote': '{name} goes live when you start streaming.',
  'add.submit': 'Add output',
  'add.goLive': 'Add and go live',
  'add.adding': 'Adding…',

  // VERT-PLAT: platforms that give the server with the key (TikTok), the 9:16 tag, TikTok's help (profile.tiktok.*; EN = the center's profile texts)
  'add.serverFromPlatform': 'Paste the server address {platform} shows with the stream key.',
  'add.serverNeeded': '{platform} gives the server address: paste it too.',
  'add.subServerKey': 'Server and key from {platform}',
  'add.whereServerKey': 'Where to get the server and the stream key',
  'add.portrait': '{platform} shows 9:16 video: this output gets your vertical input when it’s on.',
  'vert.tag': '9:16',
  'row.portraitLandscape': '{platform} shows 9:16 but gets a landscape picture now. It goes as is: send 9:16 from Vertical input.',
  'profile.tiktok.step1': 'You need LIVE access with a stream key: TikTok asks for 18+ and a follower minimum, which may vary by region.',
  'profile.tiktok.step2': 'When you set up a LIVE with a stream key (TikTok LIVE Studio or LIVE Producer), TikTok shows a Server URL and a Stream key.',
  'profile.tiktok.step3': 'Paste both here. TikTok may show new ones for another LIVE: then paste the new ones.',
  'profile.tiktok.step4': 'TikTok shows a 9:16 picture: send it from Vertical input, or set OBS to 9:16.',
  'profile.tiktok.step5': 'TikTok may take stream-key access back, for example after a trial without enough LIVEs.',
  'setup.tile.serverKey': '{platform} gives a server address and a stream key. Paste both to add it.',
  'setup.tile.add': 'Add {platform}',
  'setup.tile.how': 'How do I get them?',
  'setup.tile.saved': 'Server and key saved',
  'setup.tile.replace': 'Replace server and key',
  'setup.tile.keep': 'Keep the saved ones',
  'setup.tile.save': 'Save',
  'setup.tile.saving': 'Saving…',

  // Protocol to the platform (YouTube: RTMPS or HLS, docs/youtube-hls.md §4)
  'proto.title': 'Protocol to {platform}',
  'proto.recommended': 'Recommended',
  'proto.rtmps.sub': 'The usual way. Lowest delay.',
  'proto.hls.sub': 'Steadier on unstable networks; a few seconds more delay.',
  'proto.hls.hint1': "Latency is a few seconds higher than with RTMPS, and YouTube's Ultra low-latency mode is not available.",
  'proto.hls.hint2': 'Set the keyframe interval to 2 s with closed GOPs: YouTube refuses segments longer than 5 s.',
  'proto.hls.hint3': 'Captions embedded in the video (CEA-608) work only with H.264.',
  'proto.keySteps': 'Stream key for {proto}',
  'proto.keyNote': 'YouTube makes a separate stream key for HLS (the steps above). Paste that key below.',
  'proto.serverHls': 'YouTube’s HLS upload address, filled in. The key goes in its own field.',
  'proto.urlHttps': 'Start with https:// for HLS',
  'proto.liveNote': "You're live: switching reconnects {name}, a few seconds off air there. Other outputs keep streaming.",
  'proto.offNote': 'Applies the next time {name} goes live.',
  'proto.confirm.title': 'This will reconnect {platform}',
  'proto.confirm.body': '{name} switches to {proto}: a few seconds off air there. Other outputs keep streaming.',
  'proto.confirm.yes': 'Switch and reconnect',
  'proto.confirm.no': 'Keep {proto}',
  'proto.conflict': 'This output was changed meanwhile, in another tab or device. Close the settings, open them again and retry.',
  'proto.chip': 'Sent to {platform} over HLS',

  'edit.title': 'OUTPUT · {name}',
  'input.title': 'INPUT',
  'input.applies': 'Changes apply the next time the encoder connects. A stream on air keeps going.',
  'wizard.title': 'SETUP GUIDE',
  'wizard.side': 'Three steps, about two minutes. Close the guide any time: everything you add stays.',

  'status.outputs': 'Outputs',
  'status.col.output': 'Output',
  'status.col.state': 'State',
  'status.col.bitrate': 'Bitrate Mbps',
  'status.col.delay': 'Delay to viewers',
  'status.col.watching': 'Watching',
  'status.col.reconnects': 'Reconnects',
  'status.col.edit': 'Settings',

  'preview.low': 'LOW TRAFFIC',
  'preview.lowOnTitle': 'Low traffic: a frame every few seconds. Turn off to watch in real time.',
  'preview.lowOffTitle': 'Real time from the media server. Turn on to save traffic: a frame every few seconds.',
  'preview.unavailable': 'Real time is not available: {why}. Showing a frame every few seconds.',
  'preview.whyShort': 'NOT REAL TIME: {why}',
  'preview.liveBadge': 'LIVE · {fps} FPS',
  'preview.liveLabel': 'Stream preview, live at {fps} frames per second',
  'preview.busy': 'The live view is open in another window.',
  'preview.taken': 'Viewing in another window.',
  'preview.idle': 'Live view paused after 10 minutes without activity.',
  'preview.start': 'Start viewing',
  'preview.resume': 'Resume',
  'status.need': '{n} of the outputs need you',
  'status.fix': 'Fix in the Studio',
  'status.platforms': 'On the platforms',
  'status.input': 'Input',
  'status.reconnects': 'Reconnects',
  'status.encoder': 'Encoder',
  'status.events': 'This stream',
  'status.chart.bitrate': 'Bitrate per output · last 5 min',
  'status.chart.ingest': 'Ingest',
  'status.chart.ingestLabel': 'Bitrate from OBS to us, last 5 minutes',
  'status.stale': 'The platform API is not answering: last known number',
  'status.eventsSoon': 'A timeline of this broadcast — connections, rejected keys, retries — comes here.',

  'rtmp.footnote': 'Encoder can’t do RTMPS? It also works without encryption (RTMP)',
  'rtmp.server': 'Server without encryption (RTMP)',
  'rtmp.plain': 'Over RTMP the stream key travels in plain text. Use it only when your encoder can’t do RTMPS; the same key works.',
  'srt.latency': 'Latency {value}, set for you: it is already in the address, nothing to choose.',

  'account.menu': 'Account',
  'account.devices': 'Devices & apps',
  'account.apiTokens': 'API tokens',
  'account.signOut': 'Sign out',
  'account.billing': 'Plan & usage',

  'plan.title': 'Plan & usage',
  'plan.intro': 'Your plan, what it includes and why a broadcast may not start. Payments are not open yet.',
  'plan.loading': 'Loading your plan…',
  'plan.label': 'Plan',
  'plan.choose': 'Choose a plan',
  'plan.chooseHint': 'Opens the pricing page. Payments in the cabinet are coming.',
  'plan.trialLeft': 'Trial · {days} left',
  'plan.trialEnds': 'Trial · ends today',
  'plan.days.one': '{n} day',
  'plan.days.few': '{n} days',
  'plan.days.many': '{n} days',
  'plan.days.other': '{n} days',
  'plan.ambassadorVip': '{name} · VIP',
  'plan.legacyUntil': 'Legacy until {date}',
  'plan.giftUntil': '{name} — gift until {date}',
  'plan.gift': '{name} — gift',
  'plan.endedFree': '{name} · your previous plan ended',
  'plan.paymentFailed': '{name} · payment failed',
  'plan.canceledUntil': '{name} · canceled, works until {date}',
  'plan.trialNote': 'The trial ends on {date}. After it your account moves to Free; your outputs and settings stay.',
  'plan.giftNote': 'Given to you by Strevix. On {date} the account goes back to its own plan.',
  'plan.giftForever': 'Given to you by Strevix, with no end date.',
  'plan.ambassadorNote': 'Every feature of the service, as a Strevix ambassador.',
  'plan.legacyNote': 'You joined before plans existed: your limits stay as they were until {date}. Then the account moves to Free unless you choose a plan.',
  'plan.freeNote': 'The minimum package: {streams} broadcast at a time, {dests} destinations, {hours} h on air a month. Choose a plan for more.',
  'plan.endedNote': 'Your previous plan ended, so the account is on Free. Your outputs and settings are kept; outputs over the limit stay off until you choose a plan.',
  'plan.paymentNote': 'The last payment did not go through. Everything works for 7 days after it failed; then the account moves to Free.',
  'plan.access.account_frozen': 'Your account is frozen: new broadcasts don’t start. A broadcast already on air keeps going. Contact support.',
  'plan.access.account_streams_blocked': 'New broadcasts are blocked on your account. A broadcast already on air keeps going. Contact support.',
  'plan.access.plan_payment_disputed': 'A payment is disputed: new broadcasts are blocked until it’s resolved. A broadcast already on air keeps going.',
  'plan.access.plan_paused': 'Your plan is paused: new broadcasts don’t start until it resumes.',
  'plan.access.other': 'New broadcasts don’t start on your account right now. Contact support.',
  'plan.bar.trial': 'Trial · {days} left',
  'plan.bar.trialEnds': 'Trial · ends today',
  'plan.bar.ended': 'Plan ended — Free',
  'plan.bar.paymentFailed': 'Payment failed',
  'plan.bar.frozen': 'Frozen',
  'plan.bar.paused': 'Plan paused',
  'plan.bar.blocked': 'Broadcasts blocked',
  'plan.bar.hint': 'Your plan: open Plan & usage',
  'plan.limits': 'Limits',
  'plan.features': 'Features',
  'plan.included': 'Included',
  'plan.notOnPlan': 'Not on this plan',
  'plan.comingSoon': 'Coming soon',
  'plan.noLimit': 'No limit',
  'plan.notIncluded': 'Not included',
  'plan.unit.hMonth': '{n} h a month',
  'plan.unit.s': '{n} s',
  'plan.unit.min': '{n} min',
  'plan.unit.minS': '{m} min {s} s',
  'plan.limit.destinations': 'Destinations',
  'plan.limit.concurrent_streams': 'Simultaneous broadcasts',
  'plan.limit.stream_hours_month': 'Hours on air',
  'plan.limit.ingest_max': 'Input from OBS',
  'plan.limit.output_max': 'Output to platforms',
  'plan.outputMarkNote': 'With the Strevix mark. Paid plans send your stream as it comes.',
  'plan.limit.source_grace_max_s': 'Hold when OBS drops',
  'plan.limit.captions_hours_month': 'Live captions',
  'plan.limit.transcode_outputs': 'Transcoded outputs',
  'plan.limit.transcode_hours_month': 'Transcoding',
  'plan.limit.translation_lang_hours_month': 'Caption translation',
  'plan.ingestWarn': 'Higher input is accepted for now; we only warn.',
  'plan.hoursFair': 'Fair use: we write to you before anything changes.',
  'plan.hoursHard': 'At the limit a new broadcast doesn’t start; one already on air keeps going.',
  'plan.feature.chat': 'Unified chat',
  'plan.feature.captions': 'Live captions',
  'plan.feature.caption_translation': 'Caption translation',
  'plan.feature.transcode': 'Transcoding for platforms',
  'plan.feature.vertical_9_16': 'Vertical 9:16 stream',
  'plan.feature.srt_ingest': 'SRT input',
  'plan.feature.api_tokens': 'API tokens',
  'plan.feature.no_watermark': 'No Strevix mark',
  'plan.usageLater': 'Hours used this month appear here once usage counting is on.',
  'plan.failed': 'Couldn’t load your plan.',
  'plan.retry': 'Try again',
  'plan.refuse.concurrent1': 'OBS was refused: one broadcast at a time on {plan}, and another stream is on air. End it, or choose a plan with more.',
  'plan.refuse.concurrentN': 'OBS was refused: {n} broadcasts at a time on {plan}, and that many are on air. End one, or choose a plan with more.',
  'plan.refuse.streamHours': 'OBS was refused: this month’s hours on air on {plan} are used up. Broadcasts start again next period, or with a bigger plan.',
  'plan.refuse.destinations': '{n} of {n} destinations on {plan}. Remove one, or choose a plan with more.',
  'plan.refuse.sourceGrace': 'Not saved: {plan} holds outputs up to {max} after OBS drops. Pick a shorter time, or a bigger plan.',
  'plan.refuse.noCaptions': 'Live captions are not on {plan}.',
  'plan.refuse.captionsHours': 'Captions are off: this period’s captions hours on {plan} are used up. Your broadcast is not affected. Captions come back next period, or with a bigger plan.',
  'plan.refuse.captionsOverrun': 'Captions stopped 2 h after this period’s captions hours on {plan} ran out. Your broadcast is not affected. The next broadcast starts without captions until the period resets.',
  'plan.refuse.noVertical': 'The vertical stream is not on {plan}. The main stream is not affected.',
  'plan.seePlan': 'Your plan',
  'plan.yourPlan': 'your plan',
  'plan.graceCeiling': 'Up to {max} on {plan}.',
  'plan.graceOver': 'Above what {plan} allows ({max}): the hold may be cut to {max}.',

  'appAuth.title': 'Sign in to {app}',
  'appAuth.wants': '{app} wants to use your Strevix account.',
  'appAuth.on': 'Device: {device}',
  'appAuth.as': 'Signed in as {email}',
  'appAuth.notYou': 'Not you? Sign out',
  'appAuth.can': 'It will be able to:',
  'appAuth.scope.bridge': 'send your stream to Strevix and see whether you’re live',
  'appAuth.scope.outputs': 'see your outputs and turn them on or off (not their keys or settings)',
  'appAuth.scope.streamKeys': 'read your stream keys to stream with them',
  'appAuth.keysWarning':
    'A stream key lets anyone stream to your account. Allow only on your own computer, and only if you started this from OBS just now.',
  'appAuth.cannot': 'It can’t see your password, stream keys or your outputs’ addresses and keys.',
  'appAuth.cannotWithKeys': 'It can’t see your password or your outputs’ addresses and keys.',
  'appAuth.allow': 'Allow',
  'appAuth.cancel': 'Cancel',
  'appAuth.loading': 'Loading…',
  'appAuth.bad': 'This sign-in link isn’t valid or has expired. Start signing in again from the app.',
  'appAuth.back': 'Going back to {app}…',
  'appAuth.backHint': 'If nothing happens, return to {app}: you can close this tab.',
  'appAuth.revokeHint': 'You can sign it out any time in Account → Devices & apps.',

  'device.title': 'Connect a device',
  'device.intro': 'Type the code the app shows.',
  'device.code': 'Code',
  'device.next': 'Continue',
  'device.warn': 'Allow only if you started signing in to {app} yourself, on your own computer. Never type a code someone sent you.',
  'device.badCode': 'That code isn’t valid or has expired. Check it in the app, or start again there for a new one.',
  'device.tooMany': 'Too many wrong codes. Wait a few minutes and try again.',
  'device.allowed': '{app} is signed in. Return to it: you can close this page.',
  'device.denied': 'Sign-in cancelled. The app stays signed out.',
  'device.another': 'Connect another device',

  'devices.title': 'Devices & apps',
  'devices.intro': 'Apps signed in to your account. Sign out one you don’t use or don’t recognize: it stops working at once and has to sign in again.',
  'devices.empty': 'No apps are signed in. Strevix Bridge signs in from its window: Sign in opens this browser.',
  'devices.getApps': 'Get the apps',
  'devices.version': 'Version {v}',
  'devices.lastUsed': 'Last used {when}',
  'devices.neverUsed': 'Not used yet',
  'devices.since': 'Signed in {date}',
  'devices.from': 'from {ip}',
  'devices.signOut': 'Sign out',
  'devices.signingOut': 'Signing out…',
  'devices.everywhere': 'Sign out everywhere',
  'devices.everywhereAsk': 'Sign out all {n} apps? Each has to sign in again.',
  'devices.everywhereYes': 'Sign out all',
  'devices.done': 'Signed out of {n} apps.',
  'devices.failed': 'Couldn’t load your devices. Try again.',
  'devices.retry': 'Try again',

  'captions.panel': 'CAPTIONS',
  'captions.title': 'Captions to YouTube',
  'captions.language': 'Language',
  'captions.langAria': 'Captions language',
  'captions.off': 'Off',
  'captions.auto': 'Auto-detect',
  'captions.st.off': 'Off',
  'captions.st.live': 'Live',
  'captions.st.starting': 'Starting',
  'captions.st.waiting': 'Waiting',
  'captions.st.refused': 'Refused',
  'captions.st.stalled': 'Stalled',
  'captions.offNoDest': 'Turn on Send captions on a YouTube output: captions go to YouTube only for now.',
  'captions.offStarting': 'Starting…',
  'captions.offLater': 'Captions start when you go live.',
  'captions.offPlain': 'Captions are off.',
  'captions.live': 'Captions are on air.',
  'captions.listening': 'Listening for the main language…',
  'captions.starting': 'Connecting the speech recognizer to your stream.',
  'captions.noWorker': 'No speech recognizer is free right now. Your video is not affected; captions start as soon as one is.',
  'captions.refused': 'YouTube refuses the captions. In YouTube Studio turn on Closed captions with “POST captions to URL”. Your video is not affected.',
  'captions.workerLost': 'The speech recognizer disconnected; reconnecting. Your video is not affected.',
  'captions.nodeNo': 'This media server cannot take captions yet. Your video is not affected.',
  'captions.stuck': 'Captions are stuck; we keep trying. Your video is not affected.',
  'captions.detecting': 'detecting language',
  'captions.behind': '{s} s behind',
  'captions.chipRefused': 'CC · REFUSED',
  'dest.capLang': 'Captions on this output',
  'dest.capLangOriginal': 'Original (your stream\'s language)',
  'dest.capLangOrigHint': 'Viewers here read what you say, as you say it.',
  'dest.capLangDelayHint': 'Translated captions: this output\'s video goes about 10 s later so the text lines up with your voice. Machine translation.',
  'dest.capLangConfirm.title': 'Reconnect {name}?',
  'dest.capLangConfirm.toTr': 'Captions in {lang} need the video about 10 s later: {name} reconnects, about 2 s off air there. Other outputs keep streaming.',
  'dest.capLangConfirm.toOrig': 'Back to the original captions without the extra 10 s: this output reconnects, about 2 s off air there. Other outputs keep streaming.',
  'dest.capLangConfirm.no': 'Keep the current language',
  'dest.capLangConfirm.yes': 'Reconnect and save',
  'translate.to': 'Translate to',
  'translate.same': 'Your stream is in this language: its outputs get the original captions.',
  'translate.how': 'Pick languages here, then pick one under “Captions on this output” in a YouTube output.',
  'translate.pickOnOutput': 'To start translating, pick a language under “Captions on this output” in a YouTube output.',
  'translate.st.degraded': 'Slow',
  'translate.st.down': 'Down',
  'translate.live': 'Translation is on air. Translated outputs are about 10 s behind, so the text lines up with your voice.',
  'translate.starting': 'Starting the translation.',
  'translate.degraded': 'Some phrases came too late and were skipped. Your video is not affected.',
  'translate.noCapacity': 'No room for the translator right now: translated outputs show no captions. Your video is not affected.',
  'translate.down': 'Translation is down: translated outputs show no captions until it is back. Your video is not affected.',
  'translate.oldWorker': 'The speech recognizer cannot translate yet. Your video is not affected.',
  'translate.plan': 'Caption translation is not in your plan.',
  'translate.p90': '{s} s per phrase',
  'translate.viewAria': 'Captions to show',
  'translate.original': 'Original',
  'translate.empty': 'Phrases show here as you speak.',

  'slate.row': 'Break screen when OBS drops',
  'slate.nowOn': 'on · after {s} s',
  'slate.nowOff': 'off',
  'slate.switch': 'Show a “be right back” screen when your stream drops',
  'slate.hint': 'Viewers see the Strevix break screen instead of a frozen stream. It switches back as soon as OBS sends again; when the time is up, the platforms end the broadcast.',
  'slate.after': 'Show it after, s',
  'slate.afterHint': 'Seconds without video. Empty: 5 s.',
  'slate.max': 'Keep it for, s',
  'slate.maxPlaceholder': 'hold',
  'slate.maxHint': 'Empty: as long as the hold ({hold}), never longer.',
  'slate.holdDefault': 'the default',
  'slate.range': '{min} to {max} whole seconds',
  'slate.saved': '✓ Saved. Applies from the next time OBS connects.',
  'slate.revoked': 'This key is revoked: its settings can’t be changed. Issue a new key first.',
  'slate.cancel': 'Cancel',
  'slate.save': 'Save',
  'slate.saving': 'Saving…',
  'slate.retry': 'Try again',
  'slate.stage': 'Viewers see the break screen · {left} left',
  'slate.stageLeft': 'OBS stopped sending. Your outputs show “We’ll be right back” and switch back the moment OBS sends again. If it doesn’t, the platforms end the broadcast in {left}.',
  'slate.stageStalled': 'OBS is connected but sends no video. Your outputs show “We’ll be right back” until the picture comes back, at most {left}.',
  'slate.chip': 'BREAK',
  'slate.chipTitle': 'Viewers see the break screen',
  'apiTokens.title': 'API tokens',
  'apiTokens.intro': 'Tokens let your scripts and integrations use the Strevix API without a browser. Each token has its own permissions and expiry. Revoke one and it stops working at once.',
  'apiTokens.loading': 'Loading…',
  'apiTokens.failed': 'Couldn’t load your tokens. Try again.',
  'apiTokens.retry': 'Try again',
  'apiTokens.emptyTitle': 'No API tokens yet',
  'apiTokens.empty': 'Create a token for a script or an integration: turn outputs on and off, create stream keys, follow live events. Every request sends it in a header:',
  'apiTokens.count': '{n} of {max}',
  'apiTokens.create': 'Create token',
  'apiTokens.limit': '{n} of {max} — revoke one first',
  'apiTokens.plan': 'API tokens are available on Pro and Business plans.',
  'apiTokens.planExisting': 'The tokens below don’t work on your current plan. You can still revoke them.',
  'apiTokens.form.title': 'New API token',
  'apiTokens.form.name': 'Name',
  'apiTokens.form.nameHint': 'What uses it, e.g. “CI script”. Up to 64 characters.',
  'apiTokens.form.expiry': 'Expires',
  'apiTokens.form.days': '{n} days',
  'apiTokens.form.year': '1 year',
  'apiTokens.form.never': 'No expiry',
  'apiTokens.form.neverHint': 'A token without expiry works until you revoke it.',
  'apiTokens.form.scopes': 'Permissions',
  'apiTokens.form.pickScope': 'Pick at least one permission.',
  'apiTokens.group.read': 'Read',
  'apiTokens.group.write': 'Write',
  'apiTokens.group.secrets': 'Secrets',
  'apiTokens.secretsWarn': 'Can read or change your stream keys — anyone with this token can stream to your account.',
  'apiTokens.form.submit': 'Create token',
  'apiTokens.form.creating': 'Creating…',
  'apiTokens.form.cancel': 'Cancel',
  'apiTokens.form.error': 'Couldn’t create the token: {msg}',
  'apiTokens.once.title': 'Copy your token now',
  'apiTokens.once.warn': 'You won’t see it again. Keep it in a password manager or your script’s secrets.',
  'apiTokens.once.token': 'Your new token',
  'apiTokens.once.copy': 'Copy',
  'apiTokens.once.copied': 'Copied',
  'apiTokens.once.try': 'Try it',
  'apiTokens.once.done': 'Done',
  'apiTokens.once.ask': 'Close without copying? The token can’t be shown again.',
  'apiTokens.once.closeAnyway': 'Close anyway',
  'apiTokens.once.back': 'Back',
  'apiTokens.row.created': 'Created {date}',
  'apiTokens.row.never': 'Never expires',
  'apiTokens.row.expiresIn': 'Expires in {n} days',
  'apiTokens.row.expiresIn1': 'Expires in 1 day',
  'apiTokens.row.expiresToday': 'Expires today',
  'apiTokens.row.expired': 'Expired',
  'apiTokens.row.lastUsed': 'Last used {when}',
  'apiTokens.row.from': 'from {ip}',
  'apiTokens.row.neverUsed': 'Never used',
  'apiTokens.row.unused': 'Unused for 180+ days',
  'apiTokens.row.revoke': 'Revoke',
  'apiTokens.row.revokeAsk': 'Revoke “{name}”? Anything using it stops at once.',
  'apiTokens.row.revoking': 'Revoking…',
  'apiTokens.row.revokeFailed': 'Couldn’t revoke the token: {msg}',
  'apiTokens.row.owner': 'Owner {email}',
  'apiTokens.row.you': 'you',
  'apiTokens.scope.streams:read': 'Stream keys without secrets, ingest addresses, live state and history',
  'apiTokens.scope.streams:write': 'Create and edit stream keys, change the ingest region',
  'apiTokens.scope.stream_keys:read': 'Read stream key secrets',
  'apiTokens.scope.stream_keys:write': 'Rotate stream keys (the old key stops working)',
  'apiTokens.scope.outputs:read': 'Outputs, platform keys masked',
  'apiTokens.scope.outputs:write': 'Add outputs, turn them on and off, change their settings',
  'apiTokens.scope.events:read': 'Follow live events (server-sent events)',
  'apiTokens.scope.chat:read': 'Read the chats of your outputs',
  'apiTokens.scope.admin:nodes:read': 'Nodes and their hosts',
  'apiTokens.scope.admin:nodes:write': 'Change node settings that need no code (never breaks the air)',
  'apiTokens.scope.admin:routes:read': 'Routes, links and relay events',
  'apiTokens.scope.admin:streams:read': 'Every user’s streams and outputs',
  'apiTokens.scope.admin:audit:read': 'The audit log (SIEM export)',
  'unit.fps': 'fps',
  'unit.kbps': 'kbps',
  'unit.mbps': 'Mbps',
  'tracks.list': 'Tracks in your stream',
  'tracks.video': 'Video',
  'tracks.audio': 'Audio',
  'tracks.ignoredN': '{n} ignored',
  'tracks.endedN': '{n} ended',
  'tracks.ixVideo': 'Video track {n}',
  'tracks.ixAudio': 'Audio track {n}',
  'tracks.default': 'default',
  'tracks.captions': 'captions',
  'tracks.out': '{n} out',
  'tracks.tagVoice': 'Voice',
  'tracks.overVideo': 'Over {n} video tracks on {plan}',
  'tracks.overAudio': 'Over {n} audio tracks on {plan}',
  'tracks.yourPlan': 'your plan',
  'tracks.codec': 'Codec not supported',
  'tracks.ended': 'Ended {s} s ago',
  'tracks.warn.vertical': 'Your OBS sends a 9:16 track; vertical video isn’t in your plan. It still arrives for now.',
  'tracks.warn.ingestMax': 'A track is above your plan’s input quality. It still arrives for now.',
  'tracks.warn.ingestMaxOf': 'A track is above your plan’s input quality ({max}). It still arrives for now.',
  'tracks.strip': 'Tracks',
  'tracks.count': '{v} V · {a} A',
  'dest.videoTrack': 'Video track',
  'dest.audioTrack': 'Audio track',
  'dest.trackH264': '{platform} takes H.264 only.',
  'dest.trackAac': '{platform} takes AAC audio only.',
  'dest.trackGone': 'Not in the stream now',
  'dest.trackNote': 'The new track applies at the next keyframe, without reconnecting.',
  'dest.tracksHidden': 'Tracks {platform} can’t take are not listed: it takes H.264 video and AAC audio only.',
  'row.trackMissing': '{ix} isn’t arriving — sending {to}',
  'row.trackCodec': '{ix} is {codec}: {platform} takes H.264 only',
  'row.trackCodecSending': '{ix} is {codec} — sending {to}',
  'row.changeTrack': 'Change track',
  'captions.voice': 'Voice track',
  'captions.voiceGone': '{ix} isn’t arriving.',
  'captions.voiceGoneBody': 'Captions listen to {to} until it’s back.',
  'dest.hevc': 'Send HEVC and AV1 as they are',
  'dest.hevcHint': '{platform} only. No transcoding: {platform} receives the track’s own codec.',
  'dest.hevcHls': 'With HLS {platform} takes H.264 only. Switch the protocol to RTMPS to send HEVC or AV1.',
  'dest.beta': 'Beta',
  'dest.hevcApplies': 'Applies when you save.',
  'dest.hevcOnNote': 'The {platform} output reconnects once (about 2 s). If {platform} refuses the codec, the output stays on V0 and shows a warning.',
  'dest.hevcOffNote': 'The {platform} output reconnects once (about 2 s) and gets H.264 tracks only.',
  'dest.trackEnhanced': '{platform} gets {codec} only with “Send HEVC and AV1 as they are” on.',
  'dest.hevcRefused': '{platform} by {proto} takes H.264 only: turn off “Send HEVC and AV1 as they are” first.',

  'node.restart': 'Restart node',
  'node.updateLogic': 'Update logic',
  'node.updateAgent': 'Update agent',
  'node.updateHolders': 'Update holders',
  'node.cancel': 'Cancel',
  'node.confirm.restart': 'Restart node {name} on #{n}?',
  'node.confirm.logic': 'Update logic on {name}?',
  'node.confirm.agent': 'Update agent on {name}?',
  'node.onAir.one': '{n} broadcast stays on air',
  'node.onAir.many': '{n} broadcasts stay on air',
  'node.sockets': '{n} sockets close',
  'node.pauseTarget': 'Pause target {n} ms',
  'node.releaseStays': 'Release stays #{n}',
  'node.logicPair': 'Logic {from} → {to}',
  'node.agentPair': 'Agent {from} → {to}',
  'node.agentKeeps': 'Containers and broadcasts are not touched',
  'node.agentBack': 'The old agent comes back if the new one does not reach the center in 2 min',
  'node.code': '6-digit code',
  'node.wrongCode.one': 'Wrong code. 1 try left.',
  'node.wrongCode.many': 'Wrong code. {n} tries left.',
  'node.locked': 'Too many wrong codes. Confirmations are locked.',
  'node.lockedFor': 'Too many wrong codes. Confirmations are locked for {n} min.',
  'node.busy.restart': 'Restarting node…',
  'node.busy.logic': 'Updating logic…',
  'node.busy.agent': 'Updating agent…',
  'node.done.restart.one': 'Restarted on #{n} and {k} broadcast stays on air.',
  'node.done.restart.many': 'Restarted on #{n} and {k} broadcasts stay on air.',
  'node.done.logic.one': 'Logic is {version} and {k} broadcast stays on air.',
  'node.done.logic.many': 'Logic is {version} and {k} broadcasts stay on air.',
  'node.done.agent': 'Agent is {version}. Broadcasts were not touched.',
  'node.done.agentSame': 'The agent already runs {version}. Nothing changed.',
  'node.pause': 'Pause {n} ms.',
  'node.rolledBack': 'Rolled back at {step}: the old logic serves.',
  'node.rollbackFailed': 'The rollback failed too. {what}',
  'node.didNotRun.restart': 'Planned restart did not run. Sockets stay with the holders.',
  'node.didNotRun.logic': 'Logic update did not run. Nothing on {name} changed.',
  'node.didNotRun.agent': 'Agent update did not run. Nothing on {name} changed.',
  'node.toast.restart.one': '{name} restarted on #{n} and {k} broadcast stays on air.',
  'node.toast.restart.many': '{name} restarted on #{n} and {k} broadcasts stay on air.',
  'node.toast.logic.one': '{name} is on logic {version} and {k} broadcast stays on air.',
  'node.toast.logic.many': '{name} is on logic {version} and {k} broadcasts stay on air.',
  'node.toast.agent': '{name} runs agent {version}.',
  'node.why.monolith': 'This node runs one process: migrate the node to the split layout first.',
  'node.why.free': 'Free the node first (move streams).',
  'node.why.holdersChange': 'This release changes the holders too. Free the node first (move streams).',
  'node.why.noCaps': 'The host agent is older: update the release first.',
  'node.why.logicSame': 'Logic is already {version}.',
  'node.why.agentSame': 'Agent is already {version}.',
  'node.why.unknown': 'The center does not know this node’s versions yet.',
  'node.why.noComponents': 'The center’s release names no components: use Update.',
  'node.layout': 'Layout',
  'node.layout.split': 'Split · holders and logic',
  'node.layout.monolith': 'One process',
  'node.layout.unknown': 'Not reported yet',
  'node.activeLogic': 'Active logic',
  'node.generation': 'generation {n}',
  'node.components': 'Components',
  'node.comp.logic': 'Logic',
  'node.comp.ingress': 'Ingress holder',
  'node.comp.egress': 'Egress holder',
  'node.comp.netns': 'Network',
  'node.comp.caddy': 'Caddy',
  'node.comp.agent': 'Agent',
  'node.step.checks': 'Checks',
  'node.step.start': 'Start',
  'node.step.promote': 'Promote',
  'node.step.verify': 'Verify',
  'node.step.finish': 'Finish',
  'node.step.rollback': 'Rollback',
  'node.steps': 'Steps',
  'node.code.busy': 'Another switch is running on this host.',
  'node.code.node_not_split': 'This node runs one process: migrate the node to the split layout first.',
  'node.code.holders_unhealthy': 'The holders do not answer.',
  'node.code.color_mismatch': 'The node’s colors do not match its settings.',
  'node.code.holders_changed': 'The holders’ definitions changed on the host. Free the node first (move streams).',
  'node.code.holders_must_change': 'This release changes the holders too. Free the node first (move streams).',
  'node.code.image': 'The logic image on the host is not the release’s.',
  'node.code.start_failed': 'The standby logic did not start.',
  'node.code.ready_timeout': 'The standby logic was not ready in 30 s.',
  'node.code.promote_failed': 'The standby did not take over.',
  'node.code.stale_generation': 'The node refused the generation.',
  'node.code.retire_failed': 'The old logic did not step down.',
  'node.code.center_timeout': 'The new logic did not reach the center in 5 s.',
  'node.code.verify_failed': 'A check after the switch failed.',
  'node.code.rollback_failed': 'The rollback failed too.',
  'node.code.needs_deploy': 'The host agent is older: update the release first.',
  'node.code.up_to_date': 'The node already runs this version.',
  'node.code.changes_unknown': 'The center does not know this node’s versions yet. Try again in a few seconds.',
  'node.code.no_components': 'The center’s release names no components: use Update.',
  'node.code.publishers_present': 'Free the node first (move streams).',
  'node.code.self_update_off': 'Self-update is off on this host.',
  'node.code.offline': 'The host agent is offline.',
  'node.fleet.open': 'Update nodes…',
  'node.fleet.title': 'Update nodes',
  'node.fleet.what': 'What to update',
  'node.fleet.logic': 'Logic',
  'node.fleet.agent': 'Agent',
  'node.fleet.nodes': 'Nodes',
  'node.fleet.oneCode': 'One code covers every node: the first node takes it, the others follow in parallel within the minute.',
  'node.fleet.send.one': 'Update 1 node',
  'node.fleet.send.many': 'Update {n} nodes',
  'node.fleet.busy': 'Updating nodes…',
  'node.fleet.none': 'No node can take this update now.',
  'node.fleet.results': 'Results',
  'node.fleet.col.node': 'Node',
  'node.fleet.col.outcome': 'Outcome',
  'node.fleet.col.pause': 'Pause',
  'node.fleet.col.generation': 'Generation',
  'node.fleet.done': '{ok} of {n} nodes updated.',
  'node.fleet.notConfirmed': 'The code was not taken: the other nodes were not touched.',
  'node.fleet.close': 'Close',
  'node.ev.policyRefuseMissing': '{node} cannot refuse streams by plan (no policy_refuse/1): refused streams get no outputs there. Update the node.',
  'node.timeout': 'No result yet: the switch may still run. Its result shows up in the host’s commands.',
  'node.why.behind': 'This host is behind the center’s release: update the logic or the release first.',
  'group.title': 'Node group {name}',
  'group.back': 'Nodes',
  'group.fact.address': 'Address',
  'group.fact.members': 'Members',
  'group.fact.take': 'Take connections',
  'group.fact.publishers': 'Publishers',
  'group.fact.weights': 'Weights',
  'group.fact.noZone': 'not published yet',
  'group.col.member': 'Member',
  'group.col.state': 'State',
  'group.col.load': 'Load',
  'group.col.share': 'Share',
  'group.col.live': 'Of live',
  'group.col.cap': 'Cap',
  'group.col.weight': 'Weight',
  'group.col.publishers': 'Publishers',
  'group.col.actions': 'Actions',
  'group.state.joining': 'JOINING',
  'group.state.serving': 'OK',
  'group.state.draining': 'DRAINING',
  'group.state.drained': 'DRAINED',
  'group.state.maintenance': 'MAINTENANCE',
  'group.state.down': 'DOWN',
  'group.was': 'was {state}',
  'group.hint.joining': 'Takes a growing share of new connections: step {n} of 10.',
  'group.hint.serving': 'Takes its full share of new connections.',
  'group.hint.draining': 'Takes no new connections. Publishers on it stay until they end or until the deadline.',
  'group.hint.drained': 'Nothing left on the node. It can go to maintenance.',
  'group.hint.maintenance': 'The node may go down: no alerts. Return it to take connections again.',
  'group.hint.down': 'The center does not reach this node; its name points at the other members.',
  'group.tag.auto': 'AUTO',
  'group.tag.manual': 'MANUAL',
  'group.capHeld': 'At its cap: takes no new connections until it is under it.',
  'group.ramp': 'Ramp',
  'group.ramp.step': 'Step {n} of 10',
  'group.ramp.manual': 'Manual ramp: set a weight or end the ramp.',
  'group.load.cpu': "CPU",
  'group.load.net_in': 'net in',
  'group.load.net_out': 'net out',
  'group.btn.weight': 'Edit weight',
  'group.btn.drain': 'Drain node',
  'group.btn.maintenance': 'Maintenance',
  'group.btn.return': 'Return node',
  'group.btn.serve': 'End ramp',
  'group.btn.saveWeight': 'Save weight',
  'group.btn.saveSettings': 'Save settings',
  'group.btn.saveAddr': 'Save address',
  'group.busy': 'Saving…',
  'group.confirm.drain': 'Drain node {name}?',
  'group.drain.noNew': 'It takes no new connections.',
  'group.drain.left': 'Publishers on it now: {n}. They stay until they end or for {min} min, then reconnect to other members.',
  'group.drain.apps': 'Strevix apps on it move to another member before the deadline.',
  'group.confirm.maintenance': 'Put {name} in maintenance?',
  'group.maint.empty': 'The node is empty.',
  'group.maint.down': 'While in maintenance it may go down with no alerts.',
  'group.confirm.return': 'Return {name} to {group}?',
  'group.return.ramp': 'It ramps in over {min} min.',
  'group.return.manual': 'It stays at 0 until you set a weight or end the ramp.',
  'group.return.agent': 'The host agent of {name} must be on line.',
  'group.confirm.serve': 'End the ramp of {name}?',
  'group.serve.full': 'It takes its full share now.',
  'group.confirm.weight': 'Weight of {name}',
  'group.weight.mode': 'Weight',
  'group.weight.auto': 'Auto',
  'group.weight.manual': 'Manual',
  'group.weight.share': 'Share of new connections, %',
  'group.weight.shareHelp': 'The other members split the rest by load.',
  'group.weight.cap': 'Cap, % of connections',
  'group.weight.capHelp': 'Auto never goes above it. Empty = no cap.',
  'group.weight.autoOff': 'Automatic weights are off in this group: every member keeps a manual share.',
  'group.settings': 'Group settings',
  'group.set.autoWeights': 'Automatic weights',
  'group.set.autoWeightsHint': 'Shares follow each member\'s load. Off: every member keeps a manual share.',
  'group.set.autoRamp': 'Automatic ramp',
  'group.set.autoRampHint': 'A new or returning member grows to its full share by itself.',
  'group.set.ramp': 'Ramp, s',
  'group.set.rampHelp': '60–3600. Default 600.',
  'group.set.drain': 'Drain deadline, min',
  'group.set.drainHelp': '1–1440. Publishers left after it reconnect to other members.',
  'group.set.offload': 'Outputs from another member',
  'group.set.offloadHint': "Outputs of new broadcasts leave from the least loaded outputs node of the group, not from the center. Running broadcasts keep their outputs where they are.",
  'group.confirm.settings': 'Save the settings of {name}?',
  'group.set.changes': 'Changes: {list}.',
  'group.on': 'on',
  'group.off': 'off',
  'group.privateAddr': 'Private address',
  'group.privateAddrHelp': 'IP on the site\'s private network. Group members send relay traffic over it. Empty = public address.',
  'group.done.drain': '{name} takes no new connections.',
  'group.done.maintenance': '{name} is in maintenance.',
  'group.done.return': '{name} returns to the group.',
  'group.done.serve': '{name} takes its full share.',
  'group.done.weight': 'Weight of {name} saved.',
  'group.done.settings': 'Settings of {name} saved.',
  'group.done.addr': 'Private address of {name} saved.',
  'group.err.overCap': 'A manual share of {share} % is over the cap of {cap} %. Lower the share or raise the cap.',
  'group.err.sum': 'Manual shares in this group add up to more than 100 %. Lower this one or another member\'s.',
  'group.err.needShare': 'Enter a share for a manual weight.',
  'group.err.pct': 'Share: 0–100. Cap: above 0, at most 100.',
  'group.err.state': '{name} changed state: this works only when it is {needed}.',
  'group.err.changed': '{name} changed state meanwhile. Check it and try again.',
  'group.err.notMember': '{name} is not in a group anymore.',
  'group.err.offload': "Outputs from another member work only in a node group named <region>-<n>.",
  'group.err.range': 'Ramp: 60–3600 s. Drain deadline: 1–1440 min.',
  'group.err.addr': 'Use an IP address on a private network (10/8, 172.16/12, 192.168/16, 100.64/10, fc00::/7), without a port.',
  'group.err.noHost': '{name} has no host agent: drain and return need one.',
  'group.err.agentOff': 'The host agent of {name} is not connected. Start the node\'s machine and try again.',
  'group.err.prov': 'The second-factor check is not answering. Try again in a minute.',
  'group.err.other': 'The center did not take it: {why}',
  'group.notFound': 'No such group. It may have been deleted.',
  'group.empty': 'No members yet. Put a node in this group from its card: Failover → Region group.',
  'group.row.members': 'members {n}',
  'group.row.publishers': 'publishers {n}',
  'group.row.open': 'Open node group {name}',
  'ingest.region.label': 'Ingest region',
  'ingest.region.auto': 'Auto',
  'ingest.region.autoSub': 'closest server',
  'ingest.region.gone': 'none online',
  'ingest.region.inUse': 'IN USE',
  'ingest.region.inUseHint': 'The server address below is from here',
  'ingest.groupHelp': 'One address for the region. If a server stops, OBS reconnects to another one.',
  'srt.noneIn': 'Servers in {region} don\'t accept SRT yet. Use RTMPS here.',
  'srt.noneInGroup': '{region} doesn\'t accept SRT yet. Use RTMPS here.',
  'srt.noneHere': 'SRT isn\'t available on this server yet. Use RTMPS.',
  'srt.elsewhere': ' Regions with SRT: {list}.',
  'srt.needKey': 'SRT needs an active stream key.',
  'group.copy': 'Copy',
  'group.copied': 'Copied',
  'group.rampFact': 'Ramp {s} s',
  'group.rampManual': 'Ramp by hand',
  'group.set.offloadNew': "Applies to new broadcasts. Running broadcasts are not touched.",
  'group.col.got': "Last 15 min",
  'group.got.hint': "New connections in the last 15 min: {n}.",
  'group.confirm.moveAuto': "Move the outputs of route {route} to a node the center picks?",
  'group.log.pause': "Pause",
  'group.log.resume': "Resume",
  'group.log.paused': "Paused: new entries are not loaded.",
  'group.hub.tag': "OUTPUTS {n}",
  'group.hub.hint': "Sends the outputs of broadcasts that came in on other nodes: {n}.",
  'group.hub.hintIdle': "Outputs node: may send the outputs of other members' new broadcasts.",
  'group.drain.hub': "It carries the outputs of {n} broadcasts of other members. They stay for {min} min, then move to another outputs node: every platform of those broadcasts reconnects, and on Twitch it is a new stream.",
  'group.bc.title': "Broadcasts",
  'group.bc.col.stream': "Stream",
  'group.bc.col.in': "Ingest",
  'group.bc.col.from': "Outputs from",
  'group.bc.col.outputs': "Outputs",
  'group.bc.col.age': "On air",
  'group.bc.center': "CENTER",
  'group.bc.outputsN': "{n} outputs",
  'group.bc.ageH': "{h} h {m} min",
  'group.bc.ageM': "{m} min",
  'group.bc.empty': "No broadcasts in the group now.",
  'group.bc.error': "Relay routes are not answering: the broadcasts of the group are not shown. Moving outputs waits until they answer.",
  'group.btn.move': "Move outputs",
  'group.confirm.move': "Move the outputs of route {route} to {to}?",
  'group.move.warn': "Every platform reconnects: viewers see a short break, and on Twitch it is a new stream.",
  'group.move.from': "Stream {stream}, now from {from}: {n} outputs.",
  'group.move.to': "Move to",
  'group.move.center': "Automatically",
  'group.move.centerPick': "the center picks the node",
  'group.move.type': "Type {route} to confirm",
  'group.move.busy': "Moving outputs…",
  'group.move.why.ingress': "The stream comes in here.",
  'group.move.why.same': "The outputs already leave from here.",
  'group.move.why.noHub': "Not an outputs node: it does not send other members' outputs.",
  'group.move.why.state': "Only a member in OK takes outputs.",
  'group.move.why.centerNow': "The outputs already leave from the center.",
  'group.move.later': "You can try in {n} min.",
  'group.move.err.too_young': "This broadcast has been on air for less than 10 min. Outputs move only after 10 min on air.",
  'group.move.err.moved_recently': "The outputs of this broadcast moved less than 30 min ago. One move per broadcast per 30 min.",
  'group.move.err.target_busy': "{to} took moved outputs less than 5 min ago. Pick another node, or wait.",
  'group.move.err.budget_spent': "{group} has made its {budget} moves for these 5 min.",
  'group.move.err.frozen': "Node names are frozen after a mass loss of nodes. Outputs do not move until the freeze ends.",
  'group.move.err.not_in_group': "The stream comes in outside a node group. Only a node group's outputs move here.",
  'group.move.err.follows_main': "This is a vertical input: it follows its main stream. Move the main stream's outputs.",
  'group.move.err.same_hub': "The outputs already leave from {to}. Nothing moved.",
  'group.move.err.target_not_in_group': "{to} is in another group. Pick a member of {group} or the center.",
  'group.move.err.target_unfit': "{to} cannot take the outputs now: it must be in OK, an outputs node, with a ready relay and room for more relay connections. Nothing moved.",
  'group.move.err.no_target': "No node can take the outputs now. Nothing moved.",
  'group.move.err.route_ended': "The broadcast ended. Nothing moved.",
  'group.move.err.not_on_air': "The broadcast is not on air on its ingest now. Nothing moved.",
  'group.move.err.routes_elsewhere': "Another center instance controls the routes now. Try again in a few seconds.",
  'group.move.err.confirm_required': "The center asks to confirm that every platform reconnects. Nothing moved.",
  'group.done.move': "Outputs of {stream} leave from {to} now. Every platform reconnected.",
  'group.log.title': "Balance journal",
  'group.log.kind': "Kind",
  'group.log.locale': "en-GB",
  'group.log.k.all': "All entries",
  'group.log.k.weights': "Shares",
  'group.log.k.ramp_step': "Ramp steps",
  'group.log.k.cap': "Caps",
  'group.log.k.member_state': "Member states",
  'group.log.k.offload': "Outputs from another member",
  'group.log.k.outputs_moved': "Outputs moved",
  'group.log.k.hub_fallback': "Outputs from the center",
  'group.log.empty': "Nothing in the journal yet. Shares, ramp steps, caps, member states and moved outputs appear here.",
  'group.log.emptyKind': "No entries of this kind in the last 30 days.",
  'group.log.more': "Show more",
  'group.log.share': "share {a} % → {b} %.",
  'group.log.share.load': "share {a} % → {b} %: load {l} ({by}).",
  'group.log.share.takers': "share {a} % → {b} %: the members that take connections changed.",
  'group.log.ramp': "ramp step {n} of 10.",
  'group.log.capHeld': "reached its cap: takes no new connections until it is under it.",
  'group.log.capBelow': "is under its cap again: takes new connections.",
  'group.log.state': "{from} → {to}.",
  'group.log.state.why': "{from} → {to}: {why}.",
  'group.log.why.operator': "an operator's action",
  'group.log.why.drain': "drain order",
  'group.log.why.rampStarts': "the ramp starts",
  'group.log.why.rampDone': "the ramp is done",
  'group.log.why.undrain': "returned to the group",
  'group.log.why.drained': "nothing left on the node",
  'group.log.why.back': "back after a failure",
  'group.log.offloadOn': "outputs from another member on, for new broadcasts.",
  'group.log.offloadOff': "outputs from another member off, for new broadcasts.",
  'group.log.moved.operator': "outputs moved {from} → {to} by an operator. Every platform reconnected.",
  'group.log.moved.drain': "outputs moved {from} → {to}: {from} drained. Every platform reconnected.",
  'group.log.fallback.none': "outputs leave from the center ({to}): no member can carry them.",
  'group.log.fallback.busy': "outputs leave from the center ({to}): the members that could carry them are busy.",
  // GROUP-UI-3: create / compose a node group, readiness, default group, failover panel (AUDIT §17 NG-12…NG-20)
  'gx.create.btn': 'Create node group',
  'gx.create.title': 'Create node group',
  'gx.create.busy': 'Creating…',
  'gx.create.region': 'Region',
  'gx.create.regionHelp': 'From your nodes’ regions. A new one: Other region.',
  'gx.create.other': 'Other region',
  'gx.create.otherLabel': 'New region',
  'gx.create.otherHelp': 'Two letters, a hyphen and a word: eu-central.',
  'gx.create.format': 'Two letters, a hyphen and a word: eu-central.',
  'gx.create.nodes.one': '1 node',
  'gx.create.nodes.many': '{n} nodes',
  'gx.create.index': 'Index',
  'gx.create.indexHelp': '1–9.',
  'gx.create.taken': '{list} exists already.',
  'gx.create.allTaken': 'All 9 groups of {region} exist.',
  'gx.create.name': 'Name',
  'gx.create.nameHelp': 'Encoders and the cabinet see this one address.',
  'gx.create.noZone': 'The full address appears once the provisioner reports the ingest zone.',
  'gx.create.rampHelp': '60–3600.',
  'gx.create.drainHelp': '1–1440.',
  'gx.create.then': 'The group starts empty: add nodes to {name} on its page.',
  'gx.create.done': '{name} created. Add nodes to it.',
  'gx.add.btn': 'Add node',
  'gx.add.title': 'Add a node to {group}',
  'gx.add.label': 'Node',
  'gx.add.none': 'No node can join. Nodes with egress via center, a host agent and no other group can.',
  'gx.add.ramp': '{name} joins as JOINING and ramps in over {min} min.',
  'gx.add.rampManual': '{name} joins as JOINING; the ramp is manual: end it on this page.',
  'gx.add.cert': 'The group’s certificate is reissued with the node’s name.',
  'gx.add.done': '{name} joined {group}.',
  'gx.why.direct': 'Egress is direct. Set egress to via center first.',
  'gx.why.otherGroup': 'In {group}. Remove it there first.',
  'gx.why.noAgent': 'No host agent: drain and maintenance won’t work.',
  'gx.why.revoked': 'Revoked.',
  'gx.why.integrations': 'An integrations node takes no ingest.',
  'gx.remove.btn': 'Remove from group',
  'gx.remove.title': 'Remove {name} from {group}?',
  'gx.remove.empty': '{name} is empty: {n} publishers.',
  'gx.remove.name': 'Its own name points at it again; the group’s certificate is reissued without it.',
  'gx.remove.roles': 'Roles and egress stay as they are.',
  'gx.remove.done': '{name} left {group}.',
  'gx.empty': 'No members yet. Add a node: it ramps in by itself.',
  'gx.delete.btn': 'Delete group',
  'gx.delete.title': 'Delete group {name}?',
  'gx.delete.dns': '{fqdn} leaves DNS and the certificate.',
  'gx.delete.cabinet': 'The cabinet stops offering it as a region.',
  'gx.delete.default': 'It is the default group: users with the automatic region go back to the center’s address.',
  'gx.delete.done': 'Group {name} deleted.',
  'gx.ready.title': 'Readiness',
  'gx.ready.ok': 'OK',
  'gx.ready.pending': 'WAITING',
  'gx.ready.warn': 'CHECK',
  'gx.ready.fail': 'NOT READY',
  'gx.ready.okLine.one': '1 check OK: {list}.',
  'gx.ready.okLine.many': '{n} checks OK: {list}.',
  'gx.ready.open': 'Open {name}',
  'gx.check.cert': 'certificate',
  'gx.check.private': 'private addresses',
  'gx.check.bandwidth': 'bandwidth',
  'gx.r.certPending': 'Certificate is being issued.',
  'gx.r.dnsMissing': '{fqdn} is not in DNS.',
  'gx.r.lua': 'LUA records are off in the zone.',
  'gx.r.luaB': 'Weights can’t work: bare OBS always gets one member.',
  'gx.r.direct': '{name}: egress is direct.',
  'gx.r.private': '{name} has no private address.',
  'gx.r.privateB': 'Members talk over the public network.',
  'gx.r.bandwidth': '{name} declares no bandwidth.',
  'gx.r.sites': 'Members are on {n} sites: {sites}.',
  'gx.r.sitesB': 'The group works; bare OBS may land on either site.',
  'gx.warn.lastAuto': '{name} is the last node of the automatic address.',
  'gx.warn.lastAutoB': 'Users with the automatic region can’t connect until a default group is set or another node takes ingest.',
  'gx.warn.sites': 'Different sites.',
  'gx.err.nodeName': 'A node already has the name {name}. Choose another index.',
  'gx.err.exists': '{name} exists already.',
  'gx.err.format': 'A node group is a region and an index 1–9: eu-central and 1.',
  'gx.err.hasMembers': 'The group has members. Remove them first.',
  'gx.err.cluster': 'Clusters come later: create a node group.',
  'gx.default.title': 'Automatic region',
  'gx.default.field': 'Default group',
  'gx.default.center': 'None: the center',
  'gx.default.save': 'Save default group',
  'gx.default.help': 'Users with the automatic region stream to {fqdn}: OBS and our apps alike.',
  'gx.default.helpCenter': 'Users with the automatic region stream to the center’s address.',
  'gx.default.helpNoGroups': 'No node groups yet. Create one to make it the default.',
  'gx.default.tag': 'DEFAULT',
  'gx.default.confirm': 'Make {name} the default group?',
  'gx.default.confirmCenter': 'Use the center for the automatic region?',
  'gx.default.f1': 'Users with the automatic region get {fqdn}.',
  'gx.default.f2': 'The automatic address points at the group (weights), not at the center.',
  'gx.default.f3': 'Users who picked a region keep it.',
  'gx.default.c1': 'The automatic address points at the center again.',
  'gx.default.done': '{name} is the default group.',
  'gx.default.doneCenter': 'The automatic region uses the center.',
  'gx.fo.title': 'Failover',
  'gx.fo.chip.none': 'NONE',
  'gx.fo.chip.backup': 'BACKUP',
  'gx.fo.chip.group': 'NODE GROUP',
  'gx.fo.chip.old': 'OLD GROUP',
  'gx.fo.mode': 'Mode',
  'gx.fo.mode.none': 'None',
  'gx.fo.mode.backup': 'Backup',
  'gx.fo.mode.group': 'Node group',
  'gx.fo.hint.none': 'The node’s name always points at this node.',
  'gx.fo.hint.backup': 'A backup node takes the name when this one fails or is updated.',
  'gx.fo.hint.group': 'The live members take the group’s name by weight; the whole group down — the group’s backup.',
  'gx.fo.now.none': 'The node’s name always points at this node.',
  'gx.fo.now.backup': 'Backup: {name} takes the name when this node fails or is updated.',
  'gx.fo.now.group': 'Member of {group} ({fqdn}).',
  'gx.fo.now.groupBackup': 'Whole group down: {name}.',
  'gx.fo.now.noGroupBackup': 'No group backup.',
  'gx.fo.openGroup': 'Open the group',
  'gx.fo.old': '{group} is not a node group.',
  'gx.fo.cluster': '{group} is a cluster: clusters come later.',
  'gx.fo.oldB': 'The cabinet doesn’t see it: no weights, no group page. Create a node group and move {name} there.',
  'gx.fo.group': 'Node group',
  'gx.fo.groupHelp': 'Only node groups are listed.',
  'gx.fo.noGroups': 'No node groups yet',
  'gx.fo.members.one': '1 member',
  'gx.fo.members.many': '{n} members',
  'gx.fo.groupBackup': 'Group backup',
  'gx.fo.groupBackupHelp': 'Takes the group’s name when no member is live. Saved at once, with your code.',
  'gx.fo.none': 'None',
  'gx.fo.backupNode': 'Backup node',
  'gx.fo.noRegion': 'no region',
  'gx.fo.apply': 'Apply failover',
  'gx.fo.applying': 'Applying…',
  'gx.fo.saved': 'Saved. The center moves the DNS name by it and issues the certificates the backups need.',
  'gx.fo.groupBackupSet': 'Group {group}: backup {name}.',
  'gx.fo.groupBackupOff': 'Group {group}: backup removed.',
  'gx.fo.moveAway': 'Move the name away for maintenance',
  'gx.fo.restore': 'Return the name',
  'gx.fo.movedAway': 'The name moves to the backup or the group; encoders on this node are told to reconnect.',
  'gx.fo.returned': 'The order is over: the name returns by the usual rules.',
  'gx.fo.egressDirect': 'Egress is direct: this node’s outputs die with it, and a failure ends the platforms’ broadcasts. Set egress to via center in Roles and relay.',
  'gx.fo.provError': 'The provisioner has not taken the model: {why}',
  'gx.fo.dns.serving': 'serving',
  'gx.fo.dns.failedOver': 'moved to {to}',
  'gx.fo.dns.noTarget': '— (no target: stays here)',
  'gx.fo.dns.recovering': 'recovering: back after 5 good heartbeats, 60 s after the switch',
  'gx.fo.dns.order': 'held by an order ({order})',
  'gx.fo.dns.hold': 'flapping: held until {at} UTC',
  'gx.fo.dns.oldAgent': 'older host agent: noticed only after 90 s',
  'gx.fo.reason.agentSilent': 'host agent silent for 3 s',
  'gx.fo.reason.agentOffline': 'host agent disconnected',
  'gx.fo.reason.ingestDown': 'the node does not answer',
  'gx.fo.reason.logicNone': 'no logic process',
  'gx.fo.reason.update': 'update',
  'gx.fo.reason.drain': 'drain',
  'gx.fo.reason.healthy': 'healthy',
  'ingest.region.goneWarn': 'No servers in {region} are online right now. The address below is the automatic one until they are back.',
  'ingest.region.help': 'Pick the region closest to you. The server address changes with it: paste the new one into OBS before you go live.',
  'ingest.region.error': 'Couldn’t change the region: {why}',
  'gx.check.dns': 'DNS name',
  'gx.check.members': 'members',
  'gx.check.default': 'default group',
  'gx.r.certFail': 'Certificate failed.',
  'gx.r.certOff': 'Certificates are off in the provisioner.',
  'gx.r.certOffB': 'RTMPS to the group’s name fails the check on members.',
  'gx.r.certNoMembers': 'No certificate yet: the group has no member.',
  'gx.r.dnsUnknown': 'The zone’s state is not known.',
  'gx.r.dnsMissingB': 'Wait for the next DNS sync (1 min).',
  'gx.r.noMembers': 'The group has no member.',
  'gx.r.noMembersB': 'Add a node.',
  'gx.r.noLive': 'None of the {n} members takes encoders.',
  'gx.r.noLiveB': 'Return a member to serving, or check the nodes’ health.',
  'gx.r.directB': 'Its outputs die with it.',
  'gx.r.noIngest': '{name} takes no ingest.',
  'gx.r.noIngestB': 'The ingress role is off or the node is out of rotation.',
  'gx.r.agentOff': '{name}: host agent offline.',
  'gx.r.agentOffB': 'Drain and maintenance won’t work.',
  'gx.r.bandwidthB': 'Auto weights count it at the group’s median.',
  'gx.r.defaultNoLive': '{group} is the default group, with no live member.',
  'gx.r.defaultNoLiveB': 'Users with the automatic region get the center’s address.',
  'gx.r.defaultApex': 'The automatic address is not aligned yet.',
  'gx.r.defaultApexB': 'The next DNS sync aligns it.',
  'gx.r.other': '{check}: check it.',
  'gx.warn.hub': '{name} carries other members’ outputs.',
  'gx.warn.hubB': 'Taking egress off ends them on every platform: move their outputs first.',
  'gx.warn.sitesB': '{name} is at another site or node region than the other members of {group}. The group works; bare OBS may land on either site.',
  'gx.warn.noLive': '{group} has no live member.',
  'gx.warn.noLiveB': 'Users with the automatic region get the center’s address until one serves.',
  'gx.warn.cert': 'No certificate for {group} yet.',
  'gx.warn.certB': 'RTMPS to it fails the check until it is issued (about 10 min).',
  'gx.warn.dns': '{group} is not in DNS yet.',
  'gx.warn.defaultRemoved': '{group} is the default group.',
  'gx.warn.defaultRemovedB': 'Users with the automatic region get the center’s address; an alert fires until you set another group or clear the setting.',
  'gx.warn.defaultGone': 'The default group is deleted.',
  'gx.warn.autoCenter': 'The automatic region goes to the center’s address.',
  'gx.warn.autoCenterB': 'Check that the center takes ingest.',
  'gx.warn.free': 'This is not a node group.',
  'gx.warn.freeB': 'The cabinet doesn’t see it: no weights, no group page.',
  'gx.warn.other': 'The center warns:',
  'gx.err.notNodeGroup': 'Only a node group can be the default.',
  'gx.default.helpCenterAt': 'Users with the automatic region stream to the center: {rtmp}.',
  'ingest.region.moved': 'Your region {from} is now part of {to}. Your OBS address keeps working.',
  'ingest.region.movedAuto': 'Your region {from} is gone. You stream to the automatic region now.',
  // VERT-V4 (design/vertical/texts.md): the vertical input, its outputs, YouTube Dual stream, Status
  'out.live': 'Live',
  'out.waits': 'Waits for OBS',
  'obs.title': 'OBS settings',
  'obs.protocol': 'Protocol',
  'obs.rtmps': 'RTMPS · server + key',
  'obs.srt': 'SRT · one address',
  'obs.server': 'Server',
  'obs.key': 'Stream key',
  'obs.show': 'Show',
  'obs.copy': 'Copy',
  'obs.newKey': 'New key…',
  'dest.edit': 'Edit output',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'vert.row.main': 'Main',
  'vert.row.vertical': 'Vertical',
  'vert.sum.input': 'Input',
  'vert.sum.bitrate': 'Bitrate',
  'vert.sum.video': 'Video',
  'vert.sum.keyframes': 'Keyframes',
  'vert.sum.dropped': 'Dropped',
  'vert.sum.drift': 'Drift vs main',
  'vert.sum.outputs': 'Outputs',
  'vert.sum.settings': 'Vertical input settings',
  'vert.sum.mainSettings': 'OBS settings',
  'vert.preview.label': 'Vertical input preview',
  'vert.tip.twitch.t': 'Twitch gets your horizontal stream',
  'vert.tip.twitch.b': 'Your vertical stream goes to TikTok, Instagram and YouTube’s vertical slot. Check that this fits Twitch’s simulcasting rules.',
  'vert.tip.twitch.a': 'Open Twitch’s rules',
  'vert.tip.follow.t': 'Vertical outputs follow your vertical input',
  'vert.tip.follow.b': 'If it stops, they switch to your main stream until it’s back. If both stop, they show your break screen.',
  'vert.dual.name': 'Main channel',
  'vert.dual.sub': 'YouTube · Dual stream',
  'vert.dual.h': 'Horizontal',
  'vert.dual.v': 'Vertical',
  'vert.dual.link': 'Linked by you in YouTube Studio — we can’t check this link.',
  'vert.out.auto': 'Vertical (auto)',
  'vert.out.vertical': 'Vertical',
  'vert.out.fallback': 'Main stream — vertical lost {s} s ago',
  'vert.out.nextTime': 'Next broadcast',
  'vert.out.offVertical': 'Won’t go live · Vertical input is off',
  'vert.in.title': 'Vertical input',
  'vert.in.desc': 'Send a second, vertical stream from your encoder. It goes to TikTok, Instagram and YouTube’s vertical slot; other outputs keep your main stream. Same broadcast: it doesn’t count as a second stream on your plan.',
  'vert.in.off': 'Off. Outputs set to Auto send your main stream.',
  'vert.in.turnOn': 'Turn on Vertical input',
  'vert.in.turnOnHint': 'You get a second stream key for the vertical stream. Your main key doesn’t change.',
  'vert.in.turnOff': 'Turn off Vertical input',
  'vert.in.server': 'Server · same as your main stream',
  'vert.in.key': 'Vertical stream key',
  'vert.in.newKeyHint': 'Asks for confirmation; the old vertical key stops at once. The main key is not affected.',
  'vert.in.how': 'Send it from',
  'vert.tab.plugin': 'Strevix for OBS',
  'vert.tab.bridge': 'Strevix Bridge',
  'vert.tab.aitum': 'OBS + Aitum Vertical',
  'vert.tab.xsplit': 'XSplit',
  'vert.tab.other': 'Other encoder',
  'vert.plugin.1': 'Install Strevix for OBS (OBS 31.1 or newer) and the Aitum Vertical plugin for the vertical canvas.',
  'vert.plugin.2': 'In the Strevix dock open **Settings → Vertical** and turn on **Vertical stream**. Signed in, the plugin takes your vertical key from your account — nothing to paste.',
  'vert.plugin.3': 'Pick the canvas (**Aitum Vertical** by default) and the quality: 1080×1920 · 4.5 Mbps.',
  'vert.plugin.4': 'Go live from the dock. Both streams start and stop together; the vertical preview appears here within a few seconds.',
  'vert.bridge.1': 'In Strevix Bridge open the second output and choose **Vertical**. Signed in, Bridge takes your vertical key — nothing to paste.',
  'vert.aitum.1': 'In OBS open **Aitum Vertical → Settings → Stream** and set the server to **Custom RTMP(S)**.',
  'vert.aitum.2': 'Paste the server and the vertical stream key from above.',
  'vert.xsplit.1': 'In XSplit add a second **Custom RTMP** output in Multi Output and paste the server and the vertical stream key.',
  'vert.other.1': 'Add a second RTMP(S) output with the server and the vertical stream key. Send 1080×1920, H.264, a keyframe every 2 s.',
  'vert.in.same': '**Use this address for both streams.** A vertical stream that reaches a different server is refused; your main stream keeps going.',
  'vert.dest.input': 'What to send',
  'vert.dest.auto': 'Auto',
  'vert.dest.autoWhy': 'Vertical: {platform} shows 9:16. Your main stream while Vertical input is off.',
  'vert.dest.main': 'Main stream',
  'vert.dest.vertical': 'Vertical input',
  'vert.dest.verticalWhy': 'If Vertical input is off, this output doesn’t go live.',
  'vert.dest.fallback': 'If the vertical input stops',
  'vert.dest.fbMain': 'Switch to your main stream',
  'vert.dest.fbMainWhy': 'Default. The platform gets your 16:9 picture until vertical is back. If the main stream stops too, your break screen shows.',
  'vert.dest.fbSlate': 'Show a 9:16 break screen',
  'vert.dest.fbSlateWhy': 'The picture stays 9:16. The output waits for vertical and ends after your break-screen time.',
  'vert.dest.note': '**Until your vertical input arrives,** this output sends your main stream.',
  'vert.c2.hevcWhy': '{platform} takes H.264 only.',
  'vert.c2.note': '**Your stream carries a 9:16 track,** so this output picks a track. The vertical input and “What to send” don’t apply here.',
  'vert.yt.title': 'YouTube: horizontal + vertical',
  'vert.yt.stepVertical': 'Vertical input',
  'vert.yt.stepKeys': 'YouTube keys',
  'vert.yt.heading': 'Send both streams to one YouTube broadcast',
  'vert.yt.s1': 'Your encoder sends a vertical stream to your Strevix vertical key. Vertical input is on.',
  'vert.yt.s2': 'In YouTube Studio open **Create → Go live → Live Control Room**.',
  'vert.yt.s3': 'Under **Dual stream** turn on the vertical preview and choose **Encoder** (not Auto — Auto crops your horizontal picture).',
  'vert.yt.s4': 'In the vertical stream settings select a stream key — create a new RTMPS one if needed — and paste it below.',
  'vert.yt.s5': 'Paste your main stream key too.',
  'vert.yt.mainKey': 'Main stream key',
  'vert.yt.vKey': 'Vertical stream key',
  'vert.yt.paste': 'Paste the key from YouTube Studio',
  'vert.yt.warn': '**Start your encoder before the YouTube broadcast goes live.** YouTube can’t add the vertical stream after the start. Linking the second key to the vertical slot happens only in YouTube Studio — we can’t check it.',
  'vert.yt.captions': 'Send captions to the vertical output',
  'vert.yt.experimental': 'Experimental',
  'vert.yt.captionsHint': 'Off by default. Captions always go to the horizontal output; whether YouTube shows them on the vertical one isn’t confirmed yet.',
  'vert.yt.help': 'YouTube Help: Dual stream',
  'vert.yt.add': 'Add both outputs',
  'vert.yt.wait': 'Both YouTube outputs wait up to 15 s for the vertical input and start together.',
  'vert.st.msg': 'Live on {n} outputs. The vertical input drifts {drift} from main.',
  'vert.st.inputs': 'Inputs',
  'vert.st.encoder': 'Encoder',
  'vert.st.driftRow': 'Drift vs main: {drift} since start',
  'vert.st.driftHint': 'One of your encoders is falling behind. Viewers on different platforms won’t notice; captions on vertical outputs may lag.',
  'vert.st.events': 'Events',
  'vert.f.waiting.t': 'Waiting for vertical input',
  'vert.f.waiting.b': 'TikTok, Instagram and YouTube’s vertical slot send your main stream until it arrives.',
  'vert.f.waiting.a': 'Open vertical setup',
  'vert.f.refused.t': 'Vertical input refused',
  'vert.f.refused.a': 'Copy server',
  'vert.f.ytLate.t': 'YouTube won’t add vertical now',
  'vert.f.ytLate.b': 'YouTube won’t add a vertical stream to a broadcast that has started. It will be used next time.',
  'vert.f.plan.t': 'Vertical stream isn’t on your plan',
  'vert.f.plan.more': 'Creator, Pro and Business include it.',
  'vert.f.plan.a': 'Compare plans',
  'vert.f.ready': 'Vertical input ready · waiting for the main input',
  'vert.f.readyChip': 'WAITING FOR MAIN',
  'vert.f.listening': 'Listening',
  'vert.f.listeningB': 'Start streaming in OBS — the picture appears here.',
  'vert.f.off.t': 'Vertical input is off',
  'vert.f.off.b': '{n} outputs are set to Vertical and won’t go live. Auto outputs use your main stream.',
  'vert.f.off.b1': '1 output is set to Vertical and won’t go live. Auto outputs use your main stream.',
  'vert.sum.inN': '{v} in',
  'vert.err.noTranscode': 'A destination that sends the vertical input can’t be optimized yet. Turn off Optimize for {platform}, or send the main stream.',
  'vert.err.sameKey': 'The vertical stream needs its own YouTube stream key. Select another key for the vertical stream in YouTube Studio.',
  'vert.err.inputOff': 'Turn on Vertical input first: YouTube’s vertical slot takes it from your vertical stream key.',
  'vert.err.alreadyOn': 'Vertical input is already on.',
  'vert.err.notMain': 'This stream key is revoked. Issue a new key to turn on Vertical input.',
  'vert.in.rotate.title': 'Issue a new vertical stream key?',
  'vert.in.rotate.body': 'The old vertical key stops at once. Your main key and the broadcast are not affected; paste the new key into your encoder.',
  'vert.in.rotate.yes': 'Issue new key',
} as const

export type Key = keyof typeof en
type Dict = Partial<Record<Key, string>>

/** Filled by the localization stage (decision: EN now, ES and RU next). */
const es: Dict = {
  'auth.footer.status': 'Estado',
  'auth.register.elsewhere': 'Las cuentas se crean en el panel, no en esta dirección. Abre de nuevo tu enlace de invitación o pide uno nuevo al administrador.',
  'dest.hevc': 'Enviar HEVC y AV1 tal cual',
  'dest.hevcHint': 'Solo {platform}. Sin transcodificación: {platform} recibe el códec propio de la pista.',
  'dest.hevcHls': 'Con HLS, {platform} solo acepta H.264. Cambia el protocolo a RTMPS para enviar HEVC o AV1.',
  'dest.beta': 'Beta',
  'dest.hevcApplies': 'Se aplica al guardar.',
  'dest.hevcOnNote': 'La salida de {platform} se reconecta una vez (unos 2 s). Si {platform} rechaza el códec, la salida sigue en V0 y muestra un aviso.',
  'dest.hevcOffNote': 'La salida de {platform} se reconecta una vez (unos 2 s) y recibe solo pistas H.264.',
  'dest.trackEnhanced': '{platform} recibe {codec} solo con «Enviar HEVC y AV1 tal cual» activado.',
  'dest.hevcRefused': '{platform} por {proto} solo acepta H.264: primero desactiva «Enviar HEVC y AV1 tal cual».',
  'unit.fps': 'fps',
  'unit.kbps': 'kbps',
  'unit.mbps': 'Mbps',
  'tracks.list': 'Pistas de tu transmisión',
  'tracks.video': 'Vídeo',
  'tracks.audio': 'Audio',
  'tracks.ignoredN': '{n} ignoradas',
  'tracks.endedN': '{n} terminadas',
  'tracks.ixVideo': 'Pista de vídeo {n}',
  'tracks.ixAudio': 'Pista de audio {n}',
  'tracks.default': 'predeterminada',
  'tracks.captions': 'subtítulos',
  'tracks.out': '{n} sal.',
  'tracks.tagVoice': 'Voz',
  'tracks.overVideo': 'Más de {n} pistas de vídeo en {plan}',
  'tracks.overAudio': 'Más de {n} pistas de audio en {plan}',
  'tracks.yourPlan': 'tu plan',
  'tracks.codec': 'Códec no compatible',
  'tracks.ended': 'Terminó hace {s} s',
  'tracks.warn.vertical': 'Tu OBS envía una pista 9:16; el vídeo vertical no está en tu plan. Por ahora sigue llegando.',
  'tracks.warn.ingestMax': 'Una pista supera la calidad de entrada de tu plan. Por ahora sigue llegando.',
  'tracks.warn.ingestMaxOf': 'Una pista supera la calidad de entrada de tu plan ({max}). Por ahora sigue llegando.',
  'tracks.strip': 'Pistas',
  'tracks.count': '{v} V · {a} A',
  'dest.videoTrack': 'Pista de vídeo',
  'dest.audioTrack': 'Pista de audio',
  'dest.trackH264': '{platform} solo acepta H.264.',
  'dest.trackAac': '{platform} solo acepta audio AAC.',
  'dest.trackGone': 'No está en la transmisión ahora',
  'dest.trackNote': 'La nueva pista se aplica en el siguiente fotograma clave, sin reconectar.',
  'dest.tracksHidden': 'No se muestran las pistas que {platform} no acepta: solo acepta vídeo H.264 y audio AAC.',
  'row.trackMissing': '{ix} no llega: se envía {to}',
  'row.trackCodec': '{ix} es {codec}: {platform} solo acepta H.264',
  'row.trackCodecSending': '{ix} es {codec}: se envía {to}',
  'row.changeTrack': 'Cambiar pista',
  'captions.voice': 'Pista de voz',
  'captions.voiceGone': '{ix} no llega.',
  'captions.voiceGoneBody': 'Los subtítulos escuchan {to} hasta que vuelva.',
  'tc.running': 'Optimizado para {platform} · {res} · {mbps} Mbps',
  'tc.starting': 'Iniciando el optimizador para {platform}…',
  'tc.passthrough': 'Original · cumple con {platform}',
  'tc.original': 'Señal original a {platform}',
  'tc.bitrateOver': 'Tu OBS envía {in} Mbps; {platform} recibe {out} Mbps',
  'tc.resolutionOver': 'Tu OBS envía {in}; {platform} recibe {out}',
  'tc.gop': 'Un fotograma clave cada 2 s para {platform}',
  'tc.codec': 'Convertido a H.264 para {platform}',
  'tc.audioOver': 'El audio supera lo que acepta {platform}: pon el audio de OBS en 160 kbps',
  'tc.fallback': 'Optimizador no disponible: {platform} recibe tu señal original',
  'tc.fallbackOver': 'Optimizador no disponible: {platform} recibe tu señal original ({in} Mbps, por encima de los {max} Mbps de {platform})',
  'tc.planNone': 'La optimización para {platform} no está en tu plan: recibe tu señal original',
  'wm.row': 'Marca de Strevix · {res} · plan Free',
  'wm.rowNoRes': 'Marca de Strevix · plan Free',
  'wm.rowTitle': 'El público de esta salida ve la marca de Strevix arriba a la derecha. Los planes de pago van sin ella.',
  'wm.toastOn.title': 'Marca de Strevix en tus salidas',
  'wm.toastOn.body': 'Ahora tu plan es Free: el público ve la marca de Strevix en todas las salidas.',
  'wm.toastOff.title': 'Se quitó la marca de Strevix',
  'wm.toastOff.body': 'Tu plan es {plan}; las salidas van sin ella.',
  'cd.http': 'Subtítulos · {platform}',
  'cd.httpLang': 'Subtítulos · {platform} · {lang}',
  'cd.cc608': 'CC · {lang} (CEA-608)',
  'cd.burn': 'Subtítulos incrustados · {lang} — usa horas de transcodificación',
  'cd.burnOriginal': 'Subtítulos incrustados — usa horas de transcodificación',
  'cd.burnFixed': 'Los subtítulos incrustados forman parte del video: el público no puede ocultarlos.',
  'cd.burn608': 'CEA-608 no admite el cirílico: en {platform} los subtítulos se incrustarán en el video (horas de transcodificación).',
  'cd.off': 'Subtítulos desactivados en esta salida',
  'cd.waiting': 'Subtítulos · esperando el idioma',
  'cd.nodeNo608': 'Este servidor aún no puede añadir CC al video: {platform} recibe la señal sin subtítulos.',
  'cd.planNone': 'Los subtítulos no están en tu plan.',
  'cd.planTranslate': 'La traducción de subtítulos no está en tu plan: {platform} no recibe subtítulos.',
  'cd.burnNotReady': 'Los subtítulos incrustados aún no están disponibles: {platform} recibe el video sin subtítulos.',
  'cd.noTranscoder': 'No hay transcodificador libre: {platform} recibe el video sin subtítulos.',
  'cd.hoursOut': 'Se agotaron las horas de transcodificación de este periodo: {platform} recibe el video sin subtítulos.',
  'cd.cc608NotReady': 'CC en el video aún no está disponible: {platform} recibe la señal sin subtítulos.',
  'cd.httpOff': 'Activa Enviar subtítulos en los ajustes de esta salida para mostrarlos en {platform}.',
  'cd.notTranslated': 'Tu transmisión no se traduce al {lang}: actívalo en el panel de subtítulos.',
  'cd.unavailable': 'Los subtítulos no están disponibles en {platform} ahora.',
  'cd.setting': 'Subtítulos en esta salida',
  'cd.settingHint.http': '{platform} recibe tus subtítulos como pista que el público puede activar.',
  'cd.settingHint.cea608': '{platform} recibe subtítulos en inglés o español como CC en el video; el público puede activarlos.',
  'cd.settingHint.burn': 'Los subtítulos se dibujan en el video de {platform}: el público no puede ocultarlos y el tiempo cuenta en tus horas de transcodificación.',
  'cd.lang.en': 'inglés',
  'cd.lang.es': 'español',
  'cd.lang.ru': 'ruso',
  'tc.planOutputs': 'Ya usas todos los destinos optimizados de tu plan: {platform} recibe tu señal original',
  'tc.planHours': 'Se agotaron las horas de optimización del periodo: {platform} recibe tu señal original',
  'tc.switch': 'Optimizar para {platform}',
  'tc.switchSub': 'Si tu OBS envía más de lo que acepta {platform} (bitrate, resolución, fotogramas clave), hacemos una copia que cumple. Las señales que ya cumplen van tal cual.',
  'tc.saveNote': 'Se aplica en el siguiente fotograma clave, sin reconectar.',
  'slate.row': 'Pantalla de pausa si OBS se corta',
  'slate.nowOn': 'activa · tras {s} s',
  'slate.nowOff': 'desactivada',
  'slate.switch': 'Mostrar una pantalla “volvemos enseguida” si se corta la emisión',
  'slate.hint': 'Los espectadores ven la pantalla de pausa de Strevix en lugar de una imagen congelada. Vuelve a la emisión en cuanto OBS envía de nuevo; si se acaba el tiempo, las plataformas terminan la emisión.',
  'slate.after': 'Mostrarla tras, s',
  'slate.afterHint': 'Segundos sin vídeo. Vacío: 5 s.',
  'slate.max': 'Mantenerla, s',
  'slate.maxPlaceholder': 'retención',
  'slate.maxHint': 'Vacío: lo que dura la retención ({hold}), nunca más.',
  'slate.holdDefault': 'la predeterminada',
  'slate.range': 'De {min} a {max} segundos enteros',
  'slate.saved': '✓ Guardado. Se aplica la próxima vez que OBS se conecte.',
  'slate.revoked': 'Esta clave está revocada: no se pueden cambiar sus ajustes. Genera una clave nueva.',
  'slate.cancel': 'Cancelar',
  'slate.save': 'Guardar',
  'slate.saving': 'Guardando…',
  'slate.retry': 'Reintentar',
  'slate.stage': 'Los espectadores ven la pantalla de pausa · quedan {left}',
  'slate.stageLeft': 'OBS dejó de enviar. Tus salidas muestran “Volvemos enseguida” y vuelven a la emisión en cuanto OBS envíe de nuevo. Si no, las plataformas terminan la emisión en {left}.',
  'slate.stageStalled': 'OBS está conectado pero no envía vídeo. Tus salidas muestran “Volvemos enseguida” hasta que vuelva la imagen, como máximo {left}.',
  'slate.chip': 'PAUSA',
  'slate.chipTitle': 'Los espectadores ven la pantalla de pausa',
  'right.chats': 'Chats',
  'right.chatsN': '{n} leyendo',
  'chats.outputs': 'SALIDAS',
  'chats.offAir': '{name} está fuera del aire',
  'chats.needsYou': '{name} te necesita',
  'chats.fix': 'Arreglar',
  'chats.access': 'Acceso al chat por salida',
  'chats.noOutputs': 'Activa una salida para ver su chat aquí.',
  'chats.reads': 'LEE EL CHAT',
  'chats.connect': 'Conectar cuenta',
  'chats.opening': 'Abriendo {platform}…',
  'chat.feed': 'Mensajes del chat',
  'chat.filterLabel': 'Chat de',
  'chat.viewLabel': 'Mostrar',
  'chat.all': 'Todos',
  'chat.view.all': 'Todo',
  'chat.view.qa': 'Preguntas',
  'chat.view.highlights': 'Destacados',
  'chat.prelive': 'Los chats se abren cuando sales en vivo.',
  'chat.preliveNote': 'Para el chat hay que conectar la cuenta de la plataforma, no basta con la clave de transmisión.',
  'chat.empty': 'Aún no hay mensajes. Aparecerán aquí cuando escriban los espectadores.',
  'chat.emptyView': 'Aún no hay nada aquí.',
  'chat.paused': 'En pausa · {n} nuevos ↓',
  'chat.pausedNone': 'En pausa · volver al directo ↓',
  'chat.first': 'PRIMER MENSAJE',
  'chat.role.owner': 'DUEÑO',
  'chat.role.mod': 'MOD',
  'chat.role.vip': 'VIP',
  'chat.role.staff': 'STAFF',
  'chat.kind.paid': 'PAGO',
  'chat.kind.membership': 'SUSCRIPCIÓN',
  'chat.kind.raid': 'RAID',
  'chat.kind.announcement': 'ANUNCIO',
  'chat.tier': 'NIVEL {n}',
  'chat.months': '{n} MESES',
  'chat.gifts': '{n} REGALOS',
  'chat.viewers': '{n} ESPECTADORES',
  'chat.replyTo': 'respondiendo a {name}',
  'chat.reply': 'Responder desde el Estudio',
  'chat.reconnecting': 'Reconectando los chats…',
  'chat.limited': 'Chats en pausa: hay demasiadas pestañas o dispositivos abiertos.',
  'chat.unread': '{n} sin leer',
  'chat.openOf': 'Chat de {name}',
  'chat.notReading': 'Salidas cuyo chat no se lee',
  'chat.reconnect': 'Volver a conectar',
  'chat.access.connect': 'Conecta tu cuenta de {platform} para leer su chat',
  'chat.access.otherChannel': 'La clave de transmisión es de otro canal que la cuenta de {platform} conectada',
  'chat.access.reconnect': 'Conecta {platform} de nuevo para permitir el chat',
  'chat.access.comingLater': 'El chat de {platform} llegará más adelante',
  'chat.access.notConfigured': 'El chat de {platform} aún no está disponible en este servidor',
  'chat.access.unsupported': '{platform} no comparte el chat con otras apps',
  'chat.access.custom': 'RTMP personalizado no tiene chat',
  'chat.access.dropped': 'Se perdió la conexión del chat · reconectando',
  'studio.limited': 'Datos en vivo en pausa: hay demasiadas pestañas o dispositivos abiertos. Cierra algunos para reanudar.',
  'proto.title': 'Protocolo hacia {platform}',
  'proto.recommended': 'Recomendado',
  'proto.rtmps.sub': 'La forma habitual. El menor retraso.',
  'proto.hls.sub': 'Más estable en redes inestables; unos segundos más de retraso.',
  'proto.hls.hint1': 'El retraso es unos segundos mayor que con RTMPS, y el modo de latencia ultrabaja de YouTube no está disponible.',
  'proto.hls.hint2': 'Pon el intervalo de fotogramas clave en 2 s con GOP cerrado: YouTube rechaza segmentos de más de 5 s.',
  'proto.hls.hint3': 'Los subtítulos incrustados en el video (CEA-608) solo funcionan con H.264.',
  'proto.keySteps': 'Clave de transmisión para {proto}',
  'proto.keyNote': 'YouTube crea una clave de transmisión aparte para HLS (los pasos de arriba). Pega esa clave abajo.',
  'proto.serverHls': 'Dirección de carga HLS de YouTube, ya rellenada. La clave va en su propio campo.',
  'proto.urlHttps': 'Empieza con https:// para HLS',
  'proto.liveNote': 'Estás en vivo: al cambiar, {name} se reconecta y queda unos segundos fuera del aire. Las demás salidas siguen transmitiendo.',
  'proto.offNote': 'Se aplica la próxima vez que {name} salga en vivo.',
  'proto.confirm.title': 'Esto reconectará {platform}',
  'proto.confirm.body': '{name} pasa a {proto}: unos segundos fuera del aire allí. Las demás salidas siguen transmitiendo.',
  'proto.confirm.yes': 'Cambiar y reconectar',
  'proto.confirm.no': 'Mantener {proto}',
  'proto.conflict': 'Esta salida cambió mientras tanto, en otra pestaña o dispositivo. Cierra la configuración, ábrela de nuevo y vuelve a intentarlo.',
  'proto.chip': 'Se envía a {platform} por HLS',
  'row.gopNote': 'Fotogramas clave a más de 5 s: YouTube puede descartar partes. Pon el intervalo de fotogramas clave en 2 s en OBS.',
  'dest.capLang': 'Subtítulos en esta salida',
  'dest.capLangOriginal': 'Original (el idioma de tu transmisión)',
  'dest.capLangOrigHint': 'Aquí se lee lo que dices, mientras lo dices.',
  'dest.capLangDelayHint': 'Subtítulos traducidos: el video de esta salida va unos 10 s después para que el texto coincida con tu voz. Traducción automática.',
  'dest.capLangConfirm.title': '¿Reconectar {name}?',
  'dest.capLangConfirm.toTr': 'Los subtítulos en {lang} necesitan el video unos 10 s después: {name} se reconecta, unos 2 s fuera del aire allí. Las demás salidas siguen transmitiendo.',
  'dest.capLangConfirm.toOrig': 'Volver a los subtítulos originales sin los 10 s extra: esta salida se reconecta, unos 2 s fuera del aire allí. Las demás salidas siguen transmitiendo.',
  'dest.capLangConfirm.no': 'Mantener el idioma actual',
  'dest.capLangConfirm.yes': 'Reconectar y guardar',
  'translate.to': 'Traducir a',
  'translate.same': 'Tu transmisión está en este idioma: sus salidas reciben los subtítulos originales.',
  'translate.how': 'Elige idiomas aquí y luego uno en «Subtítulos en esta salida» de una salida de YouTube.',
  'translate.pickOnOutput': 'Elige un idioma en «Subtítulos en esta salida» de una salida de YouTube para empezar a traducir.',
  'translate.st.degraded': 'Lenta',
  'translate.st.down': 'Caída',
  'translate.live': 'La traducción está al aire. Las salidas traducidas van unos 10 s atrás para que el texto coincida con tu voz.',
  'translate.starting': 'Iniciando la traducción.',
  'translate.degraded': 'Algunas frases llegaron tarde y se omitieron. Tu video no se ve afectado.',
  'translate.noCapacity': 'No hay espacio para el traductor ahora: las salidas traducidas no muestran subtítulos. Tu video no se ve afectado.',
  'translate.down': 'La traducción no funciona: las salidas traducidas no muestran subtítulos hasta que vuelva. Tu video no se ve afectado.',
  'translate.oldWorker': 'El reconocedor de voz todavía no puede traducir. Tu video no se ve afectado.',
  'translate.plan': 'La traducción de subtítulos no está en tu plan.',
  'translate.p90': '{s} s por frase',
  'translate.viewAria': 'Subtítulos a mostrar',
  'translate.original': 'Original',
  'translate.empty': 'Las frases aparecen aquí mientras hablas.',
  'tip.input.ingest_mismatch': 'Otra de tus transmisiones está en otro servidor de ingesta. Envía todas tus transmisiones (principal y vertical) a la misma dirección de servidor.',
  'account.billing': 'Plan y uso',
  'plan.title': 'Plan y uso',
  'plan.intro': 'Tu plan, lo que incluye y por qué una emisión puede no empezar. Los pagos aún no están abiertos.',
  'plan.loading': 'Cargando tu plan…',
  'plan.label': 'Plan',
  'plan.choose': 'Elegir un plan',
  'plan.chooseHint': 'Abre la página de precios. Los pagos en la cuenta llegarán pronto.',
  'plan.trialLeft': 'Prueba · quedan {days}',
  'plan.trialEnds': 'Prueba · termina hoy',
  'plan.days.one': '{n} día',
  'plan.days.other': '{n} días',
  'plan.days.many': '{n} días',
  'plan.legacyUntil': 'Legacy hasta el {date}',
  'plan.giftUntil': '{name} — regalo hasta el {date}',
  'plan.gift': '{name} — regalo',
  'plan.endedFree': '{name} · tu plan anterior terminó',
  'plan.paymentFailed': '{name} · el pago falló',
  'plan.canceledUntil': '{name} · cancelado, funciona hasta el {date}',
  'plan.trialNote': 'La prueba termina el {date}. Después tu cuenta pasa a Free; tus salidas y ajustes se conservan.',
  'plan.giftNote': 'Te lo regala Strevix. El {date} la cuenta vuelve a su propio plan.',
  'plan.giftForever': 'Te lo regala Strevix, sin fecha de fin.',
  'plan.ambassadorNote': 'Todas las funciones del servicio, como embajador de Strevix.',
  'plan.legacyNote': 'Te uniste antes de que existieran los planes: tus límites siguen igual hasta el {date}. Después la cuenta pasa a Free si no eliges un plan.',
  'plan.freeNote': 'El paquete mínimo: {streams} emisión a la vez, {dests} destinos, {hours} h al aire al mes. Elige un plan para tener más.',
  'plan.endedNote': 'Tu plan anterior terminó, así que la cuenta está en Free. Tus salidas y ajustes se conservan; las que superan el límite quedan apagadas hasta que elijas un plan.',
  'plan.paymentNote': 'El último pago no se completó. Todo funciona 7 días desde el fallo; después la cuenta pasa a Free.',
  'plan.access.account_frozen': 'Tu cuenta está congelada: las nuevas emisiones no empiezan. La que ya está al aire continúa. Contacta con soporte.',
  'plan.access.account_streams_blocked': 'Las nuevas emisiones están bloqueadas en tu cuenta. La que ya está al aire continúa. Contacta con soporte.',
  'plan.access.plan_payment_disputed': 'Hay un pago en disputa: las nuevas emisiones están bloqueadas hasta que se resuelva. La que ya está al aire continúa.',
  'plan.access.plan_paused': 'Tu plan está en pausa: las nuevas emisiones no empiezan hasta que se reanude.',
  'plan.access.other': 'Ahora mismo las nuevas emisiones no empiezan en tu cuenta. Contacta con soporte.',
  'plan.bar.trial': 'Prueba · quedan {days}',
  'plan.bar.trialEnds': 'Prueba · termina hoy',
  'plan.bar.ended': 'Plan terminado — Free',
  'plan.bar.paymentFailed': 'El pago falló',
  'plan.bar.frozen': 'Congelada',
  'plan.bar.paused': 'Plan en pausa',
  'plan.bar.blocked': 'Emisiones bloqueadas',
  'plan.bar.hint': 'Tu plan: abrir Plan y uso',
  'plan.limits': 'Límites',
  'plan.features': 'Funciones',
  'plan.included': 'Incluido',
  'plan.notOnPlan': 'No en este plan',
  'plan.comingSoon': 'Próximamente',
  'plan.noLimit': 'Sin límite',
  'plan.notIncluded': 'No incluido',
  'plan.unit.hMonth': '{n} h al mes',
  'plan.limit.destinations': 'Destinos',
  'plan.limit.concurrent_streams': 'Emisiones simultáneas',
  'plan.limit.stream_hours_month': 'Horas al aire',
  'plan.limit.ingest_max': 'Entrada desde OBS',
  'plan.limit.output_max': 'Salida a plataformas',
  'plan.outputMarkNote': 'Con la marca de Strevix. Los planes de pago envían tu transmisión tal como llega.',
  'plan.limit.source_grace_max_s': 'Mantener si OBS se corta',
  'plan.limit.captions_hours_month': 'Subtítulos en vivo',
  'plan.limit.transcode_outputs': 'Salidas transcodificadas',
  'plan.limit.transcode_hours_month': 'Transcodificación',
  'plan.limit.translation_lang_hours_month': 'Traducción de subtítulos',
  'plan.ingestWarn': 'Por ahora se acepta una entrada mayor; solo avisamos.',
  'plan.hoursFair': 'Uso razonable: te escribimos antes de que cambie nada.',
  'plan.hoursHard': 'En el límite una nueva emisión no empieza; la que ya está al aire continúa.',
  'plan.feature.chat': 'Chat unificado',
  'plan.feature.captions': 'Subtítulos en vivo',
  'plan.feature.caption_translation': 'Traducción de subtítulos',
  'plan.feature.transcode': 'Transcodificación para plataformas',
  'plan.feature.vertical_9_16': 'Transmisión vertical 9:16',
  'plan.feature.srt_ingest': 'Entrada SRT',
  'plan.feature.api_tokens': 'Tokens de API',
  'plan.feature.no_watermark': 'Sin marca de Strevix',
  'plan.usageLater': 'Las horas usadas este mes aparecerán aquí cuando el conteo de uso esté activo.',
  'plan.failed': 'No se pudo cargar tu plan.',
  'plan.retry': 'Reintentar',
  'plan.refuse.concurrent1': 'OBS fue rechazado: en {plan} hay una emisión a la vez y otra transmisión está al aire. Termínala o elige un plan con más.',
  'plan.refuse.concurrentN': 'OBS fue rechazado: en {plan} hay {n} emisiones a la vez y ya están al aire. Termina una o elige un plan con más.',
  'plan.refuse.streamHours': 'OBS fue rechazado: se agotaron las horas al aire de este mes en {plan}. Las emisiones vuelven en el próximo periodo o con un plan mayor.',
  'plan.refuse.destinations': '{n} de {n} destinos en {plan}. Elimina uno o elige un plan con más.',
  'plan.refuse.sourceGrace': 'No guardado: {plan} mantiene las salidas hasta {max} tras el corte de OBS. Elige menos tiempo o un plan mayor.',
  'plan.refuse.noCaptions': 'Los subtítulos en vivo no están en {plan}.',
  'plan.refuse.captionsHours': 'Los subtítulos están apagados: se agotaron las horas de subtítulos de este periodo en {plan}. Tu emisión no se ve afectada. Vuelven en el próximo periodo o con un plan mayor.',
  'plan.refuse.captionsOverrun': 'Los subtítulos se detuvieron 2 h después de agotarse las horas de subtítulos de este periodo en {plan}. Tu emisión no se ve afectada. La próxima emisión empieza sin subtítulos hasta que se renueve el periodo.',
  'plan.refuse.noVertical': 'La transmisión vertical no está en {plan}. La principal no se ve afectada.',
  'plan.seePlan': 'Tu plan',
  'plan.yourPlan': 'tu plan',
  'plan.graceCeiling': 'Hasta {max} en {plan}.',
  'plan.graceOver': 'Más de lo que permite {plan} ({max}): el tiempo puede recortarse a {max}.',

  'node.restart': 'Reiniciar nodo',
  'node.updateLogic': 'Actualizar lógica',
  'node.updateAgent': 'Actualizar agente',
  'node.updateHolders': 'Actualizar holders',
  'node.cancel': 'Cancelar',
  'node.confirm.restart': '¿Reiniciar el nodo {name} en #{n}?',
  'node.confirm.logic': '¿Actualizar la lógica en {name}?',
  'node.confirm.agent': '¿Actualizar el agente en {name}?',
  'node.onAir.one': '{n} emisión sigue al aire',
  'node.onAir.many': '{n} emisiones siguen al aire',
  'node.sockets': 'Se cierran {n} sockets',
  'node.pauseTarget': 'Objetivo de pausa {n} ms',
  'node.releaseStays': 'El release sigue en #{n}',
  'node.logicPair': 'Lógica {from} → {to}',
  'node.agentPair': 'Agente {from} → {to}',
  'node.agentKeeps': 'Los contenedores y las emisiones no se tocan',
  'node.agentBack': 'El agente anterior vuelve si el nuevo no llega al centro en 2 min',
  'node.code': 'Código de 6 dígitos',
  'node.wrongCode.one': 'Código incorrecto. Queda 1 intento.',
  'node.wrongCode.many': 'Código incorrecto. Quedan {n} intentos.',
  'node.locked': 'Demasiados códigos incorrectos. Las confirmaciones están bloqueadas.',
  'node.lockedFor': 'Demasiados códigos incorrectos. Las confirmaciones están bloqueadas {n} min.',
  'node.busy.restart': 'Reiniciando nodo…',
  'node.busy.logic': 'Actualizando lógica…',
  'node.busy.agent': 'Actualizando agente…',
  'node.done.restart.one': 'Reiniciado en #{n} y {k} emisión sigue al aire.',
  'node.done.restart.many': 'Reiniciado en #{n} y {k} emisiones siguen al aire.',
  'node.done.logic.one': 'La lógica es {version} y {k} emisión sigue al aire.',
  'node.done.logic.many': 'La lógica es {version} y {k} emisiones siguen al aire.',
  'node.done.agent': 'El agente es {version}. Las emisiones no se tocaron.',
  'node.done.agentSame': 'El agente ya es {version}. No cambió nada.',
  'node.pause': 'Pausa {n} ms.',
  'node.rolledBack': 'Revertido en {step}: sirve la lógica anterior.',
  'node.rollbackFailed': 'La reversión también falló. {what}',
  'node.didNotRun.restart': 'El reinicio planificado no se ejecutó. Los sockets siguen con los holders.',
  'node.didNotRun.logic': 'La actualización de la lógica no se ejecutó. No cambió nada en {name}.',
  'node.didNotRun.agent': 'La actualización del agente no se ejecutó. No cambió nada en {name}.',
  'node.toast.restart.one': '{name} reiniciado en #{n} y {k} emisión sigue al aire.',
  'node.toast.restart.many': '{name} reiniciado en #{n} y {k} emisiones siguen al aire.',
  'node.toast.logic.one': '{name} está en la lógica {version} y {k} emisión sigue al aire.',
  'node.toast.logic.many': '{name} está en la lógica {version} y {k} emisiones siguen al aire.',
  'node.toast.agent': '{name} ejecuta el agente {version}.',
  'node.why.monolith': 'Este nodo ejecuta un solo proceso: primero migra el nodo al esquema separado.',
  'node.why.free': 'Primero libera el nodo (mueve las transmisiones).',
  'node.why.holdersChange': 'Este release también cambia los holders. Primero libera el nodo (mueve las transmisiones).',
  'node.why.noCaps': 'El agente del host es antiguo: primero actualiza el release.',
  'node.why.logicSame': 'La lógica ya es {version}.',
  'node.why.agentSame': 'El agente ya es {version}.',
  'node.why.unknown': 'El centro aún no conoce las versiones de este nodo.',
  'node.why.noComponents': 'El release del centro no indica componentes: usa Actualizar.',
  'node.layout': 'Esquema',
  'node.layout.split': 'Separado · holders y lógica',
  'node.layout.monolith': 'Un proceso',
  'node.layout.unknown': 'Aún sin datos',
  'node.activeLogic': 'Lógica activa',
  'node.generation': 'generación {n}',
  'node.components': 'Componentes',
  'node.comp.logic': 'Lógica',
  'node.comp.ingress': 'Holder de entrada',
  'node.comp.egress': 'Holder de salida',
  'node.comp.netns': 'Red',
  'node.comp.caddy': 'Caddy',
  'node.comp.agent': 'Agente',
  'node.step.checks': 'Comprobaciones',
  'node.step.start': 'Inicio',
  'node.step.promote': 'Promoción',
  'node.step.verify': 'Verificación',
  'node.step.finish': 'Final',
  'node.step.rollback': 'Reversión',
  'node.steps': 'Pasos',
  'node.code.busy': 'Otro cambio se está ejecutando en este host.',
  'node.code.node_not_split': 'Este nodo ejecuta un solo proceso: primero migra el nodo al esquema separado.',
  'node.code.holders_unhealthy': 'Los holders no responden.',
  'node.code.color_mismatch': 'Los colores del nodo no coinciden con su configuración.',
  'node.code.holders_changed': 'Las definiciones de los holders cambiaron en el host. Primero libera el nodo (mueve las transmisiones).',
  'node.code.holders_must_change': 'Este release también cambia los holders. Primero libera el nodo (mueve las transmisiones).',
  'node.code.image': 'La imagen de la lógica en el host no es la del release.',
  'node.code.start_failed': 'La lógica de reserva no arrancó.',
  'node.code.ready_timeout': 'La lógica de reserva no estuvo lista en 30 s.',
  'node.code.promote_failed': 'La lógica de reserva no tomó el relevo.',
  'node.code.stale_generation': 'El nodo rechazó la generación.',
  'node.code.retire_failed': 'La lógica anterior no se retiró.',
  'node.code.center_timeout': 'La nueva lógica no llegó al centro en 5 s.',
  'node.code.verify_failed': 'Una comprobación tras el cambio falló.',
  'node.code.rollback_failed': 'La reversión también falló.',
  'node.code.needs_deploy': 'El agente del host es antiguo: primero actualiza el release.',
  'node.code.up_to_date': 'El nodo ya ejecuta esta versión.',
  'node.code.changes_unknown': 'El centro aún no conoce las versiones de este nodo. Inténtalo en unos segundos.',
  'node.code.no_components': 'El release del centro no indica componentes: usa Actualizar.',
  'node.code.publishers_present': 'Primero libera el nodo (mueve las transmisiones).',
  'node.code.self_update_off': 'La actualización automática está desactivada en este host.',
  'node.code.offline': 'El agente del host está desconectado.',
  'node.fleet.open': 'Actualizar nodos…',
  'node.fleet.title': 'Actualizar nodos',
  'node.fleet.what': 'Qué actualizar',
  'node.fleet.logic': 'Lógica',
  'node.fleet.agent': 'Agente',
  'node.fleet.nodes': 'Nodos',
  'node.fleet.oneCode': 'Un código cubre todos los nodos: el primero lo usa y los demás siguen en paralelo en ese minuto.',
  'node.fleet.send.one': 'Actualizar 1 nodo',
  'node.fleet.send.many': 'Actualizar {n} nodos',
  'node.fleet.busy': 'Actualizando nodos…',
  'node.fleet.none': 'Ningún nodo puede recibir esta actualización ahora.',
  'node.fleet.results': 'Resultados',
  'node.fleet.col.node': 'Nodo',
  'node.fleet.col.outcome': 'Resultado',
  'node.fleet.col.pause': 'Pausa',
  'node.fleet.col.generation': 'Generación',
  'node.fleet.done': '{ok} de {n} nodos actualizados.',
  'node.fleet.notConfirmed': 'El código no se aceptó: los demás nodos no se tocaron.',
  'node.fleet.close': 'Cerrar',
  'node.ev.policyRefuseMissing': '{node} no puede rechazar transmisiones por plan (sin policy_refuse/1): las rechazadas no tienen salidas allí. Actualiza el nodo.',
  'node.timeout': 'Aún sin resultado: el cambio puede seguir en curso. El resultado aparecerá en los comandos del host.',
  'node.why.behind': 'Este host va por detrás del release del centro: primero actualiza la lógica o el release.',
  'group.title': 'Grupo de nodos {name}',
  'group.back': 'Nodos',
  'group.fact.address': 'Dirección',
  'group.fact.members': 'Miembros',
  'group.fact.take': 'Reciben conexiones',
  'group.fact.publishers': 'Emisores',
  'group.fact.weights': 'Pesos',
  'group.fact.noZone': 'aún sin publicar',
  'group.col.member': 'Miembro',
  'group.col.state': 'Estado',
  'group.col.load': 'Carga',
  'group.col.share': 'Parte',
  'group.col.live': 'De en vivo',
  'group.col.cap': 'Tope',
  'group.col.weight': 'Peso',
  'group.col.publishers': 'Emisores',
  'group.col.actions': 'Acciones',
  'group.state.joining': 'UNIÉNDOSE',
  'group.state.serving': 'OK',
  'group.state.draining': 'VACIANDO',
  'group.state.drained': 'VACÍO',
  'group.state.maintenance': 'MANTENIMIENTO',
  'group.state.down': 'CAÍDO',
  'group.was': 'antes {state}',
  'group.hint.joining': 'Recibe una parte creciente de las conexiones nuevas: paso {n} de 10.',
  'group.hint.serving': 'Recibe toda su parte de las conexiones nuevas.',
  'group.hint.draining': 'No recibe conexiones nuevas. Sus emisores siguen hasta terminar o hasta el plazo.',
  'group.hint.drained': 'No queda nada en el nodo. Puede pasar a mantenimiento.',
  'group.hint.maintenance': 'El nodo puede apagarse: sin alertas. Devuélvelo para que reciba conexiones.',
  'group.hint.down': 'El centro no llega a este nodo; su nombre apunta a los demás miembros.',
  'group.tag.auto': 'AUTO',
  'group.tag.manual': 'MANUAL',
  'group.capHeld': 'En su tope: no recibe conexiones nuevas hasta bajar de él.',
  'group.ramp': 'Entrada',
  'group.ramp.step': 'Paso {n} de 10',
  'group.ramp.manual': 'Entrada manual: fija un peso o termina la entrada.',
  'group.load.cpu': "CPU",
  'group.load.net_in': 'red entrada',
  'group.load.net_out': 'red salida',
  'group.btn.weight': 'Editar peso',
  'group.btn.drain': 'Vaciar nodo',
  'group.btn.maintenance': 'Mantenimiento',
  'group.btn.return': 'Devolver nodo',
  'group.btn.serve': 'Terminar entrada',
  'group.btn.saveWeight': 'Guardar peso',
  'group.btn.saveSettings': 'Guardar ajustes',
  'group.btn.saveAddr': 'Guardar dirección',
  'group.busy': 'Guardando…',
  'group.confirm.drain': '¿Vaciar el nodo {name}?',
  'group.drain.noNew': 'No recibe conexiones nuevas.',
  'group.drain.left': 'Emisores en él ahora: {n}. Siguen hasta terminar o {min} min; después se reconectan a otros miembros.',
  'group.drain.apps': 'Las apps de Strevix en él pasan a otro miembro antes del plazo.',
  'group.confirm.maintenance': '¿Poner {name} en mantenimiento?',
  'group.maint.empty': 'El nodo está vacío.',
  'group.maint.down': 'En mantenimiento puede apagarse sin alertas.',
  'group.confirm.return': '¿Devolver {name} a {group}?',
  'group.return.ramp': 'Entra de forma gradual en {min} min.',
  'group.return.manual': 'Se queda en 0 hasta que fijes un peso o termines la entrada.',
  'group.return.agent': 'El agente del host de {name} debe estar en línea.',
  'group.confirm.serve': '¿Terminar la entrada de {name}?',
  'group.serve.full': 'Recibe toda su parte ahora.',
  'group.confirm.weight': 'Peso de {name}',
  'group.weight.mode': 'Peso',
  'group.weight.auto': 'Auto',
  'group.weight.manual': 'Manual',
  'group.weight.share': 'Parte de las conexiones nuevas, %',
  'group.weight.shareHelp': 'Los demás miembros se reparten el resto según la carga.',
  'group.weight.cap': 'Tope, % de conexiones',
  'group.weight.capHelp': 'El modo automático nunca lo supera. Vacío = sin tope.',
  'group.weight.autoOff': 'Los pesos automáticos están desactivados en este grupo: cada miembro tiene una parte manual.',
  'group.settings': 'Ajustes del grupo',
  'group.set.autoWeights': 'Pesos automáticos',
  'group.set.autoWeightsHint': 'Las partes siguen la carga de cada miembro. Desactivado: cada miembro tiene una parte manual.',
  'group.set.autoRamp': 'Entrada automática',
  'group.set.autoRampHint': 'Un miembro nuevo o que vuelve crece solo hasta su parte completa.',
  'group.set.ramp': 'Entrada, s',
  'group.set.rampHelp': '60–3600. Por defecto 600.',
  'group.set.drain': 'Plazo de vaciado, min',
  'group.set.drainHelp': '1–1440. Los emisores que queden después se reconectan a otros miembros.',
  'group.set.offload': 'Salidas desde otro miembro',
  'group.set.offloadHint': "Las salidas de las emisiones nuevas salen del nodo de salidas menos cargado del grupo, no del centro. Las emisiones en curso mantienen sus salidas donde están.",
  'group.confirm.settings': '¿Guardar los ajustes de {name}?',
  'group.set.changes': 'Cambios: {list}.',
  'group.on': 'activado',
  'group.off': 'desactivado',
  'group.privateAddr': 'Dirección privada',
  'group.privateAddrHelp': 'IP en la red privada del sitio. Los miembros del grupo envían por ella el tráfico de relé. Vacío = dirección pública.',
  'group.done.drain': '{name} no recibe conexiones nuevas.',
  'group.done.maintenance': '{name} está en mantenimiento.',
  'group.done.return': '{name} vuelve al grupo.',
  'group.done.serve': '{name} recibe toda su parte.',
  'group.done.weight': 'Peso de {name} guardado.',
  'group.done.settings': 'Ajustes de {name} guardados.',
  'group.done.addr': 'Dirección privada de {name} guardada.',
  'group.err.overCap': 'Una parte manual de {share} % supera el tope de {cap} %. Baja la parte o sube el tope.',
  'group.err.sum': 'Las partes manuales del grupo suman más del 100 %. Baja esta o la de otro miembro.',
  'group.err.needShare': 'Introduce una parte para el peso manual.',
  'group.err.pct': 'Parte: 0–100. Tope: más de 0, como mucho 100.',
  'group.err.state': '{name} cambió de estado: esto solo funciona en {needed}.',
  'group.err.changed': '{name} cambió de estado mientras tanto. Revísalo y vuelve a intentarlo.',
  'group.err.notMember': '{name} ya no está en un grupo.',
  'group.err.offload': "Las salidas desde otro miembro solo funcionan en un grupo de nodos llamado <región>-<n>.",
  'group.err.range': 'Entrada: 60–3600 s. Plazo de vaciado: 1–1440 min.',
  'group.err.addr': 'Usa una dirección IP de una red privada (10/8, 172.16/12, 192.168/16, 100.64/10, fc00::/7), sin puerto.',
  'group.err.noHost': '{name} no tiene agente de host: vaciar y devolver lo necesitan.',
  'group.err.agentOff': 'El agente del host de {name} no está conectado. Enciende la máquina del nodo y vuelve a intentarlo.',
  'group.err.prov': 'La verificación del segundo factor no responde. Vuelve a intentarlo en un minuto.',
  'group.err.other': 'El centro no lo aceptó: {why}',
  'group.notFound': 'No existe ese grupo. Puede que se haya eliminado.',
  'group.empty': 'Aún no hay miembros. Añade un nodo desde su ficha: Failover → Grupo de región.',
  'group.row.members': 'miembros {n}',
  'group.row.publishers': 'emisores {n}',
  'group.row.open': 'Abrir el grupo de nodos {name}',
  'ingest.region.label': 'Región de ingesta',
  'ingest.region.auto': 'Auto',
  'ingest.region.autoSub': 'servidor más cercano',
  'ingest.region.gone': 'ninguno en línea',
  'ingest.region.inUse': 'EN USO',
  'ingest.region.inUseHint': 'La dirección del servidor de abajo es de aquí',
  'ingest.groupHelp': 'Una dirección para la región. Si un servidor se detiene, OBS se reconecta a otro.',
  'srt.noneIn': 'Los servidores de {region} aún no aceptan SRT. Usa RTMPS aquí.',
  'srt.noneInGroup': '{region} aún no acepta SRT. Usa RTMPS aquí.',
  'srt.noneHere': 'SRT aún no está disponible en este servidor. Usa RTMPS.',
  'srt.elsewhere': ' Regiones con SRT: {list}.',
  'srt.needKey': 'SRT necesita una clave de transmisión activa.',
  'group.copy': 'Copiar',
  'group.copied': 'Copiado',
  'group.rampFact': 'Entrada {s} s',
  'group.rampManual': 'Entrada manual',
  'group.set.offloadNew': "Se aplica a las emisiones nuevas. Las emisiones en curso no cambian.",
  'group.col.got': "Últimos 15 min",
  'group.got.hint': "Conexiones nuevas en los últimos 15 min: {n}.",
  'group.confirm.moveAuto': "¿Mover las salidas de la ruta {route} a un nodo que elija el centro?",
  'group.log.pause': "Pausar",
  'group.log.resume': "Reanudar",
  'group.log.paused': "En pausa: no se cargan entradas nuevas.",
  'group.hub.tag': "SALIDAS {n}",
  'group.hub.hint': "Envía las salidas de emisiones que llegaron a otros nodos: {n}.",
  'group.hub.hintIdle': "Nodo de salidas: puede enviar las salidas de las emisiones nuevas de otros miembros.",
  'group.drain.hub': "Lleva las salidas de {n} emisiones de otros miembros. Siguen {min} min y después pasan a otro nodo de salidas: todas las plataformas de esas emisiones se reconectan y en Twitch es una emisión nueva.",
  'group.bc.title': "Emisiones",
  'group.bc.col.stream': "Transmisión",
  'group.bc.col.in': "Recepción",
  'group.bc.col.from': "Salidas desde",
  'group.bc.col.outputs': "Salidas",
  'group.bc.col.age': "Al aire",
  'group.bc.center': "CENTRO",
  'group.bc.outputsN': "{n} salidas",
  'group.bc.ageH': "{h} h {m} min",
  'group.bc.ageM': "{m} min",
  'group.bc.empty': "Ahora no hay emisiones en el grupo.",
  'group.bc.error': "Las rutas del relay no responden: no se muestran las emisiones del grupo. Mover salidas espera a que respondan.",
  'group.btn.move': "Mover salidas",
  'group.confirm.move': "¿Mover las salidas de la ruta {route} a {to}?",
  'group.move.warn': "Todas las plataformas se reconectan: los espectadores ven una breve pausa y en Twitch es una emisión nueva.",
  'group.move.from': "Transmisión {stream}, ahora desde {from}: {n} salidas.",
  'group.move.to': "Mover a",
  'group.move.center': "Automáticamente",
  'group.move.centerPick': "el centro elige el nodo",
  'group.move.type': "Escribe {route} para confirmar",
  'group.move.busy': "Moviendo salidas…",
  'group.move.why.ingress': "La transmisión entra aquí.",
  'group.move.why.same': "Las salidas ya salen de aquí.",
  'group.move.why.noHub': "No es nodo de salidas: no envía salidas de otros miembros.",
  'group.move.why.state': "Solo un miembro en OK recibe salidas.",
  'group.move.why.centerNow': "Las salidas ya salen del centro.",
  'group.move.later': "Podrás intentarlo en {n} min.",
  'group.move.err.too_young': "Esta emisión lleva menos de 10 min al aire. Las salidas solo se mueven tras 10 min al aire.",
  'group.move.err.moved_recently': "Las salidas de esta emisión se movieron hace menos de 30 min. Un movimiento por emisión cada 30 min.",
  'group.move.err.target_busy': "{to} recibió salidas movidas hace menos de 5 min. Elige otro nodo o espera.",
  'group.move.err.budget_spent': "{group} ya hizo sus {budget} movimientos de estos 5 min.",
  'group.move.err.frozen': "Los nombres de los nodos están congelados tras una pérdida masiva de nodos. Las salidas no se mueven hasta que termine la congelación.",
  'group.move.err.not_in_group': "La transmisión entra fuera de un grupo de nodos. Aquí solo se mueven las salidas de un grupo de nodos.",
  'group.move.err.follows_main': "Es una entrada vertical: sigue a su transmisión principal. Mueve las salidas de la principal.",
  'group.move.err.same_hub': "Las salidas ya salen de {to}. No se movió nada.",
  'group.move.err.target_not_in_group': "{to} está en otro grupo. Elige un miembro de {group} o el centro.",
  'group.move.err.target_unfit': "{to} no puede recibir las salidas ahora: debe estar en OK, ser nodo de salidas, con el relay listo y espacio para más conexiones del relay. No se movió nada.",
  'group.move.err.no_target': "Ningún nodo puede recibir las salidas ahora. No se movió nada.",
  'group.move.err.route_ended': "La emisión terminó. No se movió nada.",
  'group.move.err.not_on_air': "La emisión no está al aire en su recepción ahora. No se movió nada.",
  'group.move.err.routes_elsewhere': "Otra instancia del centro controla ahora las rutas. Inténtalo de nuevo en unos segundos.",
  'group.move.err.confirm_required': "El centro pide confirmar que todas las plataformas se reconectan. No se movió nada.",
  'group.done.move': "Las salidas de {stream} salen ahora de {to}. Todas las plataformas se reconectaron.",
  'group.log.title': "Registro de balanceo",
  'group.log.kind': "Tipo",
  'group.log.locale': "es-ES",
  'group.log.k.all': "Todas las entradas",
  'group.log.k.weights': "Partes",
  'group.log.k.ramp_step': "Pasos de entrada",
  'group.log.k.cap': "Topes",
  'group.log.k.member_state': "Estados de miembros",
  'group.log.k.offload': "Salidas desde otro miembro",
  'group.log.k.outputs_moved': "Salidas movidas",
  'group.log.k.hub_fallback': "Salidas desde el centro",
  'group.log.empty': "Aún no hay nada en el registro. Aquí aparecen partes, pasos de entrada, topes, estados de miembros y salidas movidas.",
  'group.log.emptyKind': "No hay entradas de este tipo en los últimos 30 días.",
  'group.log.more': "Mostrar más",
  'group.log.share': "parte {a} % → {b} %.",
  'group.log.share.load': "parte {a} % → {b} %: carga {l} ({by}).",
  'group.log.share.takers': "parte {a} % → {b} %: cambiaron los miembros que reciben conexiones.",
  'group.log.ramp': "paso de entrada {n} de 10.",
  'group.log.capHeld': "alcanzó su tope: no recibe conexiones nuevas hasta bajar de él.",
  'group.log.capBelow': "volvió a estar bajo su tope: recibe conexiones nuevas.",
  'group.log.state': "{from} → {to}.",
  'group.log.state.why': "{from} → {to}: {why}.",
  'group.log.why.operator': "acción de un operador",
  'group.log.why.drain': "orden de vaciado",
  'group.log.why.rampStarts': "empieza la entrada",
  'group.log.why.rampDone': "la entrada terminó",
  'group.log.why.undrain': "volvió al grupo",
  'group.log.why.drained': "no queda nada en el nodo",
  'group.log.why.back': "vuelve tras un fallo",
  'group.log.offloadOn': "salidas desde otro miembro activadas, para las emisiones nuevas.",
  'group.log.offloadOff': "salidas desde otro miembro desactivadas, para las emisiones nuevas.",
  'group.log.moved.operator': "salidas movidas {from} → {to} por un operador. Todas las plataformas se reconectaron.",
  'group.log.moved.drain': "salidas movidas {from} → {to}: {from} vaciado. Todas las plataformas se reconectaron.",
  'group.log.fallback.none': "las salidas salen del centro ({to}): ningún miembro puede llevarlas.",
  'group.log.fallback.busy': "las salidas salen del centro ({to}): los miembros que podrían llevarlas están ocupados.",
  // GROUP-UI-3: create / compose a node group, readiness, default group, failover panel (AUDIT §17 NG-12…NG-20)
  'gx.create.btn': 'Crear grupo de nodos',
  'gx.create.title': 'Crear grupo de nodos',
  'gx.create.busy': 'Creando…',
  'gx.create.region': 'Región',
  'gx.create.regionHelp': 'De las regiones de tus nodos. Una nueva: Otra región.',
  'gx.create.other': 'Otra región',
  'gx.create.otherLabel': 'Región nueva',
  'gx.create.otherHelp': 'Dos letras, un guion y una palabra: eu-central.',
  'gx.create.format': 'Dos letras, un guion y una palabra: eu-central.',
  'gx.create.nodes.one': '1 nodo',
  'gx.create.nodes.many': '{n} nodos',
  'gx.create.index': 'Índice',
  'gx.create.indexHelp': '1–9.',
  'gx.create.taken': 'Ya existe: {list}.',
  'gx.create.allTaken': 'Ya existen los 9 grupos de {region}.',
  'gx.create.name': 'Nombre',
  'gx.create.nameHelp': 'Los codificadores y el panel ven esta única dirección.',
  'gx.create.noZone': 'La dirección completa aparece cuando el aprovisionador informe la zona de ingesta.',
  'gx.create.rampHelp': '60–3600.',
  'gx.create.drainHelp': '1–1440.',
  'gx.create.then': 'El grupo empieza vacío: añade nodos a {name} en su página.',
  'gx.create.done': '{name} creado. Añádele nodos.',
  'gx.add.btn': 'Añadir nodo',
  'gx.add.title': 'Añadir un nodo a {group}',
  'gx.add.label': 'Nodo',
  'gx.add.none': 'Ningún nodo puede unirse. Pueden los nodos con salida vía centro, agente de host y sin otro grupo.',
  'gx.add.ramp': '{name} entra como UNIÉNDOSE y sube en {min} min.',
  'gx.add.rampManual': '{name} entra como UNIÉNDOSE; la entrada es manual: termínala en esta página.',
  'gx.add.cert': 'El certificado del grupo se reemite con el nombre del nodo.',
  'gx.add.done': '{name} se unió a {group}.',
  'gx.why.direct': 'La salida es directa. Pon la salida vía centro primero.',
  'gx.why.otherGroup': 'Está en {group}. Sácalo de allí primero.',
  'gx.why.noAgent': 'Sin agente de host: vaciar y mantenimiento no funcionarán.',
  'gx.why.revoked': 'Revocado.',
  'gx.why.integrations': 'Un nodo de integraciones no recibe ingesta.',
  'gx.remove.btn': 'Quitar del grupo',
  'gx.remove.title': '¿Quitar {name} de {group}?',
  'gx.remove.empty': '{name} está vacío: {n} emisores.',
  'gx.remove.name': 'Su nombre vuelve a apuntar a él; el certificado del grupo se reemite sin él.',
  'gx.remove.roles': 'Roles y salida no cambian.',
  'gx.remove.done': '{name} salió de {group}.',
  'gx.empty': 'Aún no hay miembros. Añade un nodo: entra de forma gradual.',
  'gx.delete.btn': 'Eliminar grupo',
  'gx.delete.title': '¿Eliminar el grupo {name}?',
  'gx.delete.dns': '{fqdn} sale del DNS y del certificado.',
  'gx.delete.cabinet': 'El panel deja de ofrecerlo como región.',
  'gx.delete.default': 'Es el grupo por defecto: los usuarios con región automática vuelven a la dirección del centro.',
  'gx.delete.done': 'Grupo {name} eliminado.',
  'gx.ready.title': 'Preparación',
  'gx.ready.ok': 'OK',
  'gx.ready.pending': 'ESPERANDO',
  'gx.ready.warn': 'REVISAR',
  'gx.ready.fail': 'NO LISTO',
  'gx.ready.okLine.one': '1 comprobación OK: {list}.',
  'gx.ready.okLine.many': '{n} comprobaciones OK: {list}.',
  'gx.ready.open': 'Abrir {name}',
  'gx.check.cert': 'certificado',
  'gx.check.private': 'direcciones privadas',
  'gx.check.bandwidth': 'ancho de banda',
  'gx.r.certPending': 'El certificado se está emitiendo.',
  'gx.r.dnsMissing': '{fqdn} no está en el DNS.',
  'gx.r.lua': 'Los registros LUA están desactivados en la zona.',
  'gx.r.luaB': 'Los pesos no funcionan: OBS sin Strevix siempre recibe un miembro.',
  'gx.r.direct': '{name}: la salida es directa.',
  'gx.r.private': '{name} no tiene dirección privada.',
  'gx.r.privateB': 'Los miembros hablan por la red pública.',
  'gx.r.bandwidth': '{name} no declara ancho de banda.',
  'gx.r.sites': 'Los miembros están en {n} sitios: {sites}.',
  'gx.r.sitesB': 'El grupo funciona; OBS sin Strevix puede caer en cualquiera.',
  'gx.warn.lastAuto': '{name} es el último nodo de la dirección automática.',
  'gx.warn.lastAutoB': 'Los usuarios con región automática no podrán conectarse hasta que haya un grupo por defecto u otro nodo reciba ingesta.',
  'gx.warn.sites': 'Sitios distintos.',
  'gx.err.nodeName': 'Un nodo ya se llama {name}. Elige otro índice.',
  'gx.err.exists': '{name} ya existe.',
  'gx.err.format': 'Un grupo de nodos es una región y un índice 1–9: eu-central y 1.',
  'gx.err.hasMembers': 'El grupo tiene miembros. Quítalos primero.',
  'gx.err.cluster': 'Los clústeres llegarán más tarde: crea un grupo de nodos.',
  'gx.default.title': 'Región automática',
  'gx.default.field': 'Grupo por defecto',
  'gx.default.center': 'Ninguno: el centro',
  'gx.default.save': 'Guardar grupo por defecto',
  'gx.default.help': 'Los usuarios con región automática transmiten a {fqdn}, con OBS y con nuestras apps.',
  'gx.default.helpCenter': 'Los usuarios con región automática transmiten a la dirección del centro.',
  'gx.default.helpNoGroups': 'Aún no hay grupos de nodos. Crea uno para hacerlo el grupo por defecto.',
  'gx.default.tag': 'POR DEFECTO',
  'gx.default.confirm': '¿Hacer {name} el grupo por defecto?',
  'gx.default.confirmCenter': '¿Usar el centro para la región automática?',
  'gx.default.f1': 'Los usuarios con región automática reciben {fqdn}.',
  'gx.default.f2': 'La dirección automática apunta al grupo (pesos), no al centro.',
  'gx.default.f3': 'Quien eligió una región la mantiene.',
  'gx.default.c1': 'La dirección automática vuelve a apuntar al centro.',
  'gx.default.done': '{name} es el grupo por defecto.',
  'gx.default.doneCenter': 'La región automática usa el centro.',
  'gx.fo.title': 'Conmutación',
  'gx.fo.chip.none': 'NINGUNO',
  'gx.fo.chip.backup': 'RESPALDO',
  'gx.fo.chip.group': 'GRUPO DE NODOS',
  'gx.fo.chip.old': 'GRUPO ANTIGUO',
  'gx.fo.mode': 'Modo',
  'gx.fo.mode.none': 'Ninguno',
  'gx.fo.mode.backup': 'Respaldo',
  'gx.fo.mode.group': 'Grupo de nodos',
  'gx.fo.hint.none': 'El nombre del nodo siempre apunta a este nodo.',
  'gx.fo.hint.backup': 'Un nodo de respaldo toma el nombre cuando este falla o se actualiza.',
  'gx.fo.hint.group': 'Los miembros activos toman el nombre del grupo según su peso; si cae todo el grupo, su respaldo.',
  'gx.fo.now.none': 'El nombre del nodo siempre apunta a este nodo.',
  'gx.fo.now.backup': 'Respaldo: {name} toma el nombre cuando este nodo falla o se actualiza.',
  'gx.fo.now.group': 'Miembro de {group} ({fqdn}).',
  'gx.fo.now.groupBackup': 'Si cae todo el grupo: {name}.',
  'gx.fo.now.noGroupBackup': 'Sin respaldo de grupo.',
  'gx.fo.openGroup': 'Abrir el grupo',
  'gx.fo.old': '{group} no es un grupo de nodos.',
  'gx.fo.cluster': '{group} es un clúster: los clústeres llegarán más tarde.',
  'gx.fo.oldB': 'El panel no lo ve: sin pesos ni página de grupo. Crea un grupo de nodos y mueve {name} allí.',
  'gx.fo.group': 'Grupo de nodos',
  'gx.fo.groupHelp': 'Solo se muestran grupos de nodos.',
  'gx.fo.noGroups': 'Aún no hay grupos de nodos',
  'gx.fo.members.one': '1 miembro',
  'gx.fo.members.many': '{n} miembros',
  'gx.fo.groupBackup': 'Respaldo del grupo',
  'gx.fo.groupBackupHelp': 'Toma el nombre del grupo cuando ningún miembro está activo. Se guarda al momento, con tu código.',
  'gx.fo.none': 'Ninguno',
  'gx.fo.backupNode': 'Nodo de respaldo',
  'gx.fo.noRegion': 'sin región',
  'gx.fo.apply': 'Aplicar conmutación',
  'gx.fo.applying': 'Aplicando…',
  'gx.fo.saved': 'Guardado. El centro mueve el nombre DNS según esto y emite los certificados que necesitan los respaldos.',
  'gx.fo.groupBackupSet': 'Grupo {group}: respaldo {name}.',
  'gx.fo.groupBackupOff': 'Grupo {group}: respaldo quitado.',
  'gx.fo.moveAway': 'Mover el nombre para mantenimiento',
  'gx.fo.restore': 'Devolver el nombre',
  'gx.fo.movedAway': 'El nombre pasa al respaldo o al grupo; se pide a los codificadores de este nodo que se reconecten.',
  'gx.fo.returned': 'La orden terminó: el nombre vuelve según las reglas habituales.',
  'gx.fo.egressDirect': 'La salida es directa: las salidas de este nodo caen con él y un fallo termina las emisiones en las plataformas. Pon la salida vía centro en Roles y relay.',
  'gx.fo.provError': 'El aprovisionador no aceptó el modelo: {why}',
  'gx.fo.dns.serving': 'en servicio',
  'gx.fo.dns.failedOver': 'movido a {to}',
  'gx.fo.dns.noTarget': '— (sin destino: se queda aquí)',
  'gx.fo.dns.recovering': 'recuperándose: vuelve tras 5 latidos buenos, 60 s después del cambio',
  'gx.fo.dns.order': 'retenido por una orden ({order})',
  'gx.fo.dns.hold': 'inestable: retenido hasta las {at} UTC',
  'gx.fo.dns.oldAgent': 'agente de host antiguo: se detecta tras 90 s',
  'gx.fo.reason.agentSilent': 'agente de host en silencio 3 s',
  'gx.fo.reason.agentOffline': 'agente de host desconectado',
  'gx.fo.reason.ingestDown': 'el nodo no responde',
  'gx.fo.reason.logicNone': 'sin proceso de lógica',
  'gx.fo.reason.update': 'actualización',
  'gx.fo.reason.drain': 'vaciado',
  'gx.fo.reason.healthy': 'sano',
  'ingest.region.goneWarn': 'Ahora no hay servidores en línea en {region}. La dirección de abajo es la automática hasta que vuelvan.',
  'ingest.region.help': 'Elige la región más cercana. La dirección del servidor cambia con ella: pega la nueva en OBS antes de salir al aire.',
  'ingest.region.error': 'No se pudo cambiar la región: {why}',
  'gx.check.dns': 'nombre DNS',
  'gx.check.members': 'miembros',
  'gx.check.default': 'grupo por defecto',
  'gx.r.certFail': 'El certificado falló.',
  'gx.r.certOff': 'Los certificados están desactivados en el aprovisionador.',
  'gx.r.certOffB': 'RTMPS al nombre del grupo no pasa la comprobación en los miembros.',
  'gx.r.certNoMembers': 'Aún no hay certificado: el grupo no tiene miembros.',
  'gx.r.dnsUnknown': 'No se conoce el estado de la zona.',
  'gx.r.dnsMissingB': 'Espera la próxima sincronización de DNS (1 min).',
  'gx.r.noMembers': 'El grupo no tiene miembros.',
  'gx.r.noMembersB': 'Añade un nodo.',
  'gx.r.noLive': 'Ninguno de los {n} miembros recibe codificadores.',
  'gx.r.noLiveB': 'Devuelve un miembro al servicio o revisa la salud de los nodos.',
  'gx.r.directB': 'Sus salidas caen con él.',
  'gx.r.noIngest': '{name} no recibe ingesta.',
  'gx.r.noIngestB': 'El rol ingress está apagado o el nodo está fuera de rotación.',
  'gx.r.agentOff': '{name}: agente de host desconectado.',
  'gx.r.agentOffB': 'Vaciar y mantenimiento no funcionarán.',
  'gx.r.bandwidthB': 'Los pesos automáticos lo cuentan con la mediana del grupo.',
  'gx.r.defaultNoLive': '{group} es el grupo por defecto y no tiene miembros activos.',
  'gx.r.defaultNoLiveB': 'Los usuarios con región automática reciben la dirección del centro.',
  'gx.r.defaultApex': 'La dirección automática aún no está alineada.',
  'gx.r.defaultApexB': 'La próxima sincronización de DNS lo alinea.',
  'gx.r.other': '{check}: revísalo.',
  'gx.warn.hub': '{name} lleva salidas de otros miembros.',
  'gx.warn.hubB': 'Quitar la salida las termina en todas las plataformas: mueve antes sus salidas.',
  'gx.warn.sitesB': '{name} está en otro sitio o región que los demás miembros de {group}. El grupo funciona; OBS sin Strevix puede caer en cualquiera.',
  'gx.warn.noLive': '{group} no tiene miembros activos.',
  'gx.warn.noLiveB': 'Los usuarios con región automática reciben la dirección del centro hasta que uno esté en servicio.',
  'gx.warn.cert': 'Aún no hay certificado para {group}.',
  'gx.warn.certB': 'RTMPS a él no pasa la comprobación hasta que se emita (unos 10 min).',
  'gx.warn.dns': '{group} aún no está en el DNS.',
  'gx.warn.defaultRemoved': '{group} es el grupo por defecto.',
  'gx.warn.defaultRemovedB': 'Los usuarios con región automática reciben la dirección del centro; salta una alerta hasta que elijas otro grupo o borres el ajuste.',
  'gx.warn.defaultGone': 'El grupo por defecto está eliminado.',
  'gx.warn.autoCenter': 'La región automática pasa a la dirección del centro.',
  'gx.warn.autoCenterB': 'Comprueba que el centro recibe ingesta.',
  'gx.warn.free': 'Esto no es un grupo de nodos.',
  'gx.warn.freeB': 'El panel no lo ve: sin pesos ni página de grupo.',
  'gx.warn.other': 'El centro avisa:',
  'gx.err.notNodeGroup': 'Solo un grupo de nodos puede ser el grupo por defecto.',
  'gx.default.helpCenterAt': 'Los usuarios con región automática transmiten al centro: {rtmp}.',
  'ingest.region.moved': 'Tu región {from} ahora es parte de {to}. La dirección en OBS sigue funcionando.',
  'ingest.region.movedAuto': 'Tu región {from} ya no existe. Ahora transmites a la región automática.',
  // VERT-V4 (design/vertical/texts.md): the vertical input, its outputs, YouTube Dual stream, Status
  'out.live': 'Al aire',
  'out.waits': 'Espera a OBS',
  'obs.title': 'Ajustes de OBS',
  'obs.protocol': 'Protocolo',
  'obs.rtmps': 'RTMPS · servidor + clave',
  'obs.srt': 'SRT · una dirección',
  'obs.server': 'Servidor',
  'obs.key': 'Clave de transmisión',
  'obs.show': 'Mostrar',
  'obs.copy': 'Copiar',
  'obs.newKey': 'Nueva clave…',
  'dest.edit': 'Editar salida',
  'common.cancel': 'Cancelar',
  'common.save': 'Guardar',
  'vert.row.main': 'Principal',
  'vert.row.vertical': 'Vertical',
  'vert.sum.input': 'Entrada',
  'vert.sum.bitrate': 'Bitrate',
  'vert.sum.video': 'Vídeo',
  'vert.sum.keyframes': 'Fotogramas clave',
  'vert.sum.dropped': 'Perdidos',
  'vert.sum.drift': 'Deriva frente a la principal',
  'vert.sum.outputs': 'Salidas',
  'vert.sum.settings': 'Ajustes de la entrada vertical',
  'vert.sum.mainSettings': 'Ajustes de OBS',
  'vert.preview.label': 'Vista previa de la entrada vertical',
  'vert.tip.twitch.t': 'Twitch recibe tu transmisión horizontal',
  'vert.tip.twitch.b': 'Tu transmisión vertical va a TikTok, Instagram y al espacio vertical de YouTube. Comprueba que esto cumple las reglas de Twitch sobre emisión simultánea.',
  'vert.tip.twitch.a': 'Abrir las reglas de Twitch',
  'vert.tip.follow.t': 'Las salidas verticales siguen a tu entrada vertical',
  'vert.tip.follow.b': 'Si se detiene, pasan a tu transmisión principal hasta que vuelva. Si se detienen las dos, muestran tu pantalla de pausa.',
  'vert.dual.name': 'Canal principal',
  'vert.dual.sub': 'YouTube · Dual stream',
  'vert.dual.h': 'Horizontal',
  'vert.dual.v': 'Vertical',
  'vert.dual.link': 'Lo vinculas tú en YouTube Studio: no podemos comprobar este vínculo.',
  'vert.out.auto': 'Vertical (auto)',
  'vert.out.vertical': 'Vertical',
  'vert.out.fallback': 'Transmisión principal: vertical perdida hace {s} s',
  'vert.out.nextTime': 'Próxima emisión',
  'vert.out.offVertical': 'No saldrá al aire · entrada vertical desactivada',
  'vert.in.title': 'Entrada vertical',
  'vert.in.desc': 'Envía una segunda transmisión, vertical, desde tu codificador. Va a TikTok, Instagram y al espacio vertical de YouTube; las demás salidas siguen con tu transmisión principal. Es la misma emisión: no cuenta como una segunda transmisión en tu plan.',
  'vert.in.off': 'Desactivada. Las salidas en Automático envían tu transmisión principal.',
  'vert.in.turnOn': 'Activar la entrada vertical',
  'vert.in.turnOnHint': 'Recibes una segunda clave de transmisión para la vertical. Tu clave principal no cambia.',
  'vert.in.turnOff': 'Desactivar la entrada vertical',
  'vert.in.server': 'Servidor · el mismo que la principal',
  'vert.in.key': 'Clave de transmisión vertical',
  'vert.in.newKeyHint': 'Pide confirmación; la clave vertical anterior deja de funcionar al instante. La clave principal no cambia.',
  'vert.in.how': 'Envíala desde',
  'vert.tab.plugin': 'Strevix for OBS',
  'vert.tab.bridge': 'Strevix Bridge',
  'vert.tab.aitum': 'OBS + Aitum Vertical',
  'vert.tab.xsplit': 'XSplit',
  'vert.tab.other': 'Otro codificador',
  'vert.plugin.1': 'Instala Strevix for OBS (OBS 31.1 o posterior) y el plugin Aitum Vertical para el lienzo vertical.',
  'vert.plugin.2': 'En el panel Strevix abre **Settings → Vertical** y activa **Vertical stream**. Con la sesión iniciada, el plugin toma tu clave vertical de tu cuenta: no hay que pegar nada.',
  'vert.plugin.3': 'Elige el lienzo (**Aitum Vertical** por defecto) y la calidad: 1080×1920 · 4,5 Mbps.',
  'vert.plugin.4': 'Inicia la emisión desde el panel. Las dos transmisiones empiezan y terminan juntas; la vista previa vertical aparece aquí en unos segundos.',
  'vert.bridge.1': 'En Strevix Bridge abre la segunda salida y elige **Vertical**. Con la sesión iniciada, Bridge toma tu clave vertical: no hay que pegar nada.',
  'vert.aitum.1': 'En OBS abre **Aitum Vertical → Settings → Stream** y elige el servidor **Custom RTMP(S)**.',
  'vert.aitum.2': 'Pega el servidor y la clave de transmisión vertical de arriba.',
  'vert.xsplit.1': 'En XSplit añade una segunda salida **Custom RTMP** en Multi Output y pega el servidor y la clave vertical.',
  'vert.other.1': 'Añade una segunda salida RTMP(S) con el servidor y la clave vertical. Envía 1080×1920, H.264, un fotograma clave cada 2 s.',
  'vert.in.same': '**Usa esta dirección para las dos transmisiones.** Una transmisión vertical que llega a otro servidor se rechaza; la principal sigue.',
  'vert.dest.input': 'Qué enviar',
  'vert.dest.auto': 'Automático',
  'vert.dest.autoWhy': 'Vertical: {platform} muestra 9:16. Tu transmisión principal mientras la entrada vertical esté desactivada.',
  'vert.dest.main': 'Transmisión principal',
  'vert.dest.vertical': 'Entrada vertical',
  'vert.dest.verticalWhy': 'Si la entrada vertical está desactivada, esta salida no sale al aire.',
  'vert.dest.fallback': 'Si la entrada vertical se detiene',
  'vert.dest.fbMain': 'Pasar a tu transmisión principal',
  'vert.dest.fbMainWhy': 'Por defecto. La plataforma recibe tu imagen 16:9 hasta que vuelva la vertical. Si también se detiene la principal, se muestra tu pantalla de pausa.',
  'vert.dest.fbSlate': 'Mostrar una pantalla de pausa 9:16',
  'vert.dest.fbSlateWhy': 'La imagen sigue en 9:16. La salida espera a la vertical y termina tras el tiempo de tu pantalla de pausa.',
  'vert.dest.note': '**Hasta que llegue tu entrada vertical,** esta salida envía tu transmisión principal.',
  'vert.c2.hevcWhy': '{platform} solo acepta H.264.',
  'vert.c2.note': '**Tu transmisión trae una pista 9:16,** así que esta salida elige una pista. La entrada vertical y «Qué enviar» no se aplican aquí.',
  'vert.yt.title': 'YouTube: horizontal + vertical',
  'vert.yt.stepVertical': 'Entrada vertical',
  'vert.yt.stepKeys': 'Claves de YouTube',
  'vert.yt.heading': 'Envía las dos transmisiones a una emisión de YouTube',
  'vert.yt.s1': 'Tu codificador envía una transmisión vertical a tu clave vertical de Strevix. La entrada vertical está activada.',
  'vert.yt.s2': 'En YouTube Studio abre **Crear → Emitir en directo → Sala de control en directo**.',
  'vert.yt.s3': 'En **Dual stream** activa la vista previa vertical y elige **Codificador** (no Automático: recorta tu imagen horizontal).',
  'vert.yt.s4': 'En los ajustes de la transmisión vertical elige una clave (crea una RTMPS nueva si hace falta) y pégala abajo.',
  'vert.yt.s5': 'Pega también tu clave de transmisión principal.',
  'vert.yt.mainKey': 'Clave de transmisión principal',
  'vert.yt.vKey': 'Clave de transmisión vertical',
  'vert.yt.paste': 'Pega la clave de YouTube Studio',
  'vert.yt.warn': '**Inicia tu codificador antes de que la emisión de YouTube salga al aire.** YouTube no puede añadir la transmisión vertical después del inicio. La segunda clave se vincula al espacio vertical solo en YouTube Studio: no podemos comprobarlo.',
  'vert.yt.captions': 'Enviar subtítulos a la salida vertical',
  'vert.yt.experimental': 'Experimental',
  'vert.yt.captionsHint': 'Desactivado por defecto. Los subtítulos siempre van a la salida horizontal; aún no está confirmado si YouTube los muestra en la vertical.',
  'vert.yt.help': 'Ayuda de YouTube: Dual stream',
  'vert.yt.add': 'Añadir las dos salidas',
  'vert.yt.wait': 'Las dos salidas de YouTube esperan hasta 15 s a la entrada vertical y empiezan juntas.',
  'vert.st.msg': 'Al aire en {n} salidas. La entrada vertical se desvía {drift} de la principal.',
  'vert.st.inputs': 'Entradas',
  'vert.st.encoder': 'Codificador',
  'vert.st.driftRow': 'Deriva frente a la principal: {drift} desde el inicio',
  'vert.st.driftHint': 'Uno de tus codificadores se está quedando atrás. Los espectadores en distintas plataformas no lo notarán; los subtítulos de las salidas verticales pueden llegar tarde.',
  'vert.st.events': 'Eventos',
  'vert.f.waiting.t': 'Esperando la entrada vertical',
  'vert.f.waiting.b': 'TikTok, Instagram y el espacio vertical de YouTube envían tu transmisión principal hasta que llegue.',
  'vert.f.waiting.a': 'Abrir la configuración vertical',
  'vert.f.refused.t': 'Entrada vertical rechazada',
  'vert.f.refused.a': 'Copiar servidor',
  'vert.f.ytLate.t': 'YouTube no añadirá la vertical ahora',
  'vert.f.ytLate.b': 'YouTube no añade una transmisión vertical a una emisión ya iniciada. Se usará la próxima vez.',
  'vert.f.plan.t': 'La transmisión vertical no está en tu plan',
  'vert.f.plan.more': 'Lo incluyen Creator, Pro y Business.',
  'vert.f.plan.a': 'Comparar planes',
  'vert.f.ready': 'Entrada vertical lista · esperando la principal',
  'vert.f.readyChip': 'ESPERA LA PRINCIPAL',
  'vert.f.listening': 'Escuchando',
  'vert.f.listeningB': 'Empieza a transmitir en OBS: la imagen aparece aquí.',
  'vert.f.off.t': 'La entrada vertical está desactivada',
  'vert.f.off.b': '{n} salidas están en Vertical y no saldrán al aire. Las salidas en Automático usan tu transmisión principal.',
  'vert.f.off.b1': '1 salida está en Vertical y no saldrá al aire. Las salidas en Automático usan tu transmisión principal.',
  'vert.sum.inN': '{v} entrada',
  'vert.err.noTranscode': 'Una salida que envía la entrada vertical aún no se puede optimizar. Desactiva Optimizar para {platform} o envía la transmisión principal.',
  'vert.err.sameKey': 'La transmisión vertical necesita su propia clave de YouTube. Elige otra clave para la vertical en YouTube Studio.',
  'vert.err.inputOff': 'Activa primero la entrada vertical: el espacio vertical de YouTube la toma de tu clave vertical.',
  'vert.err.alreadyOn': 'La entrada vertical ya está activada.',
  'vert.err.notMain': 'Esta clave de transmisión está revocada. Emite una nueva para activar la entrada vertical.',
  'vert.in.rotate.title': '¿Emitir una nueva clave vertical?',
  'vert.in.rotate.body': 'La clave vertical anterior deja de funcionar al instante. Tu clave principal y la emisión no cambian; pega la nueva clave en tu codificador.',
  'vert.in.rotate.yes': 'Emitir clave nueva',
  // VERT-PLAT: platforms that give the server with the key (TikTok), the 9:16 tag, TikTok's help (profile.tiktok.*; EN = the center's profile texts)
  'add.serverFromPlatform': 'Pega la dirección del servidor que {platform} muestra junto a la clave de transmisión.',
  'add.serverNeeded': '{platform} da la dirección del servidor: pégala también.',
  'add.subServerKey': 'Servidor y clave de {platform}',
  'add.whereServerKey': 'Dónde obtener el servidor y la clave de transmisión',
  'add.portrait': '{platform} muestra vídeo 9:16: esta salida recibe tu entrada vertical cuando está activada.',
  'vert.tag': '9:16',
  'row.portraitLandscape': '{platform} muestra 9:16, pero ahora recibe una imagen horizontal. Se envía tal cual: envía 9:16 desde la entrada vertical.',
  'profile.tiktok.step1': 'Necesitas acceso a LIVE con clave de transmisión: TikTok pide tener 18 años o más y un mínimo de seguidores, que puede variar según la región.',
  'profile.tiktok.step2': 'Al preparar un LIVE con clave de transmisión (TikTok LIVE Studio o LIVE Producer), TikTok muestra una Server URL y una Stream key.',
  'profile.tiktok.step3': 'Pega las dos aquí. TikTok puede mostrar otras para otro LIVE: entonces pega las nuevas.',
  'profile.tiktok.step4': 'TikTok muestra una imagen 9:16: envíala desde la entrada vertical o configura OBS en 9:16.',
  'profile.tiktok.step5': 'TikTok puede retirar el acceso con clave de transmisión, por ejemplo tras una prueba sin suficientes LIVE.',
  'setup.tile.serverKey': '{platform} da una dirección de servidor y una clave de transmisión. Pega las dos para añadirla.',
  'setup.tile.add': 'Añadir {platform}',
  'setup.tile.how': '¿Cómo las obtengo?',
  'setup.tile.saved': 'Servidor y clave guardados',
  'setup.tile.replace': 'Cambiar servidor y clave',
  'setup.tile.keep': 'Conservar los guardados',
  'setup.tile.save': 'Guardar',
  'setup.tile.saving': 'Guardando…',
}
const ru: Dict = {
  'auth.footer.status': 'Статус сервиса',
  'auth.register.elsewhere': 'Аккаунты создаются в кабинете, а не на этом адресе. Откройте ссылку-приглашение ещё раз или попросите у администратора новую.',
  'dest.hevc': 'Отправлять HEVC и AV1 как есть',
  'dest.hevcHint': 'Только {platform}. Без перекодирования: {platform} получает дорожку в её кодеке.',
  'dest.hevcHls': 'По HLS {platform} принимает только H.264. Чтобы отправлять HEVC или AV1, переключите протокол на RTMPS.',
  'dest.beta': 'Beta',
  'dest.hevcApplies': 'Применится при сохранении.',
  'dest.hevcOnNote': 'Выход {platform} переподключится один раз (около 2 с). Если {platform} не примет кодек, выход останется на V0 и покажет предупреждение.',
  'dest.hevcOffNote': 'Выход {platform} переподключится один раз (около 2 с) и будет получать только дорожки H.264.',
  'dest.trackEnhanced': '{platform} принимает {codec}, только если включено «Отправлять HEVC и AV1 как есть».',
  'dest.hevcRefused': '{platform} по {proto} принимает только H.264: сначала выключите «Отправлять HEVC и AV1 как есть».',
  'unit.fps': 'к/с',
  'unit.kbps': 'кбит/с',
  'unit.mbps': 'Мбит/с',
  'tracks.list': 'Дорожки вашего потока',
  'tracks.video': 'Видео',
  'tracks.audio': 'Аудио',
  'tracks.ignoredN': '{n} отброшено',
  'tracks.endedN': '{n} закончилось',
  'tracks.ixVideo': 'Видеодорожка {n}',
  'tracks.ixAudio': 'Аудиодорожка {n}',
  'tracks.default': 'основная',
  'tracks.captions': 'субтитры',
  'tracks.out': '{n} вых.',
  'tracks.tagVoice': 'Голос',
  'tracks.overVideo': 'Больше {n} видеодорожек на {plan}',
  'tracks.overAudio': 'Больше {n} аудиодорожек на {plan}',
  'tracks.yourPlan': 'вашем тарифе',
  'tracks.codec': 'Кодек не поддерживается',
  'tracks.ended': 'Закончилась {s} с назад',
  'tracks.warn.vertical': 'Ваш OBS шлёт дорожку 9:16, а вертикального видео нет в тарифе. Пока она приходит.',
  'tracks.warn.ingestMax': 'Дорожка выше качества входа по тарифу. Пока она приходит.',
  'tracks.warn.ingestMaxOf': 'Дорожка выше качества входа по тарифу ({max}). Пока она приходит.',
  'tracks.strip': 'Дорожки',
  'tracks.count': '{v} V · {a} A',
  'dest.videoTrack': 'Видеодорожка',
  'dest.audioTrack': 'Аудиодорожка',
  'dest.trackH264': '{platform} принимает только H.264.',
  'dest.trackAac': '{platform} принимает только аудио AAC.',
  'dest.trackGone': 'Сейчас её нет в потоке',
  'dest.trackNote': 'Новая дорожка включится на следующем ключевом кадре, без переподключения.',
  'dest.tracksHidden': 'Дорожки, которые {platform} не принимает, не показаны: только видео H.264 и аудио AAC.',
  'row.trackMissing': '{ix} не приходит — идёт {to}',
  'row.trackCodec': '{ix} в {codec}: {platform} принимает только H.264',
  'row.trackCodecSending': '{ix} в {codec} — идёт {to}',
  'row.changeTrack': 'Сменить дорожку',
  'captions.voice': 'Голосовая дорожка',
  'captions.voiceGone': '{ix} не приходит.',
  'captions.voiceGoneBody': 'Субтитры слушают {to}, пока она не вернётся.',
  'tc.running': 'Оптимизировано для {platform} · {res} · {mbps} Мбит/с',
  'tc.starting': 'Запускаем оптимизацию для {platform}…',
  'tc.passthrough': 'Оригинал · подходит {platform}',
  'tc.original': 'Оригинальный поток на {platform}',
  'tc.bitrateOver': 'OBS шлёт {in} Мбит/с, {platform} получает {out} Мбит/с',
  'tc.resolutionOver': 'OBS шлёт {in}, {platform} получает {out}',
  'tc.gop': 'Ключевой кадр каждые 2 с для {platform}',
  'tc.codec': 'Перекодировано в H.264 для {platform}',
  'tc.audioOver': 'Звук выше нормы {platform}: поставьте в OBS звук 160 кбит/с',
  'tc.fallback': 'Оптимизация недоступна — {platform} получает оригинальный поток',
  'tc.fallbackOver': 'Оптимизация недоступна — {platform} получает оригинальный поток ({in} Мбит/с, выше предела {platform} {max} Мбит/с)',
  'tc.planNone': 'Оптимизация для {platform} не входит в тариф: идёт оригинальный поток',
  'wm.row': 'Знак Strevix · {res} · тариф Free',
  'wm.rowNoRes': 'Знак Strevix · тариф Free',
  'wm.rowTitle': 'Зрители этого выхода видят знак Strevix в правом верхнем углу. На платных тарифах его нет.',
  'wm.toastOn.title': 'Знак Strevix на ваших выходах',
  'wm.toastOn.body': 'Теперь ваш тариф Free: зрители видят знак Strevix на всех выходах.',
  'wm.toastOff.title': 'Знак Strevix снят',
  'wm.toastOff.body': 'Ваш тариф — {plan}, выходы идут без него.',
  'cd.http': 'Субтитры · {platform}',
  'cd.httpLang': 'Субтитры · {platform} · {lang}',
  'cd.cc608': 'CC · {lang} (CEA-608)',
  'cd.burn': 'Субтитры вшиты · {lang} — тратит часы транскодинга',
  'cd.burnOriginal': 'Субтитры вшиты — тратит часы транскодинга',
  'cd.burnFixed': 'Вшитые субтитры — часть видео: зритель не может их выключить.',
  'cd.burn608': '608 не поддерживает кириллицу — на {platform} субтитры будут вшиты в видео (часы транскодинга).',
  'cd.off': 'Субтитры на этом выходе выключены',
  'cd.waiting': 'Субтитры · ждём определения языка',
  'cd.nodeNo608': 'Этот медиасервер пока не умеет добавлять CC в видео — {platform} получает поток без субтитров.',
  'cd.planNone': 'Субтитры не входят в тариф.',
  'cd.planTranslate': 'Перевод субтитров не входит в тариф: {platform} не получает субтитры.',
  'cd.burnNotReady': 'Вшитые субтитры пока недоступны — {platform} получает видео без субтитров.',
  'cd.noTranscoder': 'Нет свободного транскодера — {platform} получает видео без субтитров.',
  'cd.hoursOut': 'Часы транскодинга за период исчерпаны — {platform} получает видео без субтитров.',
  'cd.cc608NotReady': 'CC в видео пока недоступны — {platform} получает поток без субтитров.',
  'cd.httpOff': 'Включите «Отправлять субтитры» в настройках этого выхода, чтобы показывать их на {platform}.',
  'cd.notTranslated': 'Поток не переводится на {lang}: включите перевод в панели субтитров.',
  'cd.unavailable': 'Субтитры на {platform} сейчас недоступны.',
  'cd.setting': 'Субтитры на этом выходе',
  'cd.settingHint.http': '{platform} получает субтитры отдельной дорожкой, зритель включает их сам.',
  'cd.settingHint.cea608': '{platform} получает английские или испанские субтитры как CC в видео; зритель включает их сам.',
  'cd.settingHint.burn': 'Субтитры рисуются в видео {platform}: зритель не может их выключить, время идёт в часы транскодинга.',
  'cd.lang.en': 'английский',
  'cd.lang.es': 'испанский',
  'cd.lang.ru': 'русский',
  'tc.planOutputs': 'Все оптимизированные назначения тарифа заняты: {platform} получает оригинальный поток',
  'tc.planHours': 'Часы оптимизации за период закончились: {platform} получает оригинальный поток',
  'tc.switch': 'Оптимизировать для {platform}',
  'tc.switchSub': 'Если OBS шлёт больше, чем принимает {platform} (битрейт, разрешение, ключевые кадры), мы сделаем подходящую копию. Поток, который уже подходит, идёт как есть.',
  'tc.saveNote': 'Применяется на следующем ключевом кадре, без переподключения.',
  'slate.row': 'Заглушка, если OBS отвалился',
  'slate.nowOn': 'вкл · через {s} с',
  'slate.nowOff': 'выкл',
  'slate.switch': 'Показывать заглушку «скоро вернёмся», если эфир прервался',
  'slate.hint': 'Зрители видят заглушку Strevix вместо замершей картинки. Эфир вернётся сам, как только OBS снова начнёт передавать; если время выйдет, площадки завершат трансляцию.',
  'slate.after': 'Показать через, с',
  'slate.afterHint': 'Секунд без видео. Пусто — 5 с.',
  'slate.max': 'Держать, с',
  'slate.maxPlaceholder': 'удержание',
  'slate.maxHint': 'Пусто — сколько длится удержание ({hold}), не дольше.',
  'slate.holdDefault': 'по умолчанию',
  'slate.range': 'От {min} до {max} целых секунд',
  'slate.saved': '✓ Сохранено. Действует со следующего подключения OBS.',
  'slate.revoked': 'Ключ отозван: его настройки не изменить. Сначала выпустите новый ключ.',
  'slate.cancel': 'Отмена',
  'slate.save': 'Сохранить',
  'slate.saving': 'Сохраняем…',
  'slate.retry': 'Ещё раз',
  'slate.stage': 'Зрители видят заглушку · осталось {left}',
  'slate.stageLeft': 'OBS перестал передавать. На выходах — «Скоро вернёмся»; эфир вернётся, как только OBS снова начнёт передавать. Если нет — площадки завершат трансляцию через {left}.',
  'slate.stageStalled': 'OBS подключён, но не передаёт видео. На выходах — «Скоро вернёмся», пока не вернётся картинка, но не дольше {left}.',
  'slate.chip': 'ЗАГЛУШКА',
  'slate.chipTitle': 'Зрители видят заглушку',
  'right.chats': 'Чаты',
  'right.chatsN': 'читаем {n}',
  'chats.outputs': 'ВЫХОДЫ',
  'chats.offAir': '{name} не в эфире',
  'chats.needsYou': '{name}: нужно ваше внимание',
  'chats.fix': 'Исправить',
  'chats.access': 'Доступ к чату по выходам',
  'chats.noOutputs': 'Включите выход, чтобы видеть здесь его чат.',
  'chats.reads': 'ЧИТАЕМ ЧАТ',
  'chats.connect': 'Подключить аккаунт',
  'chats.opening': 'Открываем {platform}…',
  'chat.feed': 'Сообщения чата',
  'chat.filterLabel': 'Чат выхода',
  'chat.viewLabel': 'Показать',
  'chat.all': 'Все',
  'chat.view.all': 'Все',
  'chat.view.qa': 'Вопросы',
  'chat.view.highlights': 'Важное',
  'chat.prelive': 'Чаты откроются, когда вы выйдете в эфир.',
  'chat.preliveNote': 'Для чата нужен подключённый аккаунт площадки, одного ключа потока мало.',
  'chat.empty': 'Сообщений пока нет. Они появятся здесь, когда зрители начнут писать.',
  'chat.emptyView': 'Здесь пока пусто.',
  'chat.paused': 'Пауза · новых: {n} ↓',
  'chat.pausedNone': 'Пауза · вернуться к новым ↓',
  'chat.first': 'ПЕРВОЕ СООБЩЕНИЕ',
  'chat.role.owner': 'ВЛАДЕЛЕЦ',
  'chat.role.mod': 'МОД',
  'chat.role.vip': 'VIP',
  'chat.role.staff': 'STAFF',
  'chat.kind.paid': 'ДОНАТ',
  'chat.kind.membership': 'ПОДПИСКА',
  'chat.kind.raid': 'РЕЙД',
  'chat.kind.announcement': 'ОБЪЯВЛЕНИЕ',
  'chat.tier': 'УРОВЕНЬ {n}',
  'chat.months': 'МЕСЯЦЕВ: {n}',
  'chat.gifts': 'ПОДАРКОВ: {n}',
  'chat.viewers': 'ЗРИТЕЛЕЙ: {n}',
  'chat.replyTo': 'в ответ {name}',
  'chat.reply': 'Ответ из студии',
  'chat.reconnecting': 'Переподключаемся к чатам…',
  'chat.limited': 'Чаты на паузе: открыто слишком много вкладок или устройств.',
  'chat.unread': 'непрочитанных: {n}',
  'chat.openOf': 'Чат: {name}',
  'chat.notReading': 'Выходы, чей чат не читается',
  'chat.reconnect': 'Подключить заново',
  'chat.access.connect': 'Подключите аккаунт {platform}, чтобы читать его чат',
  'chat.access.otherChannel': 'Ключ потока от другого канала, не от подключённого аккаунта {platform}',
  'chat.access.reconnect': 'Подключите {platform} заново, чтобы разрешить чат',
  'chat.access.comingLater': 'Чат {platform} появится позже',
  'chat.access.notConfigured': 'Чат {platform} на этом сервере пока недоступен',
  'chat.access.unsupported': '{platform} не отдаёт чат другим приложениям',
  'chat.access.custom': 'У своего RTMP нет чата',
  'chat.access.dropped': 'Связь с чатом прервалась · переподключаемся',
  'studio.limited': 'Живые данные на паузе: открыто слишком много вкладок или устройств. Закройте лишние, и данные вернутся.',
  'proto.title': 'Протокол до {platform}',
  'proto.recommended': 'Рекомендуется',
  'proto.rtmps.sub': 'Обычный способ. Наименьшая задержка.',
  'proto.hls.sub': 'Устойчивее на нестабильной сети; задержка на несколько секунд больше.',
  'proto.hls.hint1': 'Задержка на несколько секунд больше, чем по RTMPS, а режим сверхнизкой задержки YouTube недоступен.',
  'proto.hls.hint2': 'Поставьте интервал ключевых кадров 2 с и закрытый GOP: YouTube отвергает сегменты длиннее 5 с.',
  'proto.hls.hint3': 'Субтитры внутри видео (CEA-608) работают только с H.264.',
  'proto.keySteps': 'Ключ трансляции для {proto}',
  'proto.keyNote': 'Для HLS YouTube создаёт отдельный ключ трансляции (шаги выше). Вставьте этот ключ ниже.',
  'proto.serverHls': 'Адрес загрузки HLS в YouTube, уже заполнен. Ключ — в своём поле.',
  'proto.urlHttps': 'Для HLS адрес начинается с https://',
  'proto.liveNote': 'Вы в эфире: при смене {name} переподключится и на несколько секунд пропадёт из эфира. Остальные выходы продолжают трансляцию.',
  'proto.offNote': 'Применится, когда {name} в следующий раз выйдет в эфир.',
  'proto.confirm.title': '{platform} переподключится',
  'proto.confirm.body': '{name} переходит на {proto}: несколько секунд без эфира там. Остальные выходы продолжают трансляцию.',
  'proto.confirm.yes': 'Сменить и переподключить',
  'proto.confirm.no': 'Оставить {proto}',
  'proto.conflict': 'Этот выход тем временем изменили в другой вкладке или на другом устройстве. Закройте настройки, откройте снова и повторите.',
  'proto.chip': 'Отправляется в {platform} по HLS',
  'row.gopNote': 'Ключевые кадры реже чем раз в 5 с: YouTube может выбрасывать куски. Поставьте в OBS интервал ключевых кадров 2 с.',
  'dest.capLang': 'Субтитры на этом выходе',
  'dest.capLangOriginal': 'Оригинал (язык эфира)',
  'dest.capLangOrigHint': 'Здесь читают то, что вы говорите, по ходу речи.',
  'dest.capLangDelayHint': 'Субтитры с переводом: видео этого выхода идёт примерно на 10 с позже, чтобы текст совпадал с голосом. Машинный перевод.',
  'dest.capLangConfirm.title': 'Переподключить {name}?',
  'dest.capLangConfirm.toTr': 'Субтитрам на языке «{lang}» нужно видео примерно на 10 с позже: {name} переподключится, около 2 с без эфира там. Остальные выходы продолжают эфир.',
  'dest.capLangConfirm.toOrig': 'Вернуть субтитры оригинала без лишних 10 с: выход переподключится, около 2 с без эфира там. Остальные выходы продолжают эфир.',
  'dest.capLangConfirm.no': 'Оставить текущий язык',
  'dest.capLangConfirm.yes': 'Переподключить и сохранить',
  'translate.to': 'Перевод на',
  'translate.same': 'Эфир на этом языке: его выходы получают субтитры оригинала.',
  'translate.how': 'Отметьте языки здесь, затем выберите один в поле «Субтитры на этом выходе» выхода YouTube.',
  'translate.pickOnOutput': 'Чтобы начать перевод, выберите язык в поле «Субтитры на этом выходе» выхода YouTube.',
  'translate.st.degraded': 'Медленно',
  'translate.st.down': 'Сбой',
  'translate.live': 'Перевод в эфире. Выходы с переводом идут примерно на 10 с позже, чтобы текст совпадал с голосом.',
  'translate.starting': 'Запускаем перевод.',
  'translate.degraded': 'Часть фраз опоздала и пропущена. Видео это не затрагивает.',
  'translate.noCapacity': 'Сейчас нет места для переводчика: выходы с переводом идут без субтитров. Видео это не затрагивает.',
  'translate.down': 'Перевод не работает: выходы с переводом идут без субтитров, пока он не вернётся. Видео это не затрагивает.',
  'translate.oldWorker': 'Распознаватель речи пока не умеет переводить. Видео это не затрагивает.',
  'translate.plan': 'Перевод субтитров не входит в ваш тариф.',
  'translate.p90': '{s} с на фразу',
  'translate.viewAria': 'Какие субтитры показать',
  'translate.original': 'Оригинал',
  'translate.empty': 'Здесь появятся фразы, пока вы говорите.',
  'tip.input.ingest_mismatch': 'Другой ваш поток идёт на другой сервер приёма. Отправляйте все потоки (основной и вертикальный) на один адрес сервера.',
  'account.billing': 'Тариф и использование',
  'plan.title': 'Тариф и использование',
  'plan.intro': 'Ваш тариф, что в него входит и почему эфир может не начаться. Оплата пока не открыта.',
  'plan.loading': 'Загружаем тариф…',
  'plan.label': 'Тариф',
  'plan.choose': 'Выбрать тариф',
  'plan.chooseHint': 'Откроется страница тарифов. Оплата в кабинете появится позже.',
  'plan.trialLeft': 'Пробный период · осталось {days}',
  'plan.trialEnds': 'Пробный период · заканчивается сегодня',
  'plan.days.one': '{n} день',
  'plan.days.few': '{n} дня',
  'plan.days.many': '{n} дней',
  'plan.days.other': '{n} дня',
  'plan.legacyUntil': 'Legacy до {date}',
  'plan.giftUntil': '{name} — подарок до {date}',
  'plan.gift': '{name} — подарок',
  'plan.endedFree': '{name} · прежний тариф закончился',
  'plan.paymentFailed': '{name} · оплата не прошла',
  'plan.canceledUntil': '{name} · отменён, работает до {date}',
  'plan.trialNote': 'Пробный период закончится {date}. Потом аккаунт перейдёт на Free; выходы и настройки сохранятся.',
  'plan.giftNote': 'Подарок от Strevix. {date} аккаунт вернётся на свой тариф.',
  'plan.giftForever': 'Подарок от Strevix, без даты окончания.',
  'plan.ambassadorNote': 'Все функции сервиса — как у амбассадора Strevix.',
  'plan.legacyNote': 'Вы с нами с тех пор, когда тарифов ещё не было: лимиты остаются прежними до {date}. Потом аккаунт перейдёт на Free, если не выбрать тариф.',
  'plan.freeNote': 'Минимальный пакет: {streams} эфир одновременно, {dests} площадки, {hours} ч эфира в месяц. Больше — на тарифах.',
  'plan.endedNote': 'Прежний тариф закончился, аккаунт на Free. Выходы и настройки сохранены; выходы сверх лимита не включатся, пока не выберете тариф.',
  'plan.paymentNote': 'Последняя оплата не прошла. Всё работает 7 дней после сбоя, потом аккаунт перейдёт на Free.',
  'plan.access.account_frozen': 'Аккаунт заморожен: новые эфиры не начинаются. Эфир, который уже идёт, продолжается. Напишите в поддержку.',
  'plan.access.account_streams_blocked': 'Новые эфиры на аккаунте запрещены. Эфир, который уже идёт, продолжается. Напишите в поддержку.',
  'plan.access.plan_payment_disputed': 'Платёж оспорен: новые эфиры заблокированы, пока спор не решится. Эфир, который уже идёт, продолжается.',
  'plan.access.plan_paused': 'Тариф на паузе: новые эфиры не начнутся, пока он не возобновится.',
  'plan.access.other': 'Сейчас новые эфиры на аккаунте не начинаются. Напишите в поддержку.',
  'plan.bar.trial': 'Пробный · осталось {days}',
  'plan.bar.trialEnds': 'Пробный · последний день',
  'plan.bar.ended': 'Тариф закончился — Free',
  'plan.bar.paymentFailed': 'Оплата не прошла',
  'plan.bar.frozen': 'Заморожен',
  'plan.bar.paused': 'Тариф на паузе',
  'plan.bar.blocked': 'Эфиры запрещены',
  'plan.bar.hint': 'Ваш тариф: открыть «Тариф и использование»',
  'plan.limits': 'Лимиты',
  'plan.features': 'Функции',
  'plan.included': 'Есть',
  'plan.notOnPlan': 'Нет на этом тарифе',
  'plan.comingSoon': 'Скоро',
  'plan.noLimit': 'Без ограничений',
  'plan.notIncluded': 'Не входит',
  'plan.unit.hMonth': '{n} ч в месяц',
  'plan.unit.s': '{n} с',
  'plan.unit.min': '{n} мин',
  'plan.unit.minS': '{m} мин {s} с',
  'plan.limit.destinations': 'Площадки',
  'plan.limit.concurrent_streams': 'Одновременных эфиров',
  'plan.limit.stream_hours_month': 'Часы эфира',
  'plan.limit.ingest_max': 'Вход из OBS',
  'plan.limit.output_max': 'Выход на площадки',
  'plan.outputMarkNote': 'Со знаком Strevix. На платных тарифах поток уходит как есть.',
  'plan.limit.source_grace_max_s': 'Удержание, если OBS отвалился',
  'plan.limit.captions_hours_month': 'Субтитры',
  'plan.limit.transcode_outputs': 'Перекодированных выходов',
  'plan.limit.transcode_hours_month': 'Перекодирование',
  'plan.limit.translation_lang_hours_month': 'Перевод субтитров',
  'plan.ingestWarn': 'Вход выше пока принимается — только предупреждаем.',
  'plan.hoursFair': 'Честное использование: напишем, прежде чем что-то изменится.',
  'plan.hoursHard': 'На пределе новый эфир не начнётся; идущий продолжится.',
  'plan.feature.chat': 'Общий чат',
  'plan.feature.captions': 'Субтитры',
  'plan.feature.caption_translation': 'Перевод субтитров',
  'plan.feature.transcode': 'Перекодирование под площадки',
  'plan.feature.vertical_9_16': 'Вертикальный поток 9:16',
  'plan.feature.srt_ingest': 'Вход по SRT',
  'plan.feature.api_tokens': 'API-токены',
  'plan.feature.no_watermark': 'Без знака Strevix',
  'plan.usageLater': 'Израсходованные за месяц часы появятся здесь, когда включится учёт.',
  'plan.failed': 'Не удалось загрузить тариф.',
  'plan.retry': 'Повторить',
  'plan.refuse.concurrent1': 'OBS не пустили: на тарифе {plan} один эфир одновременно, а другой поток уже в эфире. Завершите его или выберите тариф больше.',
  'plan.refuse.concurrentN': 'OBS не пустили: на тарифе {plan} {n} эфира одновременно, и все заняты. Завершите один или выберите тариф больше.',
  'plan.refuse.streamHours': 'OBS не пустили: часы эфира этого месяца на тарифе {plan} израсходованы. Эфиры снова начнутся в следующем периоде или на тарифе больше.',
  'plan.refuse.destinations': '{n} из {n} площадок на тарифе {plan}. Удалите одну или выберите тариф больше.',
  'plan.refuse.sourceGrace': 'Не сохранено: на тарифе {plan} выходы держатся до {max} после отключения OBS. Выберите меньше или тариф больше.',
  'plan.refuse.noCaptions': 'Субтитров нет на тарифе {plan}.',
  'plan.refuse.captionsHours': 'Субтитры выключены: часы субтитров этого периода на тарифе {plan} израсходованы. Эфир идёт без изменений. Субтитры вернутся в следующем периоде или на тарифе больше.',
  'plan.refuse.captionsOverrun': 'Субтитры остановлены через 2 ч после того, как закончились часы субтитров этого периода на тарифе {plan}. Эфир идёт без изменений. Следующий эфир начнётся без субтитров до обновления периода.',
  'plan.refuse.noVertical': 'Вертикального потока нет на тарифе {plan}. Основной поток это не затрагивает.',
  'plan.seePlan': 'Ваш тариф',
  'plan.yourPlan': 'вашем',
  'plan.graceCeiling': 'До {max} на тарифе {plan}.',
  'plan.graceOver': 'Больше, чем позволяет {plan} ({max}): удержание может сократиться до {max}.',

  'node.restart': 'Перезапустить узел',
  'node.updateLogic': 'Обновить логику',
  'node.updateAgent': 'Обновить агент',
  'node.updateHolders': 'Обновить держатели',
  'node.cancel': 'Отмена',
  'node.confirm.restart': 'Перезапустить узел {name} на #{n}?',
  'node.confirm.logic': 'Обновить логику на {name}?',
  'node.confirm.agent': 'Обновить агент на {name}?',
  'node.onAir.one': '{n} эфир остаётся в эфире',
  'node.onAir.many': '{n} эфиров остаются в эфире',
  'node.sockets': 'Закрывается {n} сокетов',
  'node.pauseTarget': 'Цель паузы {n} мс',
  'node.releaseStays': 'Релиз остаётся #{n}',
  'node.logicPair': 'Логика {from} → {to}',
  'node.agentPair': 'Агент {from} → {to}',
  'node.agentKeeps': 'Контейнеры и эфиры не затрагиваются',
  'node.agentBack': 'Прежний агент вернётся, если новый не выйдет на связь с центром за 2 мин',
  'node.code': '6-значный код',
  'node.wrongCode.one': 'Неверный код. Осталась 1 попытка.',
  'node.wrongCode.many': 'Неверный код. Осталось попыток: {n}.',
  'node.locked': 'Слишком много неверных кодов. Подтверждения заблокированы.',
  'node.lockedFor': 'Слишком много неверных кодов. Подтверждения заблокированы на {n} мин.',
  'node.busy.restart': 'Перезапуск узла…',
  'node.busy.logic': 'Обновление логики…',
  'node.busy.agent': 'Обновление агента…',
  'node.done.restart.one': 'Перезапущен на #{n}, и {k} эфир остаётся в эфире.',
  'node.done.restart.many': 'Перезапущен на #{n}, и {k} эфиров остаются в эфире.',
  'node.done.logic.one': 'Логика {version}, и {k} эфир остаётся в эфире.',
  'node.done.logic.many': 'Логика {version}, и {k} эфиров остаются в эфире.',
  'node.done.agent': 'Агент {version}. Эфиры не затронуты.',
  'node.done.agentSame': 'Агент уже {version}. Ничего не изменилось.',
  'node.pause': 'Пауза {n} мс.',
  'node.rolledBack': 'Откат на шаге «{step}»: работает прежняя логика.',
  'node.rollbackFailed': 'Откат тоже не удался. {what}',
  'node.didNotRun.restart': 'Плановый рестарт не выполнен. Сокеты остаются у держателей.',
  'node.didNotRun.logic': 'Обновление логики не выполнено. На {name} ничего не изменилось.',
  'node.didNotRun.agent': 'Обновление агента не выполнено. На {name} ничего не изменилось.',
  'node.toast.restart.one': '{name} перезапущен на #{n}, и {k} эфир остаётся в эфире.',
  'node.toast.restart.many': '{name} перезапущен на #{n}, и {k} эфиров остаются в эфире.',
  'node.toast.logic.one': '{name} на логике {version}, и {k} эфир остаётся в эфире.',
  'node.toast.logic.many': '{name} на логике {version}, и {k} эфиров остаются в эфире.',
  'node.toast.agent': 'На {name} агент {version}.',
  'node.why.monolith': 'Узел работает одним процессом: сначала переведите его на раздельную схему.',
  'node.why.free': 'Сначала освободите узел (перенесите потоки).',
  'node.why.holdersChange': 'Этот релиз меняет и держатели. Сначала освободите узел (перенесите потоки).',
  'node.why.noCaps': 'Агент хоста старый: сначала обновите релиз.',
  'node.why.logicSame': 'Логика уже {version}.',
  'node.why.agentSame': 'Агент уже {version}.',
  'node.why.unknown': 'Центр ещё не знает версии этого узла.',
  'node.why.noComponents': 'В релизе центра нет компонентов: используйте обновление релиза.',
  'node.layout': 'Схема',
  'node.layout.split': 'Раздельная · держатели и логика',
  'node.layout.monolith': 'Один процесс',
  'node.layout.unknown': 'Пока нет данных',
  'node.activeLogic': 'Активная логика',
  'node.generation': 'поколение {n}',
  'node.components': 'Компоненты',
  'node.comp.logic': 'Логика',
  'node.comp.ingress': 'Держатель входа',
  'node.comp.egress': 'Держатель выхода',
  'node.comp.netns': 'Сеть',
  'node.comp.caddy': 'Caddy',
  'node.comp.agent': 'Агент',
  'node.step.checks': 'Проверки',
  'node.step.start': 'Запуск',
  'node.step.promote': 'Повышение',
  'node.step.verify': 'Проверка',
  'node.step.finish': 'Завершение',
  'node.step.rollback': 'Откат',
  'node.steps': 'Шаги',
  'node.code.busy': 'На этом хосте уже идёт другое переключение.',
  'node.code.node_not_split': 'Узел работает одним процессом: сначала переведите его на раздельную схему.',
  'node.code.holders_unhealthy': 'Держатели не отвечают.',
  'node.code.color_mismatch': 'Цвета узла не совпадают с его настройками.',
  'node.code.holders_changed': 'Определения держателей на хосте изменились. Сначала освободите узел (перенесите потоки).',
  'node.code.holders_must_change': 'Этот релиз меняет и держатели. Сначала освободите узел (перенесите потоки).',
  'node.code.image': 'Образ логики на хосте не совпадает с релизом.',
  'node.code.start_failed': 'Запасная логика не запустилась.',
  'node.code.ready_timeout': 'Запасная логика не была готова за 30 с.',
  'node.code.promote_failed': 'Запасная логика не приняла эфир.',
  'node.code.stale_generation': 'Узел отклонил поколение.',
  'node.code.retire_failed': 'Прежняя логика не ушла.',
  'node.code.center_timeout': 'Новая логика не вышла на связь с центром за 5 с.',
  'node.code.verify_failed': 'Проверка после переключения не прошла.',
  'node.code.rollback_failed': 'Откат тоже не удался.',
  'node.code.needs_deploy': 'Агент хоста старый: сначала обновите релиз.',
  'node.code.up_to_date': 'На узле уже эта версия.',
  'node.code.changes_unknown': 'Центр ещё не знает версии этого узла. Повторите через несколько секунд.',
  'node.code.no_components': 'В релизе центра нет компонентов: используйте обновление релиза.',
  'node.code.publishers_present': 'Сначала освободите узел (перенесите потоки).',
  'node.code.self_update_off': 'Самообновление на этом хосте выключено.',
  'node.code.offline': 'Агент хоста не на связи.',
  'node.fleet.open': 'Обновить узлы…',
  'node.fleet.title': 'Обновление узлов',
  'node.fleet.what': 'Что обновить',
  'node.fleet.logic': 'Логика',
  'node.fleet.agent': 'Агент',
  'node.fleet.nodes': 'Узлы',
  'node.fleet.oneCode': 'Один код на все узлы: первый узел получает код, остальные идут параллельно в ту же минуту.',
  'node.fleet.send.one': 'Обновить 1 узел',
  'node.fleet.send.many': 'Обновить узлов: {n}',
  'node.fleet.busy': 'Обновление узлов…',
  'node.fleet.none': 'Сейчас ни один узел не может принять это обновление.',
  'node.fleet.results': 'Итоги',
  'node.fleet.col.node': 'Узел',
  'node.fleet.col.outcome': 'Итог',
  'node.fleet.col.pause': 'Пауза',
  'node.fleet.col.generation': 'Поколение',
  'node.fleet.done': 'Обновлено узлов: {ok} из {n}.',
  'node.fleet.notConfirmed': 'Код не принят: остальные узлы не тронуты.',
  'node.fleet.close': 'Закрыть',
  'node.ev.policyRefuseMissing': '{node} не умеет отказывать потокам по тарифу (нет policy_refuse/1): у отклонённых потоков там нет выходов. Обновите узел.',
  'node.timeout': 'Результата пока нет: переключение может ещё идти. Итог появится в командах хоста.',
  'node.why.behind': 'Хост отстаёт от релиза центра: сначала обновите логику или релиз.',
  'group.title': 'Группа узлов {name}',
  'group.back': 'Узлы',
  'group.fact.address': 'Адрес',
  'group.fact.members': 'Участники',
  'group.fact.take': 'Принимают подключения',
  'group.fact.publishers': 'Издатели',
  'group.fact.weights': 'Веса',
  'group.fact.noZone': 'ещё не опубликован',
  'group.col.member': 'Участник',
  'group.col.state': 'Состояние',
  'group.col.load': 'Нагрузка',
  'group.col.share': 'Доля',
  'group.col.live': 'Из живых',
  'group.col.cap': 'Потолок',
  'group.col.weight': 'Вес',
  'group.col.publishers': 'Издатели',
  'group.col.actions': 'Действия',
  'group.state.joining': 'ВВОД',
  'group.state.serving': 'OK',
  'group.state.draining': 'ВЫВОДИТСЯ',
  'group.state.drained': 'ВЫВЕДЕН',
  'group.state.maintenance': 'ОБСЛУЖИВАНИЕ',
  'group.state.down': 'НЕДОСТУПЕН',
  'group.was': 'было: {state}',
  'group.hint.joining': 'Получает растущую долю новых подключений: шаг {n} из 10.',
  'group.hint.serving': 'Получает полную долю новых подключений.',
  'group.hint.draining': 'Новых подключений не берёт. Издатели на нём остаются до конца эфира или до срока.',
  'group.hint.drained': 'На узле ничего не осталось. Можно перевести в обслуживание.',
  'group.hint.maintenance': 'Узел можно выключать: тревог нет. Верните его, чтобы он снова принимал подключения.',
  'group.hint.down': 'Центр не видит узел; его имя указывает на других участников.',
  'group.tag.auto': 'АВТО',
  'group.tag.manual': 'ВРУЧНУЮ',
  'group.capHeld': 'Упёрся в потолок: новых подключений не берёт, пока не опустится ниже.',
  'group.ramp': 'Ввод',
  'group.ramp.step': 'Шаг {n} из 10',
  'group.ramp.manual': 'Ручной ввод: задайте вес или закончите ввод.',
  'group.load.cpu': 'ЦП',
  'group.load.net_in': 'сеть вход',
  'group.load.net_out': 'сеть выход',
  'group.btn.weight': 'Изменить вес',
  'group.btn.drain': 'Вывести узел',
  'group.btn.maintenance': 'Обслуживание',
  'group.btn.return': 'Вернуть узел',
  'group.btn.serve': 'Закончить ввод',
  'group.btn.saveWeight': 'Сохранить вес',
  'group.btn.saveSettings': 'Сохранить настройки',
  'group.btn.saveAddr': 'Сохранить адрес',
  'group.busy': 'Сохраняем…',
  'group.confirm.drain': 'Вывести узел {name}?',
  'group.drain.noNew': 'Новых подключений он не берёт.',
  'group.drain.left': 'Издателей на нём сейчас: {n}. Они остаются до конца эфира или {min} мин, затем переподключаются к другим участникам.',
  'group.drain.apps': 'Приложения Strevix на нём перейдут к другому участнику до срока.',
  'group.confirm.maintenance': 'Перевести {name} в обслуживание?',
  'group.maint.empty': 'Узел пуст.',
  'group.maint.down': 'В обслуживании его можно выключать без тревог.',
  'group.confirm.return': 'Вернуть {name} в {group}?',
  'group.return.ramp': 'Он войдёт постепенно за {min} мин.',
  'group.return.manual': 'Он останется на нуле, пока вы не зададите вес или не закончите ввод.',
  'group.return.agent': 'Агент хоста {name} должен быть на связи.',
  'group.confirm.serve': 'Закончить ввод {name}?',
  'group.serve.full': 'Он сразу получит полную долю.',
  'group.confirm.weight': 'Вес {name}',
  'group.weight.mode': 'Вес',
  'group.weight.auto': 'Авто',
  'group.weight.manual': 'Вручную',
  'group.weight.share': 'Доля новых подключений, %',
  'group.weight.shareHelp': 'Остальное участники делят по нагрузке.',
  'group.weight.cap': 'Потолок, % подключений',
  'group.weight.capHelp': 'Авто никогда его не превышает. Пусто — без потолка.',
  'group.weight.autoOff': 'Автоматические веса в группе выключены: у каждого участника ручная доля.',
  'group.settings': 'Настройки группы',
  'group.set.autoWeights': 'Автоматические веса',
  'group.set.autoWeightsHint': 'Доли следуют нагрузке участников. Выключено — у каждого ручная доля.',
  'group.set.autoRamp': 'Автоматический ввод',
  'group.set.autoRampHint': 'Новый или вернувшийся участник сам дорастает до полной доли.',
  'group.set.ramp': 'Ввод, с',
  'group.set.rampHelp': '60–3600. По умолчанию 600.',
  'group.set.drain': 'Срок вывода, мин',
  'group.set.drainHelp': '1–1440. Оставшиеся после него издатели переподключаются к другим участникам.',
  'group.set.offload': 'Выходы с другого участника',
  'group.set.offloadHint': "Выходы новых эфиров идут с наименее загруженного узла выходов группы, а не с центра. Идущие эфиры оставляют выходы там, где они есть.",
  'group.confirm.settings': 'Сохранить настройки {name}?',
  'group.set.changes': 'Изменения: {list}.',
  'group.on': 'вкл.',
  'group.off': 'выкл.',
  'group.privateAddr': 'Частный адрес',
  'group.privateAddrHelp': 'IP в частной сети площадки. Участники группы гоняют по ней трафик релея. Пусто — публичный адрес.',
  'group.done.drain': '{name} больше не принимает новых подключений.',
  'group.done.maintenance': '{name} в обслуживании.',
  'group.done.return': '{name} возвращается в группу.',
  'group.done.serve': '{name} получает полную долю.',
  'group.done.weight': 'Вес {name} сохранён.',
  'group.done.settings': 'Настройки {name} сохранены.',
  'group.done.addr': 'Частный адрес {name} сохранён.',
  'group.err.overCap': 'Ручная доля {share} % выше потолка {cap} %. Уменьшите долю или поднимите потолок.',
  'group.err.sum': 'Ручные доли в группе в сумме больше 100 %. Уменьшите эту или долю другого участника.',
  'group.err.needShare': 'Укажите долю для ручного веса.',
  'group.err.pct': 'Доля: 0–100. Потолок: больше 0 и не больше 100.',
  'group.err.state': 'Состояние {name} изменилось: это доступно только в состоянии {needed}.',
  'group.err.changed': 'Состояние {name} успело измениться. Проверьте и повторите.',
  'group.err.notMember': '{name} больше не входит в группу.',
  'group.err.offload': "Выходы с другого участника работают только в группе узлов с именем <регион>-<n>.",
  'group.err.range': 'Ввод: 60–3600 с. Срок вывода: 1–1440 мин.',
  'group.err.addr': 'Укажите IP-адрес частной сети (10/8, 172.16/12, 192.168/16, 100.64/10, fc00::/7) без порта.',
  'group.err.noHost': 'У {name} нет агента хоста: без него не вывести и не вернуть.',
  'group.err.agentOff': 'Агент хоста {name} не на связи. Включите машину узла и повторите.',
  'group.err.prov': 'Проверка второго фактора не отвечает. Повторите через минуту.',
  'group.err.other': 'Центр не принял: {why}',
  'group.notFound': 'Такой группы нет. Возможно, её удалили.',
  'group.empty': 'Участников пока нет. Добавьте узел из его карточки: Failover → группа региона.',
  'group.row.members': 'участников: {n}',
  'group.row.publishers': 'издателей: {n}',
  'group.row.open': 'Открыть группу узлов {name}',
  'ingest.region.label': 'Регион приёма',
  'ingest.region.auto': 'Авто',
  'ingest.region.autoSub': 'ближайший сервер',
  'ingest.region.gone': 'нет в сети',
  'ingest.region.inUse': 'ИСПОЛЬЗУЕТСЯ',
  'ingest.region.inUseHint': 'Адрес сервера ниже — отсюда',
  'ingest.groupHelp': 'Один адрес на регион. Если сервер остановится, OBS переподключится к другому.',
  'srt.noneIn': 'Серверы в {region} пока не принимают SRT. Используйте RTMPS.',
  'srt.noneInGroup': '{region} пока не принимает SRT. Используйте RTMPS.',
  'srt.noneHere': 'SRT на этом сервере пока недоступен. Используйте RTMPS.',
  'srt.elsewhere': ' Регионы с SRT: {list}.',
  'srt.needKey': 'Для SRT нужен действующий ключ потока.',
  'group.copy': 'Копировать',
  'group.copied': 'Скопировано',
  'group.rampFact': 'Ввод {s} с',
  'group.rampManual': 'Ввод вручную',
  'group.set.offloadNew': "Действует на новые эфиры. Идущие эфиры не трогает.",
  'group.col.got': "За 15 мин",
  'group.got.hint': "Новых подключений за 15 мин: {n}.",
  'group.confirm.moveAuto': "Перенести выходы маршрута {route} на узел, который выберет центр?",
  'group.log.pause': "Пауза",
  'group.log.resume': "Продолжить",
  'group.log.paused': "Пауза: новые записи не загружаются.",
  'group.hub.tag': "ВЫХОДЫ {n}",
  'group.hub.hint': "Отправляет выходы эфиров, пришедших на другие узлы: {n}.",
  'group.hub.hintIdle': "Узел выходов: может отправлять выходы новых эфиров других участников.",
  'group.drain.hub': "Он возит выходы {n} эфиров других участников. Они остаются {min} мин, затем переносятся на другой узел выходов: все площадки этих эфиров переподключатся, на Twitch это новый эфир.",
  'group.bc.title': "Эфиры",
  'group.bc.col.stream': "Поток",
  'group.bc.col.in': "Приём",
  'group.bc.col.from': "Выходы с",
  'group.bc.col.outputs': "Выходы",
  'group.bc.col.age': "В эфире",
  'group.bc.center': "ЦЕНТР",
  'group.bc.outputsN': "выходов: {n}",
  'group.bc.ageH': "{h} ч {m} мин",
  'group.bc.ageM': "{m} мин",
  'group.bc.empty': "Сейчас в группе нет эфиров.",
  'group.bc.error': "Маршруты релея не отвечают: эфиры группы не показаны. Перенос выходов подождёт, пока они ответят.",
  'group.btn.move': "Перенести выходы",
  'group.confirm.move': "Перенести выходы маршрута {route} на {to}?",
  'group.move.warn': "Все площадки переподключатся: зрители увидят короткую паузу, а на Twitch это новый эфир.",
  'group.move.from': "Поток {stream}, сейчас с {from}, выходов: {n}.",
  'group.move.to': "Куда",
  'group.move.center': "Автоматически",
  'group.move.centerPick': "узел выберет центр",
  'group.move.type': "Введите {route} для подтверждения",
  'group.move.busy': "Перенос выходов…",
  'group.move.why.ingress': "Поток приходит сюда.",
  'group.move.why.same': "Выходы уже идут отсюда.",
  'group.move.why.noHub': "Не узел выходов: чужие выходы не отправляет.",
  'group.move.why.state': "Выходы принимает только участник в состоянии OK.",
  'group.move.why.centerNow': "Выходы уже идут с центра.",
  'group.move.later': "Можно через {n} мин.",
  'group.move.err.too_young': "Эфир идёт меньше 10 мин. Выходы переносятся только после 10 мин эфира.",
  'group.move.err.moved_recently': "Выходы этого эфира переносили меньше 30 мин назад. Один перенос эфира за 30 мин.",
  'group.move.err.target_busy': "{to} принимал перенесённые выходы меньше 5 мин назад. Выберите другой узел или подождите.",
  'group.move.err.budget_spent': "Группа {group} уже сделала все переносы за эти 5 мин (не больше {budget}).",
  'group.move.err.frozen': "Имена узлов заморожены после массовой потери узлов. Выходы не переносятся, пока заморозка не снята.",
  'group.move.err.not_in_group': "Поток приходит не в группу узлов. Здесь переносятся только выходы группы узлов.",
  'group.move.err.follows_main': "Это вертикальный вход: он идёт за основным потоком. Перенесите выходы основного.",
  'group.move.err.same_hub': "Выходы уже идут с {to}. Ничего не изменилось.",
  'group.move.err.target_not_in_group': "{to} в другой группе. Выберите участника {group} или центр.",
  'group.move.err.target_unfit': "{to} сейчас не может принять выходы: нужны состояние OK, роль узла выходов, готовый релей и запас соединений релея. Ничего не изменилось.",
  'group.move.err.no_target': "Сейчас выходы некуда перенести. Ничего не изменилось.",
  'group.move.err.route_ended': "Эфир закончился. Ничего не изменилось.",
  'group.move.err.not_on_air': "Эфир сейчас не идёт на своём узле приёма. Ничего не изменилось.",
  'group.move.err.routes_elsewhere': "Маршрутами сейчас управляет другой экземпляр центра. Повторите через несколько секунд.",
  'group.move.err.confirm_required': "Центр просит подтвердить, что все площадки переподключатся. Ничего не изменилось.",
  'group.done.move': "Выходы {stream} теперь идут с {to}. Площадки переподключились.",
  'group.log.title': "Журнал балансировки",
  'group.log.kind': "Вид",
  'group.log.locale': "ru-RU",
  'group.log.k.all': "Все записи",
  'group.log.k.weights': "Доли",
  'group.log.k.ramp_step': "Шаги ввода",
  'group.log.k.cap': "Потолки",
  'group.log.k.member_state': "Состояния участников",
  'group.log.k.offload': "Выходы с другого участника",
  'group.log.k.outputs_moved': "Перенос выходов",
  'group.log.k.hub_fallback': "Выходы с центра",
  'group.log.empty': "В журнале пока пусто. Здесь появятся доли, шаги ввода, потолки, состояния участников и переносы выходов.",
  'group.log.emptyKind': "Записей этого вида за 30 дней нет.",
  'group.log.more': "Показать ещё",
  'group.log.share': "доля {a} % → {b} %.",
  'group.log.share.load': "доля {a} % → {b} %: нагрузка {l} ({by}).",
  'group.log.share.takers': "доля {a} % → {b} %: изменился состав принимающих участников.",
  'group.log.ramp': "шаг ввода {n} из 10.",
  'group.log.capHeld': "упёрся в потолок: новых подключений не берёт, пока не опустится ниже.",
  'group.log.capBelow': "снова ниже потолка: принимает новые подключения.",
  'group.log.state': "{from} → {to}.",
  'group.log.state.why': "{from} → {to}: {why}.",
  'group.log.why.operator': "действие оператора",
  'group.log.why.drain': "приказ вывода",
  'group.log.why.rampStarts': "начался ввод",
  'group.log.why.rampDone': "ввод закончен",
  'group.log.why.undrain': "возвращён в группу",
  'group.log.why.drained': "на узле ничего не осталось",
  'group.log.why.back': "вернулся после сбоя",
  'group.log.offloadOn': "выходы с другого участника включены, для новых эфиров.",
  'group.log.offloadOff': "выходы с другого участника выключены, для новых эфиров.",
  'group.log.moved.operator': "выходы перенесены {from} → {to} оператором. Площадки переподключились.",
  'group.log.moved.drain': "выходы перенесены {from} → {to}: {from} выведен. Площадки переподключились.",
  'group.log.fallback.none': "выходы идут с центра ({to}): ни один участник не может их взять.",
  'group.log.fallback.busy': "выходы идут с центра ({to}): участники, которые могли бы их взять, заняты.",
  // GROUP-UI-3: create / compose a node group, readiness, default group, failover panel (AUDIT §17 NG-12…NG-20)
  'gx.create.btn': 'Создать группу узлов',
  'gx.create.title': 'Создать группу узлов',
  'gx.create.busy': 'Создание…',
  'gx.create.region': 'Регион',
  'gx.create.regionHelp': 'Из регионов ваших узлов. Новый — «Другой регион».',
  'gx.create.other': 'Другой регион',
  'gx.create.otherLabel': 'Новый регион',
  'gx.create.otherHelp': 'Две буквы, дефис и слово: eu-central.',
  'gx.create.format': 'Две буквы, дефис и слово: eu-central.',
  'gx.create.nodes.one': '1 узел',
  'gx.create.nodes.many': 'узлов: {n}',
  'gx.create.index': 'Номер',
  'gx.create.indexHelp': '1–9.',
  'gx.create.taken': 'Уже есть: {list}.',
  'gx.create.allTaken': 'Все 9 групп региона {region} уже есть.',
  'gx.create.name': 'Имя',
  'gx.create.nameHelp': 'Кодировщики и кабинет видят этот один адрес.',
  'gx.create.noZone': 'Полный адрес появится, когда провайзер сообщит зону инжеста.',
  'gx.create.rampHelp': '60–3600.',
  'gx.create.drainHelp': '1–1440.',
  'gx.create.then': 'Группа создаётся пустой: добавьте узлы в {name} на её странице.',
  'gx.create.done': 'Группа {name} создана. Добавьте в неё узлы.',
  'gx.add.btn': 'Добавить узел',
  'gx.add.title': 'Добавить узел в {group}',
  'gx.add.label': 'Узел',
  'gx.add.none': 'Подходящих узлов нет. Подходят узлы с egress через центр, агентом хоста и без другой группы.',
  'gx.add.ramp': '{name} войдёт в состоянии ВВОД и наберёт долю за {min} мин.',
  'gx.add.rampManual': '{name} войдёт в состоянии ВВОД; ввод ручной: закончите его на этой странице.',
  'gx.add.cert': 'Сертификат группы перевыпускается с именем узла.',
  'gx.add.done': '{name} добавлен в {group}.',
  'gx.why.direct': 'Выходы идут напрямую. Сначала переключите egress на «через центр».',
  'gx.why.otherGroup': 'В {group}. Сначала уберите его оттуда.',
  'gx.why.noAgent': 'Нет агента хоста: вывод и обслуживание не сработают.',
  'gx.why.revoked': 'Отозван.',
  'gx.why.integrations': 'Узел интеграций не принимает инжест.',
  'gx.remove.btn': 'Убрать из группы',
  'gx.remove.title': 'Убрать {name} из {group}?',
  'gx.remove.empty': 'На {name} пусто: издателей {n}.',
  'gx.remove.name': 'Его имя снова указывает на него; сертификат группы перевыпускается без него.',
  'gx.remove.roles': 'Роли и egress не меняются.',
  'gx.remove.done': '{name} убран из {group}.',
  'gx.empty': 'Участников пока нет. Добавьте узел: он войдёт постепенно.',
  'gx.delete.btn': 'Удалить группу',
  'gx.delete.title': 'Удалить группу {name}?',
  'gx.delete.dns': '{fqdn} уходит из DNS и сертификата.',
  'gx.delete.cabinet': 'Кабинет перестаёт предлагать её как регион.',
  'gx.delete.default': 'Это группа по умолчанию: пользователи с автоматическим регионом вернутся на адрес центра.',
  'gx.delete.done': 'Группа {name} удалена.',
  'gx.ready.title': 'Готовность',
  'gx.ready.ok': 'OK',
  'gx.ready.pending': 'ЖДЁТ',
  'gx.ready.warn': 'ПРОВЕРЬТЕ',
  'gx.ready.fail': 'НЕ ГОТОВО',
  'gx.ready.okLine.one': 'В порядке 1 проверка: {list}.',
  'gx.ready.okLine.many': 'В порядке проверок: {n} — {list}.',
  'gx.ready.open': 'Открыть {name}',
  'gx.check.cert': 'сертификат',
  'gx.check.private': 'частные адреса',
  'gx.check.bandwidth': 'полоса',
  'gx.r.certPending': 'Сертификат выпускается.',
  'gx.r.dnsMissing': '{fqdn} нет в DNS.',
  'gx.r.lua': 'В зоне выключены записи LUA.',
  'gx.r.luaB': 'Веса не работают: голый OBS всегда попадает на один узел.',
  'gx.r.direct': '{name}: egress напрямую.',
  'gx.r.private': 'У {name} нет частного адреса.',
  'gx.r.privateB': 'Участники говорят через публичную сеть.',
  'gx.r.bandwidth': '{name} не указал полосу.',
  'gx.r.sites': 'Участники на разных площадках ({n}): {sites}.',
  'gx.r.sitesB': 'Группа работает; голый OBS может попасть на любую.',
  'gx.warn.lastAuto': '{name} — последний узел автоматического адреса.',
  'gx.warn.lastAutoB': 'Пользователи с автоматическим регионом не подключатся, пока не задана группа по умолчанию или другой узел не примет инжест.',
  'gx.warn.sites': 'Разные площадки.',
  'gx.err.nodeName': 'Имя {name} уже у узла. Выберите другой номер.',
  'gx.err.exists': '{name} уже есть.',
  'gx.err.format': 'Группа узлов — это регион и номер 1–9: eu-central и 1.',
  'gx.err.hasMembers': 'В группе есть участники. Сначала уберите их.',
  'gx.err.cluster': 'Кластеры появятся позже: создайте группу узлов.',
  'gx.default.title': 'Автоматический регион',
  'gx.default.field': 'Группа по умолчанию',
  'gx.default.center': 'Нет: центр',
  'gx.default.save': 'Сохранить группу по умолчанию',
  'gx.default.help': 'Пользователи с автоматическим регионом идут на {fqdn} — и OBS, и наши приложения.',
  'gx.default.helpCenter': 'Пользователи с автоматическим регионом идут на адрес центра.',
  'gx.default.helpNoGroups': 'Групп узлов пока нет. Создайте группу, чтобы сделать её группой по умолчанию.',
  'gx.default.tag': 'ПО УМОЛЧАНИЮ',
  'gx.default.confirm': 'Сделать {name} группой по умолчанию?',
  'gx.default.confirmCenter': 'Вернуть автоматический регион на центр?',
  'gx.default.f1': 'Пользователи с автоматическим регионом получат {fqdn}.',
  'gx.default.f2': 'Автоматический адрес указывает на группу (с весами), а не на центр.',
  'gx.default.f3': 'Кто выбрал регион, остаётся на нём.',
  'gx.default.c1': 'Автоматический адрес снова указывает на центр.',
  'gx.default.done': '{name} — группа по умолчанию.',
  'gx.default.doneCenter': 'Автоматический регион работает через центр.',
  'gx.fo.title': 'Резервирование',
  'gx.fo.chip.none': 'НЕТ',
  'gx.fo.chip.backup': 'РЕЗЕРВ',
  'gx.fo.chip.group': 'ГРУППА УЗЛОВ',
  'gx.fo.chip.old': 'СТАРАЯ ГРУППА',
  'gx.fo.mode': 'Режим',
  'gx.fo.mode.none': 'Нет',
  'gx.fo.mode.backup': 'Резерв',
  'gx.fo.mode.group': 'Группа узлов',
  'gx.fo.hint.none': 'Имя узла всегда указывает на этот узел.',
  'gx.fo.hint.backup': 'Резервный узел забирает имя, когда этот падает или обновляется.',
  'gx.fo.hint.group': 'Живые участники берут имя группы по весам; упала вся группа — резерв группы.',
  'gx.fo.now.none': 'Имя узла всегда указывает на этот узел.',
  'gx.fo.now.backup': 'Резерв: {name} забирает имя, когда этот узел падает или обновляется.',
  'gx.fo.now.group': 'Участник {group} ({fqdn}).',
  'gx.fo.now.groupBackup': 'Упала вся группа: {name}.',
  'gx.fo.now.noGroupBackup': 'Резерва группы нет.',
  'gx.fo.openGroup': 'Открыть группу',
  'gx.fo.old': '{group} — не группа узлов.',
  'gx.fo.cluster': '{group} — кластер: кластеры появятся позже.',
  'gx.fo.oldB': 'Кабинет её не видит: ни весов, ни страницы группы. Создайте группу узлов и переведите туда {name}.',
  'gx.fo.group': 'Группа узлов',
  'gx.fo.groupHelp': 'В списке только группы узлов.',
  'gx.fo.noGroups': 'Групп узлов пока нет',
  'gx.fo.members.one': '1 участник',
  'gx.fo.members.many': 'участников: {n}',
  'gx.fo.groupBackup': 'Резерв группы',
  'gx.fo.groupBackupHelp': 'Забирает имя группы, когда живых участников нет. Сохраняется сразу, с кодом.',
  'gx.fo.none': 'Нет',
  'gx.fo.backupNode': 'Резервный узел',
  'gx.fo.noRegion': 'без региона',
  'gx.fo.apply': 'Применить резервирование',
  'gx.fo.applying': 'Применение…',
  'gx.fo.saved': 'Сохранено. Центр переводит DNS-имя по этим правилам и выпускает нужные резерву сертификаты.',
  'gx.fo.groupBackupSet': 'Группа {group}: резерв {name}.',
  'gx.fo.groupBackupOff': 'Группа {group}: резерв убран.',
  'gx.fo.moveAway': 'Увести имя на обслуживание',
  'gx.fo.restore': 'Вернуть имя',
  'gx.fo.movedAway': 'Имя переходит на резерв или группу; кодировщикам на этом узле сказано переподключиться.',
  'gx.fo.returned': 'Приказ снят: имя возвращается по обычным правилам.',
  'gx.fo.egressDirect': 'Egress напрямую: выходы узла падают вместе с ним, и сбой завершает эфиры на площадках. Переключите egress на «через центр» в «Роли и релей».',
  'gx.fo.provError': 'Провайзер не принял модель: {why}',
  'gx.fo.dns.serving': 'обслуживает',
  'gx.fo.dns.failedOver': 'перенесено на {to}',
  'gx.fo.dns.noTarget': '— (некуда: остаётся здесь)',
  'gx.fo.dns.recovering': 'восстанавливается: вернётся после 5 хороших сигналов, через 60 с после переключения',
  'gx.fo.dns.order': 'держится приказом ({order})',
  'gx.fo.dns.hold': 'дребезг: удерживается до {at} UTC',
  'gx.fo.dns.oldAgent': 'старый агент хоста: заметим только через 90 с',
  'gx.fo.reason.agentSilent': 'агент хоста молчит 3 с',
  'gx.fo.reason.agentOffline': 'агент хоста отключён',
  'gx.fo.reason.ingestDown': 'узел не отвечает',
  'gx.fo.reason.logicNone': 'нет процесса логики',
  'gx.fo.reason.update': 'обновление',
  'gx.fo.reason.drain': 'вывод',
  'gx.fo.reason.healthy': 'исправен',
  'ingest.region.goneWarn': 'Серверов в {region} сейчас нет в сети. Адрес ниже — автоматический, пока они не вернутся.',
  'ingest.region.help': 'Выберите ближайший регион. Адрес сервера меняется вместе с ним: вставьте новый в OBS до начала эфира.',
  'ingest.region.error': 'Не удалось сменить регион: {why}',
  'gx.check.dns': 'DNS-имя',
  'gx.check.members': 'участники',
  'gx.check.default': 'группа по умолчанию',
  'gx.r.certFail': 'Сертификат не выпущен.',
  'gx.r.certOff': 'Сертификаты в провайзере выключены.',
  'gx.r.certOffB': 'RTMPS на имя группы не проходит проверку на участниках.',
  'gx.r.certNoMembers': 'Сертификата пока нет: в группе нет участников.',
  'gx.r.dnsUnknown': 'Состояние зоны неизвестно.',
  'gx.r.dnsMissingB': 'Дождитесь следующей синхронизации DNS (1 мин).',
  'gx.r.noMembers': 'В группе нет участников.',
  'gx.r.noMembersB': 'Добавьте узел.',
  'gx.r.noLive': 'Ни один из участников ({n}) не принимает кодировщики.',
  'gx.r.noLiveB': 'Верните участника в работу или проверьте здоровье узлов.',
  'gx.r.directB': 'Его выходы упадут вместе с ним.',
  'gx.r.noIngest': '{name} не принимает инжест.',
  'gx.r.noIngestB': 'Роль ingress выключена или узел выведен из ротации.',
  'gx.r.agentOff': '{name}: агент хоста не на связи.',
  'gx.r.agentOffB': 'Вывод и обслуживание не сработают.',
  'gx.r.bandwidthB': 'Автовеса считают его по медиане группы.',
  'gx.r.defaultNoLive': '{group} — группа по умолчанию, но живых участников нет.',
  'gx.r.defaultNoLiveB': 'Пользователи с автоматическим регионом получают адрес центра.',
  'gx.r.defaultApex': 'Автоматический адрес ещё не выровнен.',
  'gx.r.defaultApexB': 'Следующая синхронизация DNS выровняет его.',
  'gx.r.other': '{check}: проверьте.',
  'gx.warn.hub': '{name} возит выходы других участников.',
  'gx.warn.hubB': 'Если снять egress, они оборвутся на всех площадках: сначала перенесите выходы.',
  'gx.warn.sitesB': '{name} на другой площадке или в другом регионе, чем остальные участники {group}. Группа работает; голый OBS может попасть на любую.',
  'gx.warn.noLive': 'В {group} нет живых участников.',
  'gx.warn.noLiveB': 'Пользователи с автоматическим регионом получают адрес центра, пока участник не заработает.',
  'gx.warn.cert': 'Сертификата для {group} пока нет.',
  'gx.warn.certB': 'RTMPS на него не проходит проверку, пока сертификат не выпущен (около 10 мин).',
  'gx.warn.dns': '{group} пока нет в DNS.',
  'gx.warn.defaultRemoved': '{group} — группа по умолчанию.',
  'gx.warn.defaultRemovedB': 'Пользователи с автоматическим регионом получат адрес центра; тревога горит, пока вы не выберете другую группу или не снимете настройку.',
  'gx.warn.defaultGone': 'Группа по умолчанию удалена.',
  'gx.warn.autoCenter': 'Автоматический регион уходит на адрес центра.',
  'gx.warn.autoCenterB': 'Проверьте, что центр принимает инжест.',
  'gx.warn.free': 'Это не группа узлов.',
  'gx.warn.freeB': 'Кабинет её не видит: ни весов, ни страницы группы.',
  'gx.warn.other': 'Центр предупреждает:',
  'gx.err.notNodeGroup': 'Группой по умолчанию может быть только группа узлов.',
  'gx.default.helpCenterAt': 'Пользователи с автоматическим регионом идут на центр: {rtmp}.',
  'ingest.region.moved': 'Ваш регион {from} теперь входит в {to}. Адрес в OBS продолжает работать.',
  'ingest.region.movedAuto': 'Вашего региона {from} больше нет. Сейчас вы идёте на автоматический регион.',
  // VERT-V4 (design/vertical/texts.md): the vertical input, its outputs, YouTube Dual stream, Status
  'out.live': 'В эфире',
  'out.waits': 'Ждёт OBS',
  'obs.title': 'Настройки OBS',
  'obs.protocol': 'Протокол',
  'obs.rtmps': 'RTMPS · сервер + ключ',
  'obs.srt': 'SRT · один адрес',
  'obs.server': 'Сервер',
  'obs.key': 'Ключ потока',
  'obs.show': 'Показать',
  'obs.copy': 'Копировать',
  'obs.newKey': 'Новый ключ…',
  'dest.edit': 'Настройки выхода',
  'common.cancel': 'Отмена',
  'common.save': 'Сохранить',
  'vert.row.main': 'Основной',
  'vert.row.vertical': 'Вертикальный',
  'vert.sum.input': 'Вход',
  'vert.sum.bitrate': 'Битрейт',
  'vert.sum.video': 'Видео',
  'vert.sum.keyframes': 'Ключевые кадры',
  'vert.sum.dropped': 'Потери',
  'vert.sum.drift': 'Дрейф к основному',
  'vert.sum.outputs': 'Выходы',
  'vert.sum.settings': 'Настройки вертикального входа',
  'vert.sum.mainSettings': 'Настройки OBS',
  'vert.preview.label': 'Превью вертикального входа',
  'vert.tip.twitch.t': 'Twitch получает горизонтальный поток',
  'vert.tip.twitch.b': 'Вертикальный поток идёт в TikTok, Instagram и вертикальный слот YouTube. Проверьте, что это не нарушает правила Twitch об одновременных трансляциях.',
  'vert.tip.twitch.a': 'Открыть правила Twitch',
  'vert.tip.follow.t': 'Вертикальные выходы идут за вертикальным входом',
  'vert.tip.follow.b': 'Если он пропадёт, они перейдут на основной поток, пока вертикальный не вернётся. Пропали оба — покажут заглушку.',
  'vert.dual.name': 'Основной канал',
  'vert.dual.sub': 'YouTube · Dual stream',
  'vert.dual.h': 'Горизонтальный',
  'vert.dual.v': 'Вертикальный',
  'vert.dual.link': 'Связку вы делаете в YouTube Studio — мы не можем её проверить.',
  'vert.out.auto': 'Вертикальный (авто)',
  'vert.out.vertical': 'Вертикальный',
  'vert.out.fallback': 'Основной поток — вертикаль пропала {s} с назад',
  'vert.out.nextTime': 'Со следующего эфира',
  'vert.out.offVertical': 'Не выйдет в эфир · вертикальный вход выключен',
  'vert.in.title': 'Вертикальный вход',
  'vert.in.desc': 'Отправьте из кодировщика второй, вертикальный поток. Он уйдёт в TikTok, Instagram и вертикальный слот YouTube, остальные выходы получат основной. Это тот же эфир: вторым потоком на тарифе он не считается.',
  'vert.in.off': 'Выключен. Выходы в режиме «Авто» получают основной поток.',
  'vert.in.turnOn': 'Включить вертикальный вход',
  'vert.in.turnOnHint': 'Вы получите второй ключ потока для вертикального. Основной ключ не меняется.',
  'vert.in.turnOff': 'Выключить вертикальный вход',
  'vert.in.server': 'Сервер · тот же, что у основного',
  'vert.in.key': 'Ключ вертикального потока',
  'vert.in.newKeyHint': 'Попросим подтвердить; старый вертикальный ключ сразу перестанет работать. Основной ключ не меняется.',
  'vert.in.how': 'Чем отправить',
  'vert.tab.plugin': 'Strevix for OBS',
  'vert.tab.bridge': 'Strevix Bridge',
  'vert.tab.aitum': 'OBS + Aitum Vertical',
  'vert.tab.xsplit': 'XSplit',
  'vert.tab.other': 'Другой кодировщик',
  'vert.plugin.1': 'Установите Strevix for OBS (OBS 31.1 или новее) и плагин Aitum Vertical для вертикальной сцены.',
  'vert.plugin.2': 'В доке Strevix откройте **Settings → Vertical** и включите **Vertical stream**. Если вы вошли в аккаунт, плагин сам возьмёт вертикальный ключ — вставлять ничего не нужно.',
  'vert.plugin.3': 'Выберите сцену (по умолчанию **Aitum Vertical**) и качество: 1080×1920 · 4,5 Мбит/с.',
  'vert.plugin.4': 'Начните эфир из дока. Оба потока стартуют и останавливаются вместе; вертикальное превью появится здесь через несколько секунд.',
  'vert.bridge.1': 'В Strevix Bridge откройте второй выход и выберите **Vertical**. Если вы вошли в аккаунт, Bridge сам возьмёт вертикальный ключ.',
  'vert.aitum.1': 'В OBS откройте **Aitum Vertical → Settings → Stream** и выберите сервер **Custom RTMP(S)**.',
  'vert.aitum.2': 'Вставьте сервер и ключ вертикального потока, указанные выше.',
  'vert.xsplit.1': 'В XSplit добавьте второй выход **Custom RTMP** в Multi Output и вставьте сервер и ключ вертикального потока.',
  'vert.other.1': 'Добавьте второй выход RTMP(S) с сервером и ключом вертикального потока. Отправляйте 1080×1920, H.264, ключевой кадр каждые 2 с.',
  'vert.in.same': '**Используйте этот адрес для обоих потоков.** Вертикальный поток, пришедший на другой сервер, будет отклонён; основной продолжит идти.',
  'vert.dest.input': 'Что отправлять',
  'vert.dest.auto': 'Авто',
  'vert.dest.autoWhy': 'Вертикальный: {platform} показывает 9:16. Основной поток, пока вертикальный вход выключен.',
  'vert.dest.main': 'Основной поток',
  'vert.dest.vertical': 'Вертикальный вход',
  'vert.dest.verticalWhy': 'Если вертикальный вход выключен, этот выход не выйдет в эфир.',
  'vert.dest.fallback': 'Если вертикальный вход пропал',
  'vert.dest.fbMain': 'Перейти на основной поток',
  'vert.dest.fbMainWhy': 'По умолчанию. Площадка получает картинку 16:9, пока вертикальный не вернётся. Пропал и основной — показывается заглушка.',
  'vert.dest.fbSlate': 'Показать заглушку 9:16',
  'vert.dest.fbSlateWhy': 'Картинка остаётся 9:16. Выход ждёт вертикальный и закончится по времени заглушки.',
  'vert.dest.note': '**Пока вертикальный вход не пришёл,** этот выход отправляет основной поток.',
  'vert.c2.hevcWhy': '{platform} принимает только H.264.',
  'vert.c2.note': '**В вашем потоке есть дорожка 9:16,** поэтому выход выбирает дорожку. Вертикальный вход и «Что отправлять» здесь не действуют.',
  'vert.yt.title': 'YouTube: горизонтальный + вертикальный',
  'vert.yt.stepVertical': 'Вертикальный вход',
  'vert.yt.stepKeys': 'Ключи YouTube',
  'vert.yt.heading': 'Оба потока — в одну трансляцию YouTube',
  'vert.yt.s1': 'Кодировщик отправляет вертикальный поток на ваш вертикальный ключ Strevix. Вертикальный вход включён.',
  'vert.yt.s2': 'В YouTube Studio откройте **Создать → Начать трансляцию → Панель управления трансляциями**.',
  'vert.yt.s3': 'В разделе **Dual stream** включите вертикальное превью и выберите **Кодировщик** (не «Авто» — он обрезает горизонтальную картинку).',
  'vert.yt.s4': 'В настройках вертикального потока выберите ключ (при необходимости создайте новый RTMPS) и вставьте его ниже.',
  'vert.yt.s5': 'Вставьте и основной ключ потока.',
  'vert.yt.mainKey': 'Основной ключ потока',
  'vert.yt.vKey': 'Ключ вертикального потока',
  'vert.yt.paste': 'Вставьте ключ из YouTube Studio',
  'vert.yt.warn': '**Запустите кодировщик до начала трансляции на YouTube.** YouTube не добавит вертикальный поток после старта. Второй ключ привязывается к вертикальному слоту только в YouTube Studio — проверить это мы не можем.',
  'vert.yt.captions': 'Отправлять субтитры на вертикальный выход',
  'vert.yt.experimental': 'Эксперимент',
  'vert.yt.captionsHint': 'По умолчанию выключено. Субтитры всегда идут на горизонтальный выход; показывает ли их YouTube на вертикальном — пока не подтверждено.',
  'vert.yt.help': 'Справка YouTube: Dual stream',
  'vert.yt.add': 'Добавить оба выхода',
  'vert.yt.wait': 'Оба выхода YouTube ждут вертикальный вход до 15 с и стартуют вместе.',
  'vert.st.msg': 'В эфире на {n} выходах. Вертикальный вход разошёлся с основным на {drift}.',
  'vert.st.inputs': 'Входы',
  'vert.st.encoder': 'Кодировщик',
  'vert.st.driftRow': 'Дрейф к основному: {drift} с начала',
  'vert.st.driftHint': 'Один из кодировщиков отстаёт. Зрители на разных площадках этого не заметят; субтитры на вертикальных выходах могут запаздывать.',
  'vert.st.events': 'События',
  'vert.f.waiting.t': 'Ждём вертикальный вход',
  'vert.f.waiting.b': 'TikTok, Instagram и вертикальный слот YouTube получают основной поток, пока он не придёт.',
  'vert.f.waiting.a': 'Открыть настройку',
  'vert.f.refused.t': 'Вертикальный вход отклонён',
  'vert.f.refused.a': 'Скопировать сервер',
  'vert.f.ytLate.t': 'YouTube не добавит вертикальный сейчас',
  'vert.f.ytLate.b': 'YouTube не добавляет вертикальный поток в уже начатую трансляцию. Он пойдёт в следующий раз.',
  'vert.f.plan.t': 'Вертикального потока нет в тарифе',
  'vert.f.plan.more': 'Он есть на Creator, Pro и Business.',
  'vert.f.plan.a': 'Сравнить тарифы',
  'vert.f.ready': 'Вертикальный вход готов · ждём основной',
  'vert.f.readyChip': 'ЖДЁТ ОСНОВНОЙ',
  'vert.f.listening': 'Ждём сигнал',
  'vert.f.listeningB': 'Начните трансляцию в OBS — картинка появится здесь.',
  'vert.f.off.t': 'Вертикальный вход выключен',
  'vert.f.off.b': 'Выходов, настроенных на вертикальный: {n} — они не выйдут в эфир. Выходы «Авто» получают основной поток.',
  'vert.f.off.b1': '1 выход настроен на вертикальный и не выйдет в эфир. Выходы «Авто» получают основной поток.',
  'vert.sum.inN': '{v} вход',
  'vert.err.noTranscode': 'Выход, который отправляет вертикальный вход, пока нельзя перекодировать. Выключите оптимизацию для {platform} или отправляйте основной поток.',
  'vert.err.sameKey': 'Вертикальному потоку нужен свой ключ YouTube. Выберите для него другой ключ в YouTube Studio.',
  'vert.err.inputOff': 'Сначала включите вертикальный вход: вертикальный слот YouTube получает его по вертикальному ключу.',
  'vert.err.alreadyOn': 'Вертикальный вход уже включён.',
  'vert.err.notMain': 'Этот ключ потока отозван. Выпустите новый, чтобы включить вертикальный вход.',
  'vert.in.rotate.title': 'Выпустить новый ключ вертикального потока?',
  'vert.in.rotate.body': 'Старый вертикальный ключ сразу перестанет работать. Основной ключ и эфир не затронуты; вставьте новый ключ в кодировщик.',
  'vert.in.rotate.yes': 'Выпустить новый ключ',
  // VERT-PLAT: platforms that give the server with the key (TikTok), the 9:16 tag, TikTok's help (profile.tiktok.*; EN = the center's profile texts)
  'add.serverFromPlatform': 'Вставьте адрес сервера, который {platform} показывает вместе с ключом потока.',
  'add.serverNeeded': '{platform} выдаёт адрес сервера: вставьте и его.',
  'add.subServerKey': 'Сервер и ключ из {platform}',
  'add.whereServerKey': 'Где взять адрес сервера и ключ потока',
  'add.portrait': '{platform} показывает видео 9:16: выход получает вертикальный вход, когда он включён.',
  'vert.tag': '9:16',
  'row.portraitLandscape': '{platform} показывает 9:16, а сейчас получает горизонтальную картинку. Она уходит как есть: отправьте 9:16 через вертикальный вход.',
  'profile.tiktok.step1': 'Нужен доступ к LIVE с ключом потока: TikTok требует 18+ и минимум подписчиков, который зависит от региона.',
  'profile.tiktok.step2': 'Когда вы готовите LIVE с ключом потока (TikTok LIVE Studio или LIVE Producer), TikTok показывает Server URL и Stream key.',
  'profile.tiktok.step3': 'Вставьте оба сюда. Для другого LIVE TikTok может выдать новые — тогда вставьте новые.',
  'profile.tiktok.step4': 'TikTok показывает картинку 9:16: отправьте её через вертикальный вход или переключите OBS на 9:16.',
  'profile.tiktok.step5': 'TikTok может отозвать доступ к ключу потока — например, после пробного периода без достаточного числа LIVE.',
  'setup.tile.serverKey': '{platform} выдаёт адрес сервера и ключ потока. Вставьте оба, чтобы добавить.',
  'setup.tile.add': 'Добавить {platform}',
  'setup.tile.how': 'Где их взять?',
  'setup.tile.saved': 'Сервер и ключ сохранены',
  'setup.tile.replace': 'Заменить сервер и ключ',
  'setup.tile.keep': 'Оставить сохранённые',
  'setup.tile.save': 'Сохранить',
  'setup.tile.saving': 'Сохранение…',
}

const DICTS: Record<string, Dict> = { en, es, ru }

/** The cabinet is EN for now; ?lang= is the switch the localization stage will use. */
let lang = 'en'
export function setLang(l: string) {
  lang = l in DICTS ? l : 'en'
}

/** The language in force (en, es, ru). */
export function currentLang(): string {
  return lang
}

export function t(key: Key, vars?: Record<string, string | number>): string {
  const s: string = DICTS[lang]?.[key] ?? en[key]
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s
}
